import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { config } from '../config';
import { formatDatePSTShort, formatDatePSTLong, parseDateOnly } from '../utils/dateUtils';
import AdminBillingDashboard from './billing/AdminBillingDashboard';
import CampManagement from './admin/CampManagement';
import CompetitionManagement from './admin/CompetitionManagement';
import StudentDetailView from './admin/StudentDetailView';
import OrganizationManagement from './admin/OrganizationManagement';
import './billing/BillingDashboard.css';

interface User {
  id: string;
  email: string;
  userType: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  groups?: string[];  // Group names for parents
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  date_of_birth: string;
  age: number;
  grade_level: string;
  tier: string;
  status: string;
  parent_id: string;
  parent_name?: string;
  parent_email?: string;
  enrollment_date: string;
  created_at: string;
  updated_at: string;
}

interface DashboardStats {
  totalUsers: number;
  totalStudents: number;
  totalTeachers: number;
  totalParents: number;
  activeUsers: number;
  recentRegistrations: number;
}

interface Program {
  id: string;
  name: string;
  programType: string;
  description: string;
  tier: string;
  durationMinutes?: number | null;
  sessionsRequired?: number | null;
  color: string;
  scheduleNote?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Course {
  id: string;
  name: string;
  tier: string;
  ageGroup: string;
  description: string;
  durationMinutes: number;
  sessionsRequired: number;
  prerequisites: string;
  skillsCovered: string[];
  applicablePrograms: string[];
  programId?: string; // Add optional program field
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ClassSession {
  id: string;
  program_id?: string;
  course_id?: string;
  instructor_id?: string;
  date_available: string;
  start_time: string;
  end_time: string;
  location?: string;
  max_capacity: number;
  current_enrollment: number;
  scheduled_count?: number;
  attendance_count?: number;
  status: string;
  session_type: string;
  description?: string;
  program_name?: string;
  program_tier?: string;
  course_name?: string;
  instructor_name?: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  group_type?: string;
  status: string;
  created_by?: string;
  creator_name?: string;
  member_count: number;
  created_at: string;
  updated_at: string;
}

interface GroupMember {
  student_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  tier: string;
  grade_level: string;
  age?: number;
}

interface ConsentFormDetail {
  id: string;
  student_id: string;
  student_name: string;
  parent_name: string;
  parent_email?: string;
  form_type: string;
  title: string;
  status: string;
  created_at: string;
  signed_at?: string;
  expires_at?: string;
  s3_url?: string;
  program_acknowledgment?: boolean;
  media_consent?: string;
  understands_media_terms?: boolean;
  privacy_consent?: boolean;
  emergency_medical_authorization?: boolean;
  liability_consent?: boolean;
  code_of_conduct_consent?: boolean;
  final_acknowledgment?: boolean;
  newsletter_subscription?: boolean;
}

const AdminDashboard: React.FC = () => {
  // For development, bypass authentication
  const user = { email: 'admin@example.com', userType: 'admin' };
  const [users, setUsers] = useState<User[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentSortField, setStudentSortField] = useState<string | null>(null);
  const [studentSortDirection, setStudentSortDirection] = useState<'asc' | 'desc'>('asc');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'students' | 'programs' | 'courses' | 'sessions' | 'groups' | 'consent-forms' | 'camps' | 'competitions' | 'attendance' | 'analytics' | 'settings' | 'billing' | 'orgs'>('overview');
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  
  // User form state
  const [userFormData, setUserFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    userType: 'parent',
    password: '',
    passwordOption: 'generate', // 'generate', 'custom', 'temporary'
    temporaryPassword: '',
    forcePasswordChange: false
  });
  const [emailManuallySet, setEmailManuallySet] = useState(false);
  const [userFormErrors, setUserFormErrors] = useState<{[key: string]: string}>({});
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  
  // Programs state
  const [programs, setPrograms] = useState<Program[]>([]);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [showProgramDetailModal, setShowProgramDetailModal] = useState(false);
  const [selectedProgramDetail, setSelectedProgramDetail] = useState<Program | null>(null);
  
  // Calendar state for session scheduling
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [sessionScheduleInfo, setSessionScheduleInfo] = useState({
    startTime: '15:00',
    endTime: '16:00',
    daysOfWeek: [] as string[]
  });
  const [programSessions, setProgramSessions] = useState<any[]>([]);
  const [programFormData, setProgramFormData] = useState<{
    name: string;
    programType: string;
    description: string;
    tier: string;
    durationMinutes?: number | null;
    sessionsRequired?: number | null;
    color: string;
    scheduleNote?: string | null;
    status: string;
  }>({
    name: '',
    programType: 'after_school',
    description: '',
    tier: 'all',
    durationMinutes: undefined,
    sessionsRequired: undefined,
    color: '#6c63ff',
    scheduleNote: undefined,
    status: 'active'
  });
  
  // Courses state
  const [courses, setCourses] = useState<Course[]>([]);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [courseFormData, setCourseFormData] = useState({
    name: '',
    tier: 'foundations',
    ageGroup: '',
    description: '',
    durationMinutes: 60,
    sessionsRequired: 12,
    prerequisites: '',
    skillsCovered: [] as string[],
    applicablePrograms: [] as string[],
    programId: '', // Add program field
    status: 'active'
  });
  
  // Sessions state
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ClassSession | null>(null);
  
  // Session filters
  const [sessionFilters, setSessionFilters] = useState({
    fromDate: new Date().toISOString().split('T')[0], // Today by default
    toDate: new Date().toISOString().split('T')[0], // Today by default
    selectedProgramIds: [] as string[] // Empty array means "all programs"
  });
  const [sessionFormData, setSessionFormData] = useState({
    programId: '',
    courseId: '',
    instructorId: '',
    dateAvailable: '',
    startTime: '',
    endTime: '',
    location: '',
    maxCapacity: 20,
    description: ''
  });
  
  // Session creation calendar state
  const [sessionCalendarMonth, setSessionCalendarMonth] = useState(new Date());
  const [sessionSelectedDates, setSessionSelectedDates] = useState<Date[]>([]);
  const [isCreatingSessions, setIsCreatingSessions] = useState(false);
  const [showAddStudentsModal, setShowAddStudentsModal] = useState(false);
  const [selectedSessionForStudents, setSelectedSessionForStudents] = useState<ClassSession | null>(null);
  const [availableStudents, setAvailableStudents] = useState<Student[]>([]);
  const [sessionStudents, setSessionStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [userGroupFilterIds, setUserGroupFilterIds] = useState<string[]>([]);
  const [showUserGroupFilter, setShowUserGroupFilter] = useState(false);
  const userGroupFilterRef = useRef<HTMLDivElement>(null);
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  
  // Student form state
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentFormData, setStudentFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gradeLevel: '',
    email: '',
    phone: '',
    address: '',
    parentId: '',
    enrollmentDate: new Date().toISOString().split('T')[0],
    profileNotes: '',
    learningGoals: '',
    slackUsername: ''
  });
  const [studentFormErrors, setStudentFormErrors] = useState<{[key: string]: string}>({});
  const [parentSearchTerm, setParentSearchTerm] = useState<string>('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  
  // User detail view state
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState<User | null>(null);
  const [userChildren, setUserChildren] = useState<any[]>([]);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [instructors, setInstructors] = useState<any[]>([]);
  
  // Child management state
  const [showAddChildForm, setShowAddChildForm] = useState(false);
  const [showEditChildForm, setShowEditChildForm] = useState(false);
  const [selectedChild, setSelectedChild] = useState<any>(null);
  const [childFormData, setChildFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    grade_level: '',
    tier: 'foundations',
    email: '',
    phone: '',
    address: '',
    status: 'trial',
    enrollment_date: new Date().toISOString().split('T')[0]
  });
  const [isSavingChild, setIsSavingChild] = useState(false);

  // Session Detail View state
  const [showSessionDetailModal, setShowSessionDetailModal] = useState(false);
  const [selectedSessionDetail, setSelectedSessionDetail] = useState<ClassSession | null>(null);
  const [sessionDetailStudents, setSessionDetailStudents] = useState<any[]>([]);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<{[key: string]: {status: string, notes: string}}>({});

  // Attendance Dashboard state
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [attendanceFilters, setAttendanceFilters] = useState({
    studentId: '',
    programId: '',
    startDate: '',
    endDate: '',
    status: ''
  });
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // Students filter state
  const [studentGroupFilter, setStudentGroupFilter] = useState<string>('');
  const [studentSearchTerm, setStudentSearchTerm] = useState<string>('');
  
  // Student detail view state
  const [showStudentDetailModal, setShowStudentDetailModal] = useState(false);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState<Student | null>(null);
  const [studentEnrollments, setStudentEnrollments] = useState<any[]>([]);
  const [studentAttendance, setStudentAttendance] = useState<any[]>([]);
  const [showStudentDetailView, setShowStudentDetailView] = useState(false);
  const [viewingStudent, setViewingStudent] = useState<Student | null>(null);
  const [studentDetailLoading, setStudentDetailLoading] = useState(false);
  const [editingAttendanceId, setEditingAttendanceId] = useState<string | null>(null);
  const [editingAttendanceData, setEditingAttendanceData] = useState<any>(null);
  const [isUpdatingAttendance, setIsUpdatingAttendance] = useState(false);
  const [deletingAttendanceId, setDeletingAttendanceId] = useState<string | null>(null);
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [studentEditData, setStudentEditData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    grade_level: '',
    phone: '',
    address: '',
    status: '',
    profile_notes: '',
    learning_goals: '',
    slack_username: ''
  });
  const [isUpdatingStudent, setIsUpdatingStudent] = useState(false);
  const [unenrollingId, setUnenrollingId] = useState<string | null>(null);
  const [unenrollingSessionId, setUnenrollingSessionId] = useState<string | null>(null);
  
  // Attendance calendar state
  const [attendanceCalendarMonth, setAttendanceCalendarMonth] = useState(new Date());
  const [attendanceSelectedDates, setAttendanceSelectedDates] = useState<Date[]>([]);
  const [attendanceSelectedProgram, setAttendanceSelectedProgram] = useState<string>('');
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);
  const [attendanceScheduledSessions, setAttendanceScheduledSessions] = useState<any[]>([]);
  
  // Schedule calendar state
  const [scheduleCalendarMonth, setScheduleCalendarMonth] = useState(new Date());
  const [scheduleSelectedDates, setScheduleSelectedDates] = useState<Date[]>([]);
  const [scheduleSelectedProgram, setScheduleSelectedProgram] = useState<string>('');
  const [studentSchedule, setStudentSchedule] = useState<any[]>([]);
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);
  const [availableSessionsForDates, setAvailableSessionsForDates] = useState<any[]>([]);
  
  // Enrollment state
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [enrollmentProgramId, setEnrollmentProgramId] = useState<string>('');
  const [enrollmentDate, setEnrollmentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [availablePrograms, setAvailablePrograms] = useState<any[]>([]);
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Groups state
  const [groups, setGroups] = useState<Group[]>([]);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    groupType: '',
    status: 'active'
  });
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState<Group | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [memberSearchTerm, setMemberSearchTerm] = useState<string>('');
  
  // Group detail view state
  const [showGroupDetailModal, setShowGroupDetailModal] = useState(false);
  const [selectedGroupDetail, setSelectedGroupDetail] = useState<Group | null>(null);
  const [groupDetailMembers, setGroupDetailMembers] = useState<GroupMember[]>([]);
  const [groupDetailLoading, setGroupDetailLoading] = useState(false);

  // Consent Forms state
  const [consentForms, setConsentForms] = useState<ConsentFormDetail[]>([]);
  const [consentFormsSearch, setConsentFormsSearch] = useState('');
  const [consentFormsLoading, setConsentFormsLoading] = useState(false);
  const [selectedConsentOptions, setSelectedConsentOptions] = useState<string[]>([]);
  const [consentFilterValue, setConsentFilterValue] = useState<'yes' | 'no' | ''>('');
  const [showConsentFilterDropdown, setShowConsentFilterDropdown] = useState(false);
  const consentFilterDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (consentFilterDropdownRef.current && !consentFilterDropdownRef.current.contains(event.target as Node)) {
        setShowConsentFilterDropdown(false);
      }
      if (userGroupFilterRef.current && !userGroupFilterRef.current.contains(event.target as Node)) {
        setShowUserGroupFilter(false);
      }
    };

    if (showConsentFilterDropdown || showUserGroupFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConsentFilterDropdown, showUserGroupFilter]);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
    fetchPrograms();
    fetchCourses();
    fetchSessions();
    fetchInstructors();
    fetchGroups();
  }, []);

  // Refetch users when group filter changes
  useEffect(() => {
    if (activeTab === 'users') {
      fetchDashboardData();
    }
  }, [userGroupFilterIds]);

  // Fetch sessions when Sessions tab becomes active or filters change
  useEffect(() => {
    if (activeTab === 'sessions') {
      if (programs.length === 0) {
        fetchPrograms(); // Fetch programs if not already loaded
      }
      fetchSessions();
    }
  }, [activeTab, sessionFilters]);

  // Fetch students when Students tab becomes active or group filter changes
  useEffect(() => {
    if (activeTab === 'students') {
      fetchStudents();
      if (groups.length === 0) {
        fetchGroups(); // Fetch groups if not already loaded
      }
      if (users.length === 0) {
        fetchDashboardData(); // Fetch users if not already loaded (for parent dropdown)
      }
    }
  }, [activeTab, studentGroupFilter]);

  // Fetch groups when Groups tab becomes active
  useEffect(() => {
    if (activeTab === 'groups') {
      fetchGroups();
    }
  }, [activeTab]);

  // Fetch consent forms when Consent Forms tab becomes active
  useEffect(() => {
    if (activeTab === 'consent-forms') {
      fetchConsentForms();
    }
    if (activeTab === 'attendance') {
      fetchAttendanceRecords();
    }
  }, [activeTab]);

  // Fetch available sessions when program is selected or calendar month changes
  useEffect(() => {
    if (scheduleSelectedProgram && selectedStudentDetail) {
      const year = scheduleCalendarMonth.getFullYear();
      const month = scheduleCalendarMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const fromDate = firstDay.toISOString().split('T')[0];
      const toDate = lastDay.toISOString().split('T')[0];
      
      axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
        params: {
          program_id: scheduleSelectedProgram,
          from_date: fromDate,
          to_date: toDate
        }
      })
      .then((response) => {
        setAvailableSessionsForDates(response.data || []);
      })
      .catch((err) => {
        console.error('Error fetching available sessions:', err);
        setAvailableSessionsForDates([]);
      });
    } else {
      setAvailableSessionsForDates([]);
    }
  }, [scheduleSelectedProgram, scheduleCalendarMonth, selectedStudentDetail]);

  const fetchPrograms = async () => {
    try {
      console.log('🔍 Fetching programs from:', `${config.BACKEND_URL}/api/programs/`);
      const response = await axios.get(`${config.BACKEND_URL}/api/programs/`);
      console.log('📚 Programs response:', response.data);
      setPrograms(response.data.programs || []);
      console.log('📚 Programs set:', response.data.programs?.length || 0, 'programs');
    } catch (error) {
      console.error('❌ Error fetching programs:', error);
      setError('Failed to fetch programs');
    }
  };

  const fetchCourses = async () => {
    try {
      console.log('🔍 Fetching courses from:', `${config.BACKEND_URL}/api/courses/`);
      const response = await axios.get(`${config.BACKEND_URL}/api/courses/`);
      console.log('📚 Courses response:', response.data);
      setCourses(response.data.courses || []);
      console.log('📚 Courses set:', response.data.courses?.length || 0, 'courses');
    } catch (error) {
      console.error('❌ Error fetching courses:', error);
      setError('Failed to fetch courses');
    }
  };

  const fetchSessions = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (sessionFilters.fromDate) {
        params.append('from_date', sessionFilters.fromDate);
      }
      if (sessionFilters.toDate) {
        params.append('to_date', sessionFilters.toDate);
      }
      if (sessionFilters.selectedProgramIds.length > 0) {
        params.append('program_ids', sessionFilters.selectedProgramIds.join(','));
      }
      
      const url = `${config.BACKEND_URL}/api/class-sessions/${params.toString() ? '?' + params.toString() : ''}`;
      console.log('🔍 Fetching sessions from:', url);
      const response = await axios.get(url);
      console.log('📅 Sessions response:', response.data);
      setSessions(Array.isArray(response.data) ? response.data : []);
      console.log('📅 Sessions set:', Array.isArray(response.data) ? response.data.length : 0, 'sessions');
    } catch (error) {
      console.error('❌ Error fetching sessions:', error);
      setError('Failed to fetch sessions');
    }
  };

  const fetchInstructors = async () => {
    try {
      console.log('🔍 Fetching instructors...');
      const response = await axios.get(`${config.BACKEND_URL}/api/test-users`);
      const users = response.data.users || [];
      const instructors = users.filter((user: any) => user.userType === 'teacher');
      console.log(`👨‍🏫 Found ${instructors.length} instructors`);
      setInstructors(instructors);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      setInstructors([]);
    }
  };

  const fetchGroups = async () => {
    try {
      console.log('🔍 Fetching groups from:', `${config.BACKEND_URL}/api/groups/`);
      const response = await axios.get(`${config.BACKEND_URL}/api/groups/`);
      console.log('👥 Groups response:', response.data);
      setGroups(Array.isArray(response.data) ? response.data : []);
      console.log('👥 Groups set:', Array.isArray(response.data) ? response.data.length : 0, 'groups');
    } catch (error) {
      console.error('❌ Error fetching groups:', error);
      setError('Failed to fetch groups');
    }
  };

  const fetchConsentForms = async () => {
    try {
      setConsentFormsLoading(true);
      console.log('🔍 Fetching consent forms from:', `${config.BACKEND_URL}/api/consent-forms/all`);
      const response = await axios.get(`${config.BACKEND_URL}/api/consent-forms/all`);
      console.log('📝 Consent forms response:', response.data);
      if (response.data.success && response.data.consent_forms) {
        setConsentForms(response.data.consent_forms);
        console.log('📝 Consent forms set:', response.data.consent_forms.length, 'forms');
      } else {
        setConsentForms([]);
      }
    } catch (error) {
      console.error('❌ Error fetching consent forms:', error);
      setError('Failed to fetch consent forms');
      setConsentForms([]);
    } finally {
      setConsentFormsLoading(false);
    }
  };


  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use test endpoints that don't require authentication
      console.log('🔍 Fetching dashboard data from test endpoints...');
      
      // Fetch users with group filter
      let usersUrl = `${config.BACKEND_URL}/api/test-users`;
      if (userGroupFilterIds.length > 0) {
        usersUrl += `?group_ids=${userGroupFilterIds.join(',')}`;
      }
      const usersResponse = await axios.get(usersUrl);
      console.log('🔍 Users response:', usersResponse.data);
      setUsers(usersResponse.data.users);

      // Fetch dashboard stats
      const statsResponse = await axios.get(`${config.BACKEND_URL}/api/test-dashboard`);
      console.log('🔍 Stats response:', statsResponse.data);
      setStats(statsResponse.data.stats);

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusUpdate = async (userId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('cognitoToken');
      await axios.put(`${config.BACKEND_URL}/api/users/${userId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh users list
      fetchDashboardData();
    } catch (err: any) {
      console.error('Error updating user status:', err);
      setError(err.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleViewUserDetail = async (user: User) => {
    setSelectedUserDetail(user);
    setUserDetailLoading(true);
    setShowUserDetailModal(true);
    setShowAddChildForm(false);
    setShowEditChildForm(false);
    setSelectedChild(null);
    
    try {
      // If user is a parent, fetch their children
      if (user.userType === 'parent') {
        try {
          // Fetch students where this user is the parent
          const studentsResponse = await axios.get(`${config.BACKEND_URL}/api/test-students`);
          const allStudents = studentsResponse.data.students || [];
          const children = allStudents.filter((student: any) => student.parent_id === user.id);
          setUserChildren(children);
        } catch (err: any) {
          console.error('Error fetching children:', err);
          setUserChildren([]);
        }
      } else {
        setUserChildren([]);
      }
    } catch (error: any) {
      console.error('❌ Error loading user details:', error);
      setError(error.response?.data?.detail || 'Failed to load user details');
    } finally {
      setUserDetailLoading(false);
    }
  };

  const resetChildForm = () => {
    setChildFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      grade_level: '',
      tier: 'foundations',
      email: '',
      phone: '',
      address: '',
      status: 'trial',
      enrollment_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleAddChild = () => {
    resetChildForm();
    setShowAddChildForm(true);
    setShowEditChildForm(false);
    setSelectedChild(null);
  };

  const handleEditChild = (child: any) => {
    setSelectedChild(child);
    setChildFormData({
      first_name: child.first_name || '',
      last_name: child.last_name || '',
      date_of_birth: child.date_of_birth ? new Date(child.date_of_birth).toISOString().split('T')[0] : '',
      grade_level: child.grade_level || '',
      tier: child.tier || 'foundations',
      email: child.email || '',
      phone: child.phone || '',
      address: child.address || '',
      status: child.status || 'trial',
      enrollment_date: child.enrollment_date ? new Date(child.enrollment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setShowEditChildForm(true);
    setShowAddChildForm(false);
  };

  const handleSaveChild = async () => {
    if (!childFormData.first_name || !childFormData.last_name || !childFormData.date_of_birth || !childFormData.grade_level) {
      alert('Please fill in all required fields (First Name, Last Name, Date of Birth, Grade Level)');
      return;
    }

    try {
      setIsSavingChild(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      if (showEditChildForm && selectedChild) {
        // Update existing child
        const response = await axios.put(
          `${config.BACKEND_URL}/api/students/${selectedChild.id}`,
          {
            first_name: childFormData.first_name,
            last_name: childFormData.last_name,
            date_of_birth: childFormData.date_of_birth,
            grade_level: childFormData.grade_level,
            tier: childFormData.tier,
            email: childFormData.email || null,
            phone: childFormData.phone || null,
            address: childFormData.address || null,
            status: childFormData.status,
            enrollment_date: childFormData.enrollment_date,
            parent_id: selectedUserDetail?.id // Ensure parent_id is set
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 200) {
          alert('✅ Child updated successfully!');
          setShowEditChildForm(false);
          setSelectedChild(null);
          resetChildForm();
          // Refresh children list
          if (selectedUserDetail) {
            handleViewUserDetail(selectedUserDetail);
          }
        }
      } else {
        // Create new child
        const response = await axios.post(
          `${config.BACKEND_URL}/api/students`,
          {
            first_name: childFormData.first_name,
            last_name: childFormData.last_name,
            date_of_birth: childFormData.date_of_birth,
            grade_level: childFormData.grade_level,
            tier: childFormData.tier,
            email: childFormData.email || null,
            phone: childFormData.phone || null,
            address: childFormData.address || null,
            status: childFormData.status,
            enrollment_date: childFormData.enrollment_date,
            parent_id: selectedUserDetail?.id
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 201) {
          alert('✅ Child added successfully!');
          setShowAddChildForm(false);
          resetChildForm();
          // Refresh children list
          if (selectedUserDetail) {
            handleViewUserDetail(selectedUserDetail);
          }
        }
      }
    } catch (error: any) {
      console.error('Error saving child:', error);
      alert(`❌ Failed to save child: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setIsSavingChild(false);
    }
  };

  const handleRemoveChild = async (child: any) => {
    if (!window.confirm(`Are you sure you want to remove "${child.first_name} ${child.last_name}" from this parent? This will unlink the child from the parent but will not delete the child record.`)) {
      return;
    }

    try {
      setIsSavingChild(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      // Update child to remove parent_id
      const response = await axios.put(
        `${config.BACKEND_URL}/api/students/${child.id}`,
        {
          parent_id: null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        alert('✅ Child removed from parent successfully!');
        // Refresh children list
        if (selectedUserDetail) {
          handleViewUserDetail(selectedUserDetail);
        }
      }
    } catch (error: any) {
      console.error('Error removing child:', error);
      alert(`❌ Failed to remove child: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setIsSavingChild(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      console.log('🔍 Deleting user with ID:', userId);
      
      // Use test endpoint that doesn't require authentication
      const response = await axios.delete(`${config.BACKEND_URL}/api/test-users/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000
      });
      
      console.log('🔍 Delete response:', response.data);
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        // Refresh users list
        fetchDashboardData();
      } else {
        setError(response.data.message || 'Failed to delete user');
      }
    } catch (err: any) {
      console.error('❌ Error deleting user:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText
      });
      setError(err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to delete user');
    }
  };

  // Students management functions
  const fetchStudents = async () => {
    try {
      console.log('🔍 Fetching students from test endpoint...');
      const params: any = { limit: 1000 };
      if (studentGroupFilter) {
        params.group_id = studentGroupFilter;
      }
      
      const queryString = new URLSearchParams(params).toString();
      const url = `${config.BACKEND_URL}/api/test-students${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url);
      console.log('🔍 Students response:', response.data);
      
      if (response.data.students) {
        setStudents(response.data.students);
      }
    } catch (err: any) {
      console.error('Error fetching students:', err);
      setError(err.response?.data?.detail || 'Failed to fetch students');
    }
  };

  const handleViewStudentDetail = async (student: Student) => {
    // Show the new Student Detail View with competition management
    setViewingStudent(student);
    setShowStudentDetailView(true);
  };

  const handleViewStudentDetailOld = async (student: Student) => {
    setSelectedStudentDetail(student);
    setStudentDetailLoading(true);
    setShowStudentDetailModal(true);
    
    try {
      // Fetch enrollments directly from enrollments API (with status='active' by default)
      let enrollmentsData: any[] = [];
      
      try {
        const token = localStorage.getItem('cognitoToken') || localStorage.getItem('token') || '';
        const enrollmentsResponse = await axios.get(`${config.BACKEND_URL}/api/enrollments/student/${student.id}`, {
          params: { status: 'active' },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        enrollmentsData = enrollmentsResponse.data || [];
        console.log('📋 Fetched enrollments:', enrollmentsData.length);
      } catch (err: any) {
        console.error('Error fetching enrollments:', err);
        enrollmentsData = [];
      }
      
      setStudentEnrollments(enrollmentsData);
      
      // Fetch attendance records
      try {
        const attendanceResponse = await axios.get(`${config.BACKEND_URL}/api/attendance/records`, {
          params: { student_id: student.id }
        });
        setStudentAttendance(attendanceResponse.data.records || []);
      } catch (err: any) {
        console.error('Error fetching attendance:', err);
        setStudentAttendance([]);
      }
      
      // Fetch student schedule
      try {
        const scheduleResponse = await axios.get(`${config.BACKEND_URL}/api/students/${student.id}/schedule`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('cognitoToken') || localStorage.getItem('token') || ''}`
          }
        });
        setStudentSchedule(scheduleResponse.data.schedule || []);
      } catch (err: any) {
        console.error('Error fetching schedule:', err);
        setStudentSchedule([]);
      }
      
      // Reset attendance calendar state when viewing a new student
      setAttendanceSelectedProgram('');
      setAttendanceSelectedDates([]);
      setAttendanceScheduledSessions([]);
      
      // Fetch available programs (filtered by student tier)
      try {
        const programsResponse = await axios.get(`${config.BACKEND_URL}/api/programs/`);
        console.log('🔍 Programs response:', programsResponse.data);
        const allPrograms = programsResponse.data.programs || programsResponse.data || [];
        console.log('🔍 All programs:', allPrograms.length);
        // Filter programs by student tier (or show all if tier is 'all')
        // Use case-insensitive comparison for tier matching
        const studentTierLower = (student.tier || '').toLowerCase();
        const filteredPrograms = allPrograms.filter((p: any) => {
          const programTierLower = (p.tier || '').toLowerCase();
          const matchesTier = programTierLower === 'all' || programTierLower === studentTierLower;
          const isActive = p.status === 'active' || !p.status; // Include active or undefined status
          return matchesTier && isActive;
        });
        console.log('🔍 Filtered programs:', filteredPrograms.length, 'for tier:', student.tier);
        setAvailablePrograms(filteredPrograms);
      } catch (err: any) {
        console.error('Error fetching programs:', err);
        setAvailablePrograms([]);
      }
    } catch (error: any) {
      console.error('❌ Error loading student details:', error);
      setError(error.response?.data?.detail || 'Failed to load student details');
    } finally {
      setStudentDetailLoading(false);
    }
  };
  
  const handleUnenrollStudent = async (enrollmentId: string, programName: string) => {
    if (!selectedStudentDetail) {
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove this student from "${programName}"?`)) {
      return;
    }
    
    setUnenrollingId(enrollmentId);
    try {
      // Get authentication token - try to refresh if expired
      let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
      
      // Check if token is expired and try to refresh
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const exp = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = exp - now;
          
          console.log('🔍 Unenroll - Token expiration check:', {
            exp: new Date(exp).toISOString(),
            now: new Date(now).toISOString(),
            expired: exp < now,
            timeUntilExpiry: timeUntilExpiry,
            timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / 60000)
          });
          
          // Refresh if expired or about to expire (within 5 minutes)
          if (exp < now || timeUntilExpiry < 5 * 60 * 1000) {
            console.log('⚠️ Token expired or expiring soon, attempting to refresh...');
            // Try to refresh the session
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession({ forceRefresh: true });
            if (session.tokens?.accessToken) {
              token = session.tokens.accessToken.toString();
              localStorage.setItem('cognitoToken', token);
              console.log('✅ Token refreshed successfully');
              console.log('🔍 New token length:', token.length);
            } else {
              throw new Error('Unable to refresh token - no access token in session');
            }
          } else {
            console.log('✅ Token is still valid');
          }
        } catch (refreshError: any) {
          console.error('❌ Token refresh failed:', refreshError);
          console.error('❌ Refresh error details:', {
            message: refreshError.message,
            error: refreshError
          });
          alert('Your session has expired. Please log out and log in again.');
          setUnenrollingId(null);
          return;
        }
      }
      
      console.log('🔍 Unenroll - Token found:', token ? 'Yes (length: ' + token.length + ')' : 'No');
      console.log('🔍 Unenroll - Enrollment ID:', enrollmentId);
      
      if (!token || token.trim() === '') {
        const errorMsg = 'Authentication token not found. Please log out and log in again.';
        console.error('❌', errorMsg);
        alert(errorMsg);
        setUnenrollingId(null);
        return;
      }
      
      const response = await axios.delete(
        `${config.BACKEND_URL}/api/enrollments/${enrollmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        alert(`Student successfully removed from "${programName}"`);
        
        // Refresh enrollments list from enrollments API
        try {
          const token = localStorage.getItem('cognitoToken') || localStorage.getItem('token') || '';
          const enrollmentsResponse = await axios.get(`${config.BACKEND_URL}/api/enrollments/student/${selectedStudentDetail.id}`, {
            params: { status: 'active' },
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setStudentEnrollments(enrollmentsResponse.data || []);
          
          // Refresh available programs (student can now enroll in this program again)
          const programsResponse = await axios.get(`${config.BACKEND_URL}/api/programs/`);
          const allPrograms = programsResponse.data.programs || programsResponse.data || [];
          // Use case-insensitive comparison for tier matching
          const studentTierLower = (selectedStudentDetail.tier || '').toLowerCase();
          const filteredPrograms = allPrograms.filter((p: any) => {
            const programTierLower = (p.tier || '').toLowerCase();
            const matchesTier = programTierLower === 'all' || programTierLower === studentTierLower;
            const isActive = p.status === 'active' || !p.status;
            return matchesTier && isActive;
          });
          setAvailablePrograms(filteredPrograms);
        } catch (err: any) {
          console.error('Error refreshing enrollments:', err);
        }
      } else {
        alert(`Error: ${response.data.message || 'Failed to remove student from program'}`);
      }
    } catch (error: any) {
      console.error('Error unenrolling student:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove student from program';
      alert(`Error: ${errorMessage}`);
    } finally {
      setUnenrollingId(null);
    }
  };

  const handleUnenrollFromSession = async (studentSessionId: string, sessionDate: string, programName: string) => {
    if (!selectedStudentDetail) {
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove this student from the session on ${sessionDate ? formatDatePSTShort(sessionDate) : 'this date'}?`)) {
      return;
    }
    
    setUnenrollingSessionId(studentSessionId);
    try {
      // Get authentication token - try to refresh if expired
      let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
      
      // Check if token is expired and try to refresh
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const exp = payload.exp * 1000;
          const now = Date.now();
          const timeUntilExpiry = exp - now;
          
          if (exp < now || timeUntilExpiry < 5 * 60 * 1000) {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession({ forceRefresh: true });
            if (session.tokens?.accessToken) {
              token = session.tokens.accessToken.toString();
              localStorage.setItem('cognitoToken', token);
            } else {
              throw new Error('Unable to refresh token');
            }
          }
        } catch (refreshError: any) {
          console.error('❌ Token refresh failed:', refreshError);
          alert('Your session has expired. Please log out and log in again.');
          setUnenrollingSessionId(null);
          return;
        }
      }
      
      if (!token || token.trim() === '') {
        alert('Authentication token not found. Please log out and log in again.');
        setUnenrollingSessionId(null);
        return;
      }
      
      const response = await axios.delete(
        `${config.BACKEND_URL}/api/students/${selectedStudentDetail.id}/sessions/${studentSessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        alert(`Student successfully removed from session`);
        
        // Refresh student schedule
        try {
          const scheduleResponse = await axios.get(`${config.BACKEND_URL}/api/students/${selectedStudentDetail.id}/schedule`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setStudentSchedule(scheduleResponse.data.schedule || []);
        } catch (err: any) {
          console.error('Error refreshing schedule:', err);
        }
      } else {
        alert(`Error: ${response.data.message || 'Failed to remove student from session'}`);
      }
    } catch (error: any) {
      console.error('Error unenrolling from session:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove student from session';
      alert(`Error: ${errorMessage}`);
    } finally {
      setUnenrollingSessionId(null);
    }
  };

  const handleEnrollStudent = async () => {
    if (!selectedStudentDetail || !enrollmentProgramId) {
      alert('Please select a program');
      return;
    }
    
    // Check if already enrolled
    const alreadyEnrolled = studentEnrollments.some((e: any) => 
      e.program_id === enrollmentProgramId && e.status === 'active'
    );
    
    if (alreadyEnrolled) {
      alert('Student is already enrolled in this program');
      return;
    }
    
    setIsEnrolling(true);
    try {
      // Get authentication token - try to refresh if expired
      let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
      
      // Check if token is expired and try to refresh
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const exp = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = exp - now;
          
          console.log('🔍 Token expiration check:', {
            exp: new Date(exp).toISOString(),
            now: new Date(now).toISOString(),
            expired: exp < now,
            timeUntilExpiry: timeUntilExpiry,
            timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / 60000)
          });
          
          // Refresh if expired or about to expire (within 5 minutes)
          if (exp < now || timeUntilExpiry < 5 * 60 * 1000) {
            console.log('⚠️ Token expired or expiring soon, attempting to refresh...');
            // Try to refresh the session
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession({ forceRefresh: true });
            if (session.tokens?.accessToken) {
              token = session.tokens.accessToken.toString();
              localStorage.setItem('cognitoToken', token);
              console.log('✅ Token refreshed successfully');
              console.log('🔍 New token length:', token.length);
            } else {
              throw new Error('Unable to refresh token - no access token in session');
            }
          } else {
            console.log('✅ Token is still valid');
          }
        } catch (refreshError: any) {
          console.error('❌ Token refresh failed:', refreshError);
          console.error('❌ Refresh error details:', {
            message: refreshError.message,
            error: refreshError
          });
          alert('Your session has expired. Please log out and log in again.');
          setIsEnrolling(false);
          return;
        }
      }
      
      console.log('🔍 Enrollment - Token found:', token ? 'Yes (length: ' + token.length + ')' : 'No');
      console.log('🔍 Enrollment - Student ID:', selectedStudentDetail.id);
      console.log('🔍 Enrollment - Program ID:', enrollmentProgramId);
      console.log('🔍 Enrollment - Enrollment Date:', enrollmentDate);
      
      if (!token || token.trim() === '') {
        const errorMsg = 'Authentication token not found. Please log out and log in again.';
        console.error('❌', errorMsg);
        alert(errorMsg);
        setIsEnrolling(false);
        return;
      }
      
      const enrollmentData = {
        student_id: selectedStudentDetail.id,
        program_id: enrollmentProgramId,
        enrollment_date: enrollmentDate || new Date().toISOString().split('T')[0]
      };
      
      console.log('🔍 Enrollment - Request URL:', `${config.BACKEND_URL}/api/enrollments`);
      console.log('🔍 Enrollment - Request data:', enrollmentData);
      console.log('🔍 Enrollment - Authorization header:', `Bearer ${token.substring(0, 20)}...`);
      
      const response = await axios.post(`${config.BACKEND_URL}/api/enrollments`, enrollmentData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Enrollment - Success response:', response.data);
      
      alert('Student enrolled successfully!');
      
      // Refresh enrollments list from enrollments API
      let enrollmentsData: any[] = [];
      try {
        const enrollmentsResponse = await axios.get(`${config.BACKEND_URL}/api/enrollments/student/${selectedStudentDetail.id}`, {
          params: { status: 'active' },
          headers: {
            'Authorization': `Bearer ${token || ''}`
          }
        });
        enrollmentsData = enrollmentsResponse.data || [];
      } catch (err: any) {
        console.error('Error refreshing enrollments from schedule:', err);
        // Fallback to regular enrollments
        try {
          const enrollmentsResponse = await axios.get(`${config.BACKEND_URL}/api/enrollments/student/${selectedStudentDetail.id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          enrollmentsData = enrollmentsResponse.data || [];
        } catch (err2: any) {
          console.error('Error fetching enrollments:', err2);
          enrollmentsData = [];
        }
      }
      
      setStudentEnrollments(enrollmentsData);
      
      // Reset form
      setShowEnrollmentForm(false);
      setEnrollmentProgramId('');
      setEnrollmentDate(new Date().toISOString().split('T')[0]);
    } catch (error: any) {
      console.error('❌ Error enrolling student:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      let errorMessage = 'Failed to enroll student';
      
      if (error.response) {
        console.error('❌ Response status:', error.response.status);
        console.error('❌ Response data:', error.response.data);
        
        if (error.response.status === 401) {
          // Check if token might be expired
          const token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
          console.error('❌ 401 Error - Token present:', token ? 'Yes' : 'No');
          
          if (token) {
            try {
              // Decode JWT to check expiration (without verification)
              const payload = JSON.parse(atob(token.split('.')[1]));
              const exp = payload.exp * 1000; // Convert to milliseconds
              const now = Date.now();
              
              console.error('❌ Token expiration check:', {
                exp: new Date(exp).toISOString(),
                now: new Date(now).toISOString(),
                expired: exp < now,
                timeUntilExpiry: exp - now
              });
              
              if (exp < now) {
                errorMessage = 'Your session has expired. Please log out and log in again to continue.';
                // Clear expired token
                localStorage.removeItem('cognitoToken');
                localStorage.removeItem('token');
              } else {
                errorMessage = `Authentication failed. Server response: ${error.response.data?.detail || 'Unknown error'}. Please log out and log in again.`;
              }
            } catch (e) {
              console.error('❌ Error decoding token:', e);
              errorMessage = 'Authentication failed. Please log out and log in again.';
            }
          } else {
            errorMessage = 'Authentication token not found. Please log in again.';
          }
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to enroll students.';
        } else {
          const detail = error.response.data?.detail || error.response.data?.message;
          errorMessage = detail || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        console.error('❌ No response received:', error.request);
        errorMessage = 'No response from server. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsEnrolling(false);
    }
  };

  // Attendance Dashboard functions
  const fetchAttendanceRecords = async () => {
    try {
      setAttendanceLoading(true);
      const params: any = {};
      
      if (attendanceFilters.studentId) params.student_id = attendanceFilters.studentId;
      if (attendanceFilters.programId) params.program_id = attendanceFilters.programId;
      if (attendanceFilters.startDate) params.start_date = attendanceFilters.startDate;
      if (attendanceFilters.endDate) params.end_date = attendanceFilters.endDate;
      if (attendanceFilters.status) params.status = attendanceFilters.status;
      
      const queryString = new URLSearchParams(params).toString();
      const url = `${config.BACKEND_URL}/api/attendance/records${queryString ? '?' + queryString : ''}`;
      
      console.log('🔍 Fetching attendance records:', url);
      const response = await axios.get(url);
      
      if (response.data.success) {
        setAttendanceRecords(response.data.records || []);
        console.log(`✅ Loaded ${response.data.total} attendance records`);
      } else {
        setError('Failed to fetch attendance records');
      }
    } catch (err: any) {
      console.error('Error fetching attendance records:', err);
      setError(err.response?.data?.detail || 'Failed to fetch attendance records');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleAttendanceFilterChange = (field: string, value: string) => {
    setAttendanceFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteStudent = async (studentId: string) => {
    if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('cognitoToken') || localStorage.getItem('token') || '';
      
      if (!token || token.trim() === '') {
        setError('Authentication required. Please log in again.');
        return;
      }
      
      console.log('🔍 Deleting student with ID:', studentId);
      console.log('🔍 Using endpoint:', `${config.BACKEND_URL}/api/students/${studentId}`);
      
      const response = await axios.delete(`${config.BACKEND_URL}/api/students/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000
      });
      
      console.log('🔍 Delete response:', response.data);
      
      if (response.data.success) {
        // Show success message
        alert(`✅ ${response.data.message}`);
        
        // Refresh students list
        fetchStudents();
        // Also refresh dashboard data to update stats
        fetchDashboardData();
      } else {
        setError(response.data.message || 'Failed to delete student');
      }
    } catch (err: any) {
      console.error('❌ Error deleting student:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText
      });
      const errorMessage = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to delete student';
      setError(errorMessage);
      alert(`❌ Error: ${errorMessage}`);
    }
  };

  // User management functions
  const generateTemporaryPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleUserFormChange = (field: string, value: any) => {
    console.log(`🔍 Form field changed: ${field} = ${value}`);
    setUserFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log('🔍 Updated form data:', newData);
      return newData;
    });
    
    // Clear error for this field
    if (userFormErrors[field]) {
      setUserFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateUserForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!userFormData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!userFormData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!userFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userFormData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!userFormData.phone.trim()) {
      errors.phone = 'Phone is required';
    } else {
      const phoneDigits = userFormData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        errors.phone = 'Phone must be a 10-digit Canadian number (e.g., 2501234567)';
      }
    }
    
    if (userFormData.passwordOption === 'custom' && !userFormData.password.trim()) {
      errors.password = 'Password is required';
    } else if (userFormData.passwordOption === 'custom' && userFormData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateUserForm()) {
      return;
    }
    
    setIsCreatingUser(true);
    
    try {
      let finalPassword = userFormData.password;
      
      // Generate password based on option
      if (userFormData.passwordOption === 'generate') {
        finalPassword = generateTemporaryPassword();
      } else if (userFormData.passwordOption === 'temporary') {
        finalPassword = userFormData.temporaryPassword || generateTemporaryPassword();
      }
      
      // Format phone number: remove all non-digits and ensure it's 10 digits, then add +1 for backend
      const phoneDigits = userFormData.phone.replace(/\D/g, '');
      const formattedPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : userFormData.phone.trim();
      
      const userData = {
        firstName: userFormData.firstName.trim(),
        lastName: userFormData.lastName.trim(),
        email: userFormData.email.trim().toLowerCase(),
        phone: formattedPhone,
        userType: userFormData.userType,
        password: finalPassword,
        forcePasswordChange: userFormData.forcePasswordChange || userFormData.passwordOption !== 'custom'
      };
      
      console.log('🔍 Creating user:', userData);
      
      const response = await axios.post(`${config.BACKEND_URL}/api/admin/users`, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 15000
      });
      
      console.log('🔍 Create user response:', response.data);
      
      if (response.data.success) {
        alert(`✅ User created successfully!\n\nEmail: ${userData.email}\nPassword: ${finalPassword}\n\n${userData.forcePasswordChange ? 'User will be required to change password on first login.' : 'User can use this password permanently.'}`);
        
        // Reset form
        setUserFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          userType: 'parent',
          password: '',
          passwordOption: 'generate',
          temporaryPassword: '',
          forcePasswordChange: false
        });
        setUserFormErrors({});
        setShowUserForm(false);
        
        // Refresh users list
        fetchDashboardData();
      } else {
        setError(response.data.message || 'Failed to create user');
      }
    } catch (err: any) {
      console.error('❌ Error creating user:', err);
      setError(err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to create user');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditingUser(true);
    setShowPasswordReset(false);
    setUserFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      userType: user.userType || 'parent',
      password: '',
      passwordOption: 'generate',
      temporaryPassword: '',
      forcePasswordChange: false
    });
    setUserFormErrors({});
    setEmailManuallySet(true); // Email is read-only when editing
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      setIsCreatingUser(true);
      setError(null);
      
      const updateData = {
        firstName: userFormData.firstName,
        lastName: userFormData.lastName,
        phone: userFormData.phone,
        userType: userFormData.userType,
        status: selectedUser.status // Preserve status
      };
      
      const response = await axios.put(
        `${config.BACKEND_URL}/api/admin/users/${selectedUser.id}`,
        updateData
      );
      
      if (response.data.success) {
        console.log('✅ User updated successfully:', response.data);
        setIsEditingUser(false);
        setSelectedUser(null);
        fetchDashboardData();
        alert('User updated successfully!');
      } else {
        setError(response.data.message || 'Failed to update user');
      }
    } catch (err: any) {
      console.error('❌ Error updating user:', err);
      setError(err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to update user');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    try {
      setIsCreatingUser(true);
      setError(null);
      
      const passwordData = {
        passwordOption: userFormData.passwordOption,
        password: userFormData.passwordOption === 'custom' ? userFormData.password : '',
        forcePasswordChange: userFormData.forcePasswordChange
      };
      
      const response = await axios.post(
        `${config.BACKEND_URL}/api/admin/users/${selectedUser.id}/reset-password`,
        passwordData
      );
      
      if (response.data.success) {
        console.log('✅ Password reset successfully:', response.data);
        const newPassword = response.data.password;
        if (newPassword) {
          alert(`Password reset successfully!\n\nGenerated password: ${newPassword}\n\nPlease save this password securely.`);
        } else {
          alert('Password reset successfully!');
        }
        setShowPasswordReset(false);
        // Reset password fields
        setUserFormData(prev => ({
          ...prev,
          password: '',
          passwordOption: 'generate',
          temporaryPassword: '',
          forcePasswordChange: false
        }));
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err: any) {
      console.error('❌ Error resetting password:', err);
      setError(err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to reset password');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const resetUserForm = () => {
    setUserFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      userType: 'parent',
      password: '',
      passwordOption: 'generate',
      temporaryPassword: '',
      forcePasswordChange: false
    });
    setUserFormErrors({});
    setEmailManuallySet(false);
    setShowUserForm(false);
    setIsEditingUser(false);
    setSelectedUser(null);
    setShowPasswordReset(false);
  };

  // Program management functions
  const handleAddProgram = () => {
    setSelectedProgram(null);
    setProgramFormData({
      name: '',
      programType: 'after_school',
      description: '',
      tier: 'all',
      durationMinutes: undefined,
      sessionsRequired: undefined,
      color: '#6c63ff',
      scheduleNote: undefined,
      status: 'active'
    });
    setShowProgramForm(true);
  };

  const handleViewProgram = async (program: Program) => {
    setSelectedProgramDetail(program);
    setShowProgramDetailModal(true);
    // Reset calendar to current month
    setCurrentMonth(new Date());
    setSelectedDates([]);
    
    // Fetch existing sessions for this program
    try {
      const response = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
        params: {
          program_ids: program.id
        }
      });
      setProgramSessions(Array.isArray(response.data) ? response.data : []);
      console.log('📅 Fetched program sessions:', Array.isArray(response.data) ? response.data.length : 0);
    } catch (error) {
      console.error('❌ Error fetching program sessions:', error);
      setProgramSessions([]);
    }
  };

  const handleEditProgram = (program: Program) => {
    setSelectedProgram(program);
    setProgramFormData({
      name: program.name,
      programType: program.programType,
      description: program.description,
      tier: program.tier,
      durationMinutes: program.durationMinutes ?? null,
      sessionsRequired: program.sessionsRequired ?? null,
      color: program.color,
      scheduleNote: (program as any).scheduleNote ?? null,
      status: program.status
    });
    setShowProgramForm(true);
  };

  const handleDeleteProgram = async (programId: string) => {
    const program = programs.find(p => p.id === programId);
    const programName = program?.name || 'this program';
    
    if (window.confirm(
      `⚠️ WARNING: Delete "${programName}"?\n\n` +
      `This will permanently delete:\n` +
      `• The program itself\n` +
      `• All class sessions for this program\n` +
      `• All enrollments in this program\n` +
      `• All attendance records for this program\n` +
      `• All session packages for this program\n` +
      `• All drop-in sessions for this program\n\n` +
      `NOTE: Courses will NOT be deleted (they are independent)\n\n` +
      `This action cannot be undone!`
    )) {
      try {
        console.log(`🗑️ Deleting program: ${programName} (ID: ${programId})`);
        await axios.delete(`${config.BACKEND_URL}/api/programs/${programId}`);
        setPrograms(programs.filter(p => p.id !== programId));
        console.log(`✅ Successfully deleted program: ${programName}`);
      } catch (error) {
        console.error('❌ Error deleting program:', error);
        setError('Failed to delete program');
      }
    }
  };

  const handleProgramFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare form data, removing optional fields if they're null/undefined
      const submitData: any = { ...programFormData };
      if (submitData.sessionsRequired === null || submitData.sessionsRequired === undefined) {
        delete submitData.sessionsRequired;
      }
      if (submitData.durationMinutes === null || submitData.durationMinutes === undefined) {
        delete submitData.durationMinutes;
      }
      if (submitData.scheduleNote === null || submitData.scheduleNote === undefined || submitData.scheduleNote === '') {
        delete submitData.scheduleNote;
      }
      
      if (selectedProgram) {
        // Update existing program
        console.log('📝 Updating program:', selectedProgram.id, submitData);
        const response = await axios.put(`${config.BACKEND_URL}/api/programs/${selectedProgram.id}`, submitData);
        console.log('✅ Program updated:', response.data);
        setPrograms(programs.map(p => p.id === selectedProgram.id ? { ...p, ...response.data } : p));
      } else {
        // Create new program
        console.log('📝 Creating new program:', submitData);
        const response = await axios.post(`${config.BACKEND_URL}/api/programs/`, submitData);
        console.log('✅ Program created:', response.data);
        setPrograms([...programs, response.data]);
      }
      setShowProgramForm(false);
      setSelectedProgram(null);
      setError(null);
    } catch (error: any) {
      console.error('❌ Error saving program:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save program';
      setError(`Failed to save program: ${errorMessage}`);
    }
  };

  const handleProgramFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProgramFormData(prev => ({
      ...prev,
      [name]: name === 'durationMinutes' 
        ? (value === '' ? null : parseInt(value) || null)
        : name === 'sessionsRequired' 
          ? (value === '' ? null : parseInt(value) || null)
          : value
    }));
  };

  // Course management functions
  const handleAddCourse = () => {
    setSelectedCourse(null);
    setCourseFormData({
      name: '',
      tier: 'foundations',
      ageGroup: '',
      description: '',
      durationMinutes: 60,
      sessionsRequired: 12,
      prerequisites: '',
      skillsCovered: [],
      applicablePrograms: [],
      programId: '', // Reset program field
      status: 'active'
    });
    setShowCourseForm(true);
  };

  const handleEditCourse = (course: Course) => {
    setSelectedCourse(course);
    setCourseFormData({
      name: course.name,
      tier: course.tier,
      ageGroup: course.ageGroup,
      description: course.description,
      durationMinutes: course.durationMinutes,
      sessionsRequired: course.sessionsRequired,
      prerequisites: course.prerequisites,
      skillsCovered: course.skillsCovered,
      applicablePrograms: course.applicablePrograms,
      programId: course.programId || '', // Add program field, default to empty if not set
      status: course.status
    });
    setShowCourseForm(true);
  };

  const handleDeleteCourse = async (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    const courseName = course?.name || 'this course';
    
    if (window.confirm(
      `⚠️ WARNING: Delete "${courseName}"?\n\n` +
      `This will permanently delete:\n` +
      `• The course itself\n` +
      `• All class sessions\n` +
      `• All enrollments\n\n` +
      `This action cannot be undone!`
    )) {
      try {
        console.log(`🗑️ Deleting course: ${courseName} (ID: ${courseId})`);
        await axios.delete(`${config.BACKEND_URL}/api/courses/${courseId}`);
        setCourses(courses.filter(c => c.id !== courseId));
        console.log(`✅ Successfully deleted course: ${courseName}`);
      } catch (error) {
        console.error('❌ Error deleting course:', error);
        setError('Failed to delete course');
      }
    }
  };

  // Session management functions
  const handleAddSession = () => {
    setSelectedSession(null);
    setError('');
    setSessionFormData({
      programId: '',
      courseId: '',
      instructorId: '',
      dateAvailable: '',
      startTime: '',
      endTime: '',
      location: '',
      maxCapacity: 20,
      description: ''
    });
    setSessionSelectedDates([]);
    setSessionCalendarMonth(new Date());
    setShowSessionForm(true);
  };

  const handleEditSession = (session: ClassSession) => {
    setSelectedSession(session);
    setError('');
    setSessionFormData({
      programId: session.program_id || '',
      courseId: session.course_id || '',
      instructorId: session.instructor_id || '',
      dateAvailable: session.date_available,
      startTime: session.start_time.substring(0, 5), // Extract HH:MM from HH:MM:SS
      endTime: session.end_time.substring(0, 5),
      location: session.location || '',
      maxCapacity: session.max_capacity,
      description: session.description || ''
    });
    // When editing, set selected date from the session
    if (session.date_available) {
      setSessionSelectedDates([new Date(session.date_available)]);
      // Set calendar month to the session date
      setSessionCalendarMonth(new Date(session.date_available));
    } else {
      setSessionSelectedDates([]);
    }
    setShowSessionForm(true);
  };

  const handleDeleteSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    const sessionInfo = session 
      ? `${session.program_name || 'Session'} - ${session.date_available} ${session.start_time.substring(0, 5)}`
      : 'this session';
    
    if (window.confirm(`⚠️ Delete session "${sessionInfo}"?\n\nThis action cannot be undone!`)) {
      try {
        // Get authentication token
        let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
        
        console.log(`🗑️ Deleting session: ${sessionInfo} (ID: ${sessionId})`);
        console.log(`🔍 Delete Session - Token found:`, token ? 'Yes (length: ' + token.length + ')' : 'No');
        
        const headers: any = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        await axios.delete(`${config.BACKEND_URL}/api/class-sessions/${sessionId}`, {
          headers: headers
        });
        await fetchSessions();
        console.log(`✅ Successfully deleted session: ${sessionInfo}`);
        alert('Session deleted successfully!');
      } catch (error: any) {
        console.error('❌ Error deleting session:', error);
        let errorMessage = 'Failed to delete session';
        
        if (error.response) {
          if (error.response.status === 401) {
            errorMessage = 'Authentication required. Please log out and log in again.';
          } else if (error.response.status === 403) {
            errorMessage = 'You do not have permission to delete this session.';
          } else if (error.response.status === 404) {
            errorMessage = 'Session not found.';
          } else {
            errorMessage = error.response.data?.detail || `Server error: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.';
        }
        
        alert(errorMessage);
        setError(errorMessage);
      }
    }
  };

  const handleSessionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSessionFormData(prev => ({
      ...prev,
      [name]: name === 'maxCapacity' ? parseInt(value) || 20 : value
    }));
  };

  const handleSessionFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields (only time is mandatory)
    if (!sessionFormData.startTime || !sessionFormData.endTime) {
      setError('Start time and end time are required');
      return;
    }
    
    setIsCreatingSessions(true);
    setError(null);
    
    try {
      // Determine which dates to use
      const datesToUse = sessionSelectedDates.length > 0 
        ? sessionSelectedDates 
        : (sessionFormData.dateAvailable ? [new Date(sessionFormData.dateAvailable)] : []);
      
      if (datesToUse.length === 0 && !selectedSession) {
        setError('Please select at least one date or provide a date');
        setIsCreatingSessions(false);
        return;
      }
      
      if (selectedSession) {
        // Update existing session - use single date
        const apiData: any = {
          start_time: sessionFormData.startTime,
          end_time: sessionFormData.endTime,
          max_capacity: sessionFormData.maxCapacity || 20
        };

        // Add optional fields only if they have values
        if (sessionFormData.programId) apiData.program_id = sessionFormData.programId;
        if (sessionFormData.courseId) apiData.course_id = sessionFormData.courseId;
        if (sessionFormData.instructorId) apiData.instructor_id = sessionFormData.instructorId;
        if (sessionFormData.dateAvailable) apiData.date_available = sessionFormData.dateAvailable;
        if (sessionFormData.location) apiData.location = sessionFormData.location;
        if (sessionFormData.description) apiData.description = sessionFormData.description;
        
        await axios.put(`${config.BACKEND_URL}/api/class-sessions/${selectedSession.id}`, apiData);
        await fetchSessions();
        setShowSessionForm(false);
        setSelectedSession(null);
        setError(null);
      } else {
        // Create new sessions - one for each selected date
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];
        
        for (const date of datesToUse) {
          try {
            const dateStr = date.toISOString().split('T')[0];
            
            // Transform camelCase to snake_case for API
            const apiData: any = {
              start_time: sessionFormData.startTime,
              end_time: sessionFormData.endTime,
              max_capacity: sessionFormData.maxCapacity || 20,
              date_available: dateStr
            };

            // Add optional fields only if they have values
            if (sessionFormData.programId) apiData.program_id = sessionFormData.programId;
            if (sessionFormData.courseId) apiData.course_id = sessionFormData.courseId;
            if (sessionFormData.instructorId) apiData.instructor_id = sessionFormData.instructorId;
            if (sessionFormData.location) apiData.location = sessionFormData.location;
            if (sessionFormData.description) apiData.description = sessionFormData.description;
            
            console.log(`📝 Creating session for ${dateStr}:`, apiData);
            
            await axios.post(`${config.BACKEND_URL}/api/class-sessions/`, apiData);
            successCount++;
          } catch (error: any) {
            console.error(`❌ Error creating session for ${date.toLocaleDateString()}:`, error);
            errorCount++;
            errors.push(`${date.toLocaleDateString()}: ${error.response?.data?.detail || error.message}`);
          }
        }
        
        await fetchSessions();
        
        if (successCount > 0) {
          if (errorCount === 0) {
            setShowSessionForm(false);
            setSelectedSession(null);
            setError(null);
            setSessionSelectedDates([]);
            setSessionFormData({
              programId: '',
              courseId: '',
              instructorId: '',
              dateAvailable: '',
              startTime: '',
              endTime: '',
              location: '',
              maxCapacity: 20,
              description: ''
            });
          } else {
            setError(`Created ${successCount} session(s) successfully. ${errorCount} failed:\n${errors.join('\n')}`);
          }
        } else {
          setError(`Failed to create sessions:\n${errors.join('\n')}`);
        }
      }
    } catch (error: any) {
      console.error('❌ Error saving session:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save session';
      setError(errorMessage);
    } finally {
      setIsCreatingSessions(false);
    }
  };

  const handleViewSessionDetail = async (session: ClassSession) => {
    setSelectedSessionDetail(session);
    setSessionDetailLoading(true);
    setEditingAttendance({});
    
    try {
      // Fetch session details with students
      const response = await axios.get(`${config.BACKEND_URL}/api/attendance/session-students/${session.id}`);
      if (response.data.success) {
        setSessionDetailStudents(response.data.students || []);
      }
      setShowSessionDetailModal(true);
    } catch (error: any) {
      console.error('❌ Error loading session details:', error);
      setError('Failed to load session details');
    } finally {
      setSessionDetailLoading(false);
    }
  };

  const handleMarkAttendance = async (studentId: string, status: string, notes: string = '') => {
    if (!selectedSessionDetail) return;
    
    try {
      const attendanceData = [{
        student_id: studentId,
        status: status,
        notes: notes
      }];
      
      await axios.post(`${config.BACKEND_URL}/api/attendance/mark-attendance`, {
        session_id: selectedSessionDetail.id,
        attendance_date: selectedSessionDetail.date_available,
        attendance_data: attendanceData
      });
      
      // Refresh session details
      await handleViewSessionDetail(selectedSessionDetail);
      setError(null);
    } catch (error: any) {
      console.error('❌ Error marking attendance:', error);
      setError(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  const handleBulkMarkAttendance = async () => {
    if (!selectedSessionDetail) return;
    
    try {
      const attendanceData = sessionDetailStudents.map(student => ({
        student_id: student.student_id || student.id,
        status: editingAttendance[student.student_id || student.id]?.status || student.attendance_status || 'absent',
        notes: editingAttendance[student.student_id || student.id]?.notes || student.notes || ''
      }));
      
      await axios.post(`${config.BACKEND_URL}/api/attendance/mark-attendance`, {
        session_id: selectedSessionDetail.id,
        attendance_date: selectedSessionDetail.date_available,
        attendance_data: attendanceData
      });
      
      // Refresh session details
      await handleViewSessionDetail(selectedSessionDetail);
      setEditingAttendance({});
      setError(null);
    } catch (error: any) {
      console.error('❌ Error marking attendance:', error);
      setError(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  const handleAddStudentsToSession = async (session: ClassSession) => {
    setSelectedSessionForStudents(session);
    setSelectedStudentIds([]);
    setSelectedGroupIds([]);
    
    try {
      // Fetch all students
      const studentsResponse = await axios.get(`${config.BACKEND_URL}/api/test-students`);
      const allStudents = studentsResponse.data.students || [];
      setAvailableStudents(allStudents);
      
      // Fetch all groups
      const groupsResponse = await axios.get(`${config.BACKEND_URL}/api/groups/`);
      const allGroups = Array.isArray(groupsResponse.data) ? groupsResponse.data : [];
      // Store groups in a way we can access them - we'll use the groups state
      setGroups(allGroups);
      
      // Fetch students already in this session
      const sessionStudentsResponse = await axios.get(`${config.BACKEND_URL}/api/attendance/session-students/${session.id}`);
      const enrolledStudentIds = (sessionStudentsResponse.data.students || []).map((s: any) => s.student_id);
      setSessionStudents(sessionStudentsResponse.data.students || []);
      setSelectedStudentIds(enrolledStudentIds);
      
      setShowAddStudentsModal(true);
    } catch (error) {
      console.error('❌ Error loading students:', error);
      setError('Failed to load students');
    }
  };

  const handleSaveStudentsToSession = async () => {
    if (!selectedSessionForStudents || selectedStudentIds.length === 0) return;
    
    if (!selectedSessionForStudents.program_id) {
      setError('Session must have a program assigned to add students');
      return;
    }
    
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      
      // Add each student to the session
      for (const studentId of selectedStudentIds) {
        // Skip if already enrolled
        if (sessionStudents.some(s => s.id === studentId)) {
          continue;
        }
        
        try {
          const response = await axios.post(`${config.BACKEND_URL}/api/course-registrations`, {
            studentId: studentId,
            programId: selectedSessionForStudents.program_id,
            selectedSessions: [selectedSessionForStudents.id]
          });
          
          if (response.data.success) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`Failed to add student ${studentId}`);
          }
        } catch (error: any) {
          errorCount++;
          const student = availableStudents.find(s => s.id === studentId);
          const studentName = student ? `${student.first_name} ${student.last_name}` : studentId;
          errors.push(`${studentName}: ${error.response?.data?.detail || error.message}`);
        }
      }
      
      if (successCount > 0) {
        await fetchSessions();
        if (errorCount === 0) {
          setShowAddStudentsModal(false);
          setSelectedSessionForStudents(null);
          setSelectedStudentIds([]);
          setSelectedGroupIds([]);
          setError(null);
        } else {
          setError(`Added ${successCount} student(s), but ${errorCount} failed:\n${errors.join('\n')}`);
        }
      } else {
        setError(`Failed to add students:\n${errors.join('\n')}`);
      }
    } catch (error: any) {
      console.error('❌ Error adding students to session:', error);
      setError(error.response?.data?.detail || 'Failed to add students to session');
    }
  };

  // Group management functions
  const handleAddGroup = () => {
    setSelectedGroup(null);
    setError('');
    setGroupFormData({
      name: '',
      description: '',
      groupType: '',
      status: 'active'
    });
    setShowGroupForm(true);
  };

  const handleEditGroup = (group: Group) => {
    setSelectedGroup(group);
    setError('');
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      groupType: group.group_type || '',
      status: group.status
    });
    setShowGroupForm(true);
  };

  const handleDeleteGroup = async (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    const groupName = group?.name || 'this group';
    
    if (window.confirm(`⚠️ Delete group "${groupName}"?\n\nThis will remove all students from the group.\n\nThis action cannot be undone!`)) {
      try {
        console.log(`🗑️ Deleting group: ${groupName} (ID: ${groupId})`);
        await axios.delete(`${config.BACKEND_URL}/api/groups/${groupId}`);
        await fetchGroups();
        console.log(`✅ Successfully deleted group: ${groupName}`);
      } catch (error) {
        console.error('❌ Error deleting group:', error);
        setError('Failed to delete group');
      }
    }
  };

  const handleGroupFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGroupFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!groupFormData.name.trim()) {
      setError('Group name is required');
      return;
    }
    
    try {
      const apiData: any = {
        name: groupFormData.name.trim(),
        status: groupFormData.status || 'active'
      };

      if (groupFormData.description) apiData.description = groupFormData.description.trim();
      if (groupFormData.groupType) apiData.group_type = groupFormData.groupType.trim();
      
      console.log('📝 Submitting group with data:', apiData);

      if (selectedGroup) {
        // Update existing group
        await axios.put(`${config.BACKEND_URL}/api/groups/${selectedGroup.id}`, apiData);
      } else {
        // Create new group
        await axios.post(`${config.BACKEND_URL}/api/groups/`, apiData);
      }
      
      await fetchGroups();
      setShowGroupForm(false);
      setSelectedGroup(null);
      setError(null);
    } catch (error: any) {
      console.error('❌ Error saving group:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save group';
      setError(errorMessage);
    }
  };

  const handleViewGroupDetail = async (group: Group) => {
    setSelectedGroupDetail(group);
    setGroupDetailLoading(true);
    setShowGroupDetailModal(true);
    
    try {
      // Fetch group details (to get latest info)
      const groupResponse = await axios.get(`${config.BACKEND_URL}/api/groups/${group.id}`);
      setSelectedGroupDetail(groupResponse.data);
      
      // Fetch group members
      const membersResponse = await axios.get(`${config.BACKEND_URL}/api/groups/${group.id}/members`);
      setGroupDetailMembers(membersResponse.data || []);
    } catch (error: any) {
      console.error('❌ Error loading group details:', error);
      setError(error.response?.data?.detail || 'Failed to load group details');
    } finally {
      setGroupDetailLoading(false);
    }
  };

  const handleAddMembersToGroup = async (group: Group) => {
    setSelectedGroupForMembers(group);
    setSelectedMemberIds([]);
    
    try {
      // Fetch all students
      const studentsResponse = await axios.get(`${config.BACKEND_URL}/api/test-students`);
      const allStudents = studentsResponse.data.students || [];
      setAvailableStudents(allStudents);
      
      // Fetch students already in this group
      const membersResponse = await axios.get(`${config.BACKEND_URL}/api/groups/${group.id}/members`);
      const memberStudentIds = (membersResponse.data || []).map((m: GroupMember) => m.student_id);
      setGroupMembers(membersResponse.data || []);
      setSelectedMemberIds(memberStudentIds);
      
      setShowAddMembersModal(true);
    } catch (error) {
      console.error('❌ Error loading group members:', error);
      setError('Failed to load group members');
    }
  };

  const handleSaveMembersToGroup = async () => {
    if (!selectedGroupForMembers) return;
    
    try {
      // Get students to add (those selected but not already in group)
      const currentMemberIds = groupMembers.map(m => m.student_id);
      const studentsToAdd = selectedMemberIds.filter(id => !currentMemberIds.includes(id));
      const studentsToRemove = currentMemberIds.filter(id => !selectedMemberIds.includes(id));
      
      // Add new members
      if (studentsToAdd.length > 0) {
        await axios.post(`${config.BACKEND_URL}/api/groups/${selectedGroupForMembers.id}/members`, studentsToAdd);
      }
      
      // Remove members
      for (const studentId of studentsToRemove) {
        await axios.delete(`${config.BACKEND_URL}/api/groups/${selectedGroupForMembers.id}/members/${studentId}`);
      }
      
      await fetchGroups();
      setShowAddMembersModal(false);
      setSelectedGroupForMembers(null);
      setSelectedMemberIds([]);
      setError(null);
    } catch (error: any) {
      console.error('❌ Error saving group members:', error);
      setError(error.response?.data?.detail || 'Failed to save group members');
    }
  };


  const handleCourseFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare form data, converting empty strings to null for optional fields
      const submitData: any = { ...courseFormData };
      
      // Convert empty programId to null to allow removing program association
      if (submitData.programId === '' || submitData.programId === null || submitData.programId === undefined) {
        submitData.programId = null;
      }
      
      // Convert empty ageGroup to null
      if (submitData.ageGroup === '') {
        submitData.ageGroup = null;
      }
      
      // Convert empty prerequisites to null
      if (submitData.prerequisites === '') {
        submitData.prerequisites = null;
      }
      
      if (selectedCourse) {
        // Update existing course
        console.log('📝 Updating course:', selectedCourse.id, submitData);
        const response = await axios.put(`${config.BACKEND_URL}/api/courses/${selectedCourse.id}`, submitData);
        console.log('✅ Course updated:', response.data);
        setCourses(courses.map(c => c.id === selectedCourse.id ? { ...c, ...response.data } : c));
      } else {
        // Create new course
        console.log('📝 Creating new course:', submitData);
        const response = await axios.post(`${config.BACKEND_URL}/api/courses/`, submitData);
        console.log('✅ Course created:', response.data);
        setCourses([...courses, response.data]);
      }
      setShowCourseForm(false);
      setSelectedCourse(null);
      setError(null);
    } catch (error: any) {
      console.error('❌ Error saving course:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save course';
      setError(`Failed to save course: ${errorMessage}`);
    }
  };

  const handleCourseFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCourseFormData(prev => ({
      ...prev,
      [name]: name === 'durationMinutes' || name === 'sessionsRequired' ? parseInt(value) : value
    }));
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome back, {user?.email}</p>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users
        </button>
        <button 
          className={`nav-tab ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('students');
          }}
        >
          🎓 Students
        </button>
        <button 
          className={`nav-tab ${activeTab === 'programs' ? 'active' : ''}`}
          onClick={() => setActiveTab('programs')}
        >
          📚 Programs
        </button>
        <button 
          className={`nav-tab ${activeTab === 'courses' ? 'active' : ''}`}
          onClick={() => setActiveTab('courses')}
        >
          🎓 Courses
        </button>
        <button 
          className={`nav-tab ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          📅 Sessions
        </button>
        <button 
          className={`nav-tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          👥 Groups
        </button>
        <button 
          className={`nav-tab ${activeTab === 'consent-forms' ? 'active' : ''}`}
          onClick={() => setActiveTab('consent-forms')}
        >
          📝 Consent Forms
        </button>
        <button 
          className={`nav-tab ${activeTab === 'camps' ? 'active' : ''}`}
          onClick={() => setActiveTab('camps')}
        >
          🏕️ Camps
        </button>
        <button 
          className={`nav-tab ${activeTab === 'competitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('competitions')}
        >
          🏆 Competitions
        </button>
        <button 
          className={`nav-tab ${activeTab === 'orgs' ? 'active' : ''}`}
          onClick={() => setActiveTab('orgs')}
        >
          🏢 Orgs
        </button>
        <button 
          className={`nav-tab ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('attendance');
            fetchAttendanceRecords();
          }}
        >
          ✅ Attendance
        </button>
        <button 
          className={`nav-tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          💳 Billing
        </button>
        <button 
          className={`nav-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
        <button 
          className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="dashboard-content">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{stats?.totalUsers || 0}</h3>
                <p>Total Users</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎓</div>
              <div className="stat-content">
                <h3>{stats?.totalStudents || 0}</h3>
                <p>Students</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👨‍🏫</div>
              <div className="stat-content">
                <h3>{stats?.totalTeachers || 0}</h3>
                <p>Teachers</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👨‍👩‍👧‍👦</div>
              <div className="stat-content">
                <h3>{stats?.totalParents || 0}</h3>
                <p>Parents</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{stats?.activeUsers || 0}</h3>
                <p>Active Users</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🆕</div>
              <div className="stat-content">
                <h3>{stats?.recentRegistrations || 0}</h3>
                <p>New This Week</p>
              </div>
            </div>
          </div>

          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <div className="action-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  console.log('🔍 Opening user form, resetting form data');
                  setEmailManuallySet(false);
                  setShowUserForm(true);
                }}
              >
                ➕ Add New User
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveTab('users')}
              >
                👥 Manage Users
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveTab('analytics')}
              >
                📊 View Reports
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="dashboard-content">
          <div className="users-header">
            <h3>User Management</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* Group Filter Dropdown */}
              <div ref={userGroupFilterRef} style={{ position: 'relative' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowUserGroupFilter(!showUserGroupFilter)}
                  style={{ fontSize: '14px', padding: '8px 12px' }}
                >
                  🔍 Filter by Group {userGroupFilterIds.length > 0 ? `(${userGroupFilterIds.length})` : ''}
                </button>
                {showUserGroupFilter && (
                  <div 
                    className="group-filter-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '5px',
                      background: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '250px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      padding: '10px'
                    }}
                  >
                    <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
                      Select Groups:
                    </div>
                    {groups.map((group) => (
                      <label
                        key={group.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '5px 0',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={userGroupFilterIds.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserGroupFilterIds([...userGroupFilterIds, group.id]);
                            } else {
                              setUserGroupFilterIds(userGroupFilterIds.filter(id => id !== group.id));
                            }
                          }}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        <span>{group.name}</span>
                      </label>
                    ))}
                    {groups.length === 0 && (
                      <div style={{ padding: '10px', color: '#666', fontSize: '14px' }}>
                        No groups available
                      </div>
                    )}
                    {userGroupFilterIds.length > 0 && (
                      <button
                        onClick={() => {
                          setUserGroupFilterIds([]);
                        }}
                        style={{
                          marginTop: '10px',
                          padding: '5px 10px',
                          fontSize: '12px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  console.log('🔍 Opening user form from users tab, resetting form data');
                  setEmailManuallySet(false);
                  setShowUserForm(true);
                }}
              >
                ➕ Add User
              </button>
            </div>
          </div>

          {/* Search Box */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px'
          }}>
            <input
              type="text"
              placeholder="🔍 Search users by name or email..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
                transition: 'border-color 0.3s',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4caf50';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
            />
            {userSearchTerm && (
              <div style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#666',
                fontStyle: 'italic'
              }}>
                {(() => {
                  const filtered = users.filter(user => {
                    if (!userSearchTerm.trim()) return true;
                    const searchLower = userSearchTerm.toLowerCase();
                    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                    const email = user.email?.toLowerCase() || '';
                    return fullName.includes(searchLower) || email.includes(searchLower);
                  });
                  return filtered.length;
                })()} of {users.length} users
              </div>
            )}
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Groups</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Filter users based on search term
                  const filteredUsers = users.filter(user => {
                    if (!userSearchTerm.trim()) return true;
                    const searchLower = userSearchTerm.toLowerCase();
                    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                    const email = user.email?.toLowerCase() || '';
                    return fullName.includes(searchLower) || email.includes(searchLower);
                  });
                  
                  if (filteredUsers.length === 0) {
                    return (
                      <tr>
                        <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                          {userSearchTerm.trim() 
                            ? `No users found matching "${userSearchTerm}"` 
                            : 'No users found'}
                        </td>
                      </tr>
                    );
                  }
                  
                  return filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.userType}`}>
                        {user.userType}
                      </span>
                    </td>
                    <td>
                      {user.userType === 'parent' && user.groups && user.groups.length > 0 ? (
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {user.groups.join(', ')}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#999' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <div className="action-buttons" style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-xs btn-info"
                          onClick={() => handleViewUserDetail(user)}
                          style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: '#17a2b8', color: 'white', border: 'none' }}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleEditUser(user)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-xs btn-warning"
                          onClick={() => handleUserStatusUpdate(
                            user.id, 
                            user.status === 'active' ? 'inactive' : 'active'
                          )}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {user.status === 'active' ? '⏸️' : '▶️'}
                        </button>
                        <button 
                          className="btn btn-xs btn-danger"
                          onClick={() => handleDeleteUser(user.id)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && (
        <div className="dashboard-content">
          <div className="students-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3>Student Management</h3>
              <p style={{ color: '#666', marginTop: '5px' }}>Manage all students in the system. You can view, edit, and delete student records.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setShowStudentForm(true);
                setSelectedStudent(null);
                setStudentFormData({
                  firstName: '',
                  lastName: '',
                  dateOfBirth: '',
                  gradeLevel: '',
                  email: '',
                  phone: '',
                  address: '',
                  parentId: '',
                  enrollmentDate: new Date().toISOString().split('T')[0],
                  profileNotes: '',
                  learningGoals: '',
                  slackUsername: ''
                });
                setStudentFormErrors({});
              }}
            >
              ➕ Add Student
            </button>
          </div>

          {/* Search Box */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            position: 'relative',
            zIndex: 1
          }}>
            <input
              type="text"
              placeholder="🔍 Search students by name..."
              value={studentSearchTerm}
              onChange={(e) => setStudentSearchTerm(e.target.value)}
              disabled={false}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
                transition: 'border-color 0.3s',
                outline: 'none',
                position: 'relative',
                zIndex: 1,
                pointerEvents: 'auto',
                cursor: 'text',
                backgroundColor: '#fff'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4caf50';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
            />
            {studentSearchTerm && (
              <div style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#666',
                fontStyle: 'italic'
              }}>
                {(() => {
                  const filtered = students.filter(student => {
                    if (!studentSearchTerm.trim()) return true;
                    const searchLower = studentSearchTerm.toLowerCase();
                    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
                    return fullName.includes(searchLower);
                  });
                  return filtered.length;
                })()} of {students.length} students
              </div>
            )}
          </div>

          {/* Group Filter */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            display: 'flex',
            gap: '15px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <label htmlFor="filter-group-students" style={{ fontWeight: '500', minWidth: '100px' }}>
              Filter by Group:
            </label>
            <select
              id="filter-group-students"
              value={studentGroupFilter}
              onChange={(e) => {
                setStudentGroupFilter(e.target.value);
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '250px',
                backgroundColor: '#fff'
              }}
            >
              <option value="">All Students</option>
              {groups.filter(g => g.status === 'active').map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            {studentGroupFilter && (
              <button
                onClick={() => setStudentGroupFilter('')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear Filter
              </button>
            )}
            {studentGroupFilter && (
              <span style={{ color: '#666', fontSize: '14px' }}>
                Showing {students.length} student{students.length !== 1 ? 's' : ''} in selected group
              </span>
            )}
          </div>

          <div className="students-table-container">
            <table className="students-table">
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'name') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('name');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Name {studentSortField === 'name' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'email') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('email');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Email {studentSortField === 'email' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'age') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('age');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Age {studentSortField === 'age' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'grade') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('grade');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Grade {studentSortField === 'grade' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'tier') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('tier');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Tier {studentSortField === 'tier' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'status') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('status');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Status {studentSortField === 'status' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'parent') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('parent');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Parent {studentSortField === 'parent' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => {
                      if (studentSortField === 'enrolled') {
                        setStudentSortDirection(studentSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setStudentSortField('enrolled');
                        setStudentSortDirection('asc');
                      }
                    }}
                  >
                    Enrolled {studentSortField === 'enrolled' && (studentSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Filter students based on search term
                  let filteredStudents = students.filter(student => {
                    if (!studentSearchTerm.trim()) return true;
                    const searchLower = studentSearchTerm.toLowerCase();
                    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
                    return fullName.includes(searchLower);
                  });
                  
                  // Sort students
                  if (studentSortField) {
                    filteredStudents = [...filteredStudents].sort((a, b) => {
                      let aVal: any;
                      let bVal: any;
                      
                      switch (studentSortField) {
                        case 'name':
                          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
                          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
                          break;
                        case 'email':
                          aVal = (a.parent_email || '').toLowerCase();
                          bVal = (b.parent_email || '').toLowerCase();
                          break;
                        case 'age':
                          aVal = a.age || 0;
                          bVal = b.age || 0;
                          break;
                        case 'grade':
                          aVal = a.grade_level || '';
                          bVal = b.grade_level || '';
                          break;
                        case 'tier':
                          aVal = a.tier || '';
                          bVal = b.tier || '';
                          break;
                        case 'status':
                          aVal = a.status || '';
                          bVal = b.status || '';
                          break;
                        case 'parent':
                          aVal = (a.parent_name || '').toLowerCase();
                          bVal = (b.parent_name || '').toLowerCase();
                          break;
                        case 'enrolled':
                          aVal = new Date(a.enrollment_date).getTime();
                          bVal = new Date(b.enrollment_date).getTime();
                          break;
                        default:
                          return 0;
                      }
                      
                      if (aVal < bVal) return studentSortDirection === 'asc' ? -1 : 1;
                      if (aVal > bVal) return studentSortDirection === 'asc' ? 1 : -1;
                      return 0;
                    });
                  }
                  
                  if (filteredStudents.length === 0) {
                    return (
                      <tr>
                        <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                          {studentSearchTerm.trim() 
                            ? `No students found matching "${studentSearchTerm}"` 
                            : 'No students found'}
                        </td>
                      </tr>
                    );
                  }
                  
                  return filteredStudents.map((student) => (
                  <tr key={student.id}>
                    <td>{student.first_name} {student.last_name}</td>
                    <td>{student.parent_email || '—'}</td>
                    <td>{student.age}</td>
                    <td>{student.grade_level}</td>
                    <td>
                      <span className={`tier-badge ${student.tier}`}>
                        {student.tier}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${student.status}`}>
                        {student.status}
                      </span>
                    </td>
                    <td>{student.parent_name || 'Unknown'}</td>
                    <td>{new Date(student.enrollment_date).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons" style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-xs btn-info"
                          onClick={() => handleViewStudentDetail(student)}
                          style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: '#17a2b8', color: 'white', border: 'none' }}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="btn btn-xs btn-secondary"
                          onClick={() => {
                            // TODO: Add edit student functionality
                            alert('Edit student functionality coming soon');
                          }}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-xs btn-danger"
                          onClick={() => handleDeleteStudent(student.id)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                  ));
                })()}
              </tbody>
            </table>
            
            {students.length === 0 && (
              <div className="no-data">
                <p>No students found. Students will appear here when parents enroll their children.</p>
              </div>
            )}
          </div>

          {/* Student Detail View Modal */}
          {showStudentDetailView && viewingStudent && (
            <StudentDetailView
              student={{
                id: viewingStudent.id,
                firstName: viewingStudent.first_name,
                lastName: viewingStudent.last_name,
                dateOfBirth: viewingStudent.date_of_birth || '',
                tier: viewingStudent.tier || 'foundations',
                parentId: viewingStudent.parent_id || '',
                parentEmail: viewingStudent.parent_email || ''
              }}
              onClose={() => {
                setShowStudentDetailView(false);
                setViewingStudent(null);
              }}
              onUpdate={() => {
                fetchStudents();
              }}
            />
          )}
        </div>
      )}

      {/* Programs Tab */}
      {activeTab === 'programs' && (
        <div className="dashboard-content">
          <div className="programs-header">
            <h3>Program Management</h3>
            <button 
              className="btn btn-primary"
              onClick={handleAddProgram}
            >
              ➕ Add Program
            </button>
          </div>

          <div className="programs-table-container">
            <table className="programs-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Tier</th>
                  <th>Duration</th>
                  <th>Sessions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((program) => (
                  <tr key={program.id} data-tier={program.tier}>
                    <td>
                      <div className="program-name">
                        <div 
                          className="program-color" 
                          style={{ backgroundColor: program.color }}
                        ></div>
                        {program.name}
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${program.programType}`}>
                        {program.programType.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className={`tier-badge ${program.tier}`}>
                        {program.tier}
                      </span>
                    </td>
                    <td>{program.durationMinutes != null ? `${program.durationMinutes} min` : 'N/A'}</td>
                    <td>{program.sessionsRequired != null ? program.sessionsRequired : 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${program.status}`}>
                        {program.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-xs btn-info"
                          onClick={() => handleViewProgram(program)}
                          title="View Program Details"
                        >
                          👁️ View
                        </button>
                        <button 
                          className="btn btn-xs btn-secondary"
                          onClick={() => handleEditProgram(program)}
                          title="Edit Program"
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          className="btn btn-xs btn-danger"
                          onClick={() => handleDeleteProgram(program.id)}
                          title="Delete Program"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <div className="dashboard-content">
          <div className="courses-header">
            <h3>Course Management</h3>
            <button 
              className="btn btn-primary"
              onClick={handleAddCourse}
            >
              ➕ Add Course
            </button>
          </div>

          <div className="courses-table-container">
            <table className="courses-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tier</th>
                  <th>Age Group</th>
                  <th>Program</th>
                  <th>Duration</th>
                  <th>Sessions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.id}>
                    <td>
                      <div className="course-name">
                        {course.name}
                      </div>
                    </td>
                    <td>
                      <span className={`tier-badge ${course.tier}`}>
                        {course.tier}
                      </span>
                    </td>
                    <td>{course.ageGroup || 'N/A'}</td>
                    <td>
                      {course.programId ? 
                        (() => {
                          const program = programs.find(p => p.id === course.programId);
                          return program ? `${program.name} (${program.tier})` : 'Unknown Program';
                        })() : 
                        'No Program'
                      }
                    </td>
                    <td>{course.durationMinutes} min</td>
                    <td>{course.sessionsRequired}</td>
                    <td>
                      <span className={`status-badge ${course.status}`}>
                        {course.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditCourse(course)}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteCourse(course.id)}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="dashboard-content">
          <div className="sessions-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3>Session Management</h3>
              <p style={{ color: '#666', marginTop: '5px' }}>Create and manage class sessions. Time is required, all other fields are optional.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleAddSession}
            >
              ➕ Create Session
            </button>
          </div>

          {/* Session Filters */}
          <div className="session-filters" style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '20px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1rem', fontWeight: '600' }}>Filter Sessions</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '0.9rem' }}>From Date</label>
                <input
                  type="date"
                  value={sessionFilters.fromDate}
                  onChange={(e) => setSessionFilters({ ...sessionFilters, fromDate: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '0.9rem' }}>To Date</label>
                <input
                  type="date"
                  value={sessionFilters.toDate}
                  onChange={(e) => setSessionFilters({ ...sessionFilters, toDate: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '0.9rem' }}>Programs</label>
                <select
                  multiple
                  value={sessionFilters.selectedProgramIds}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    // Filter out empty string if "All Programs" is selected
                    const filtered = selected.filter(id => id !== '');
                    setSessionFilters({ ...sessionFilters, selectedProgramIds: filtered });
                  }}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
                  size={5}
                >
                  {programs.map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.name} ({program.tier})
                    </option>
                  ))}
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '0.85rem' }}>
                  Hold Ctrl/Cmd to select multiple programs. {sessionFilters.selectedProgramIds.length === 0 ? 'Showing all programs.' : `Selected: ${sessionFilters.selectedProgramIds.length} program(s)`}
                </small>
              </div>
              <div>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    setSessionFilters({
                      fromDate: today,
                      toDate: today,
                      selectedProgramIds: []
                    });
                  }}
                  style={{ width: '100%' }}
                >
                  Reset to Today
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ 
              padding: '12px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px', 
              marginBottom: '20px',
              color: '#c33'
            }}>
              ⚠️ {error}
              <button 
                onClick={() => setError(null)}
                style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>
          )}

          <div className="sessions-table-container" style={{ 
            backgroundColor: '#fff', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table className="sessions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f5f5f5' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date & Time</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Program</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Course</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Instructor</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Location</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Enrollment</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      <p>No sessions found. Create your first session to get started.</p>
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => (
                    <tr key={session.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '500' }}>
                          {session.date_available ? formatDatePSTShort(session.date_available) : 'N/A'}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                          {session.start_time ? new Date(`2000-01-01T${session.start_time}`).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit', 
                            hour12: true,
                            timeZone: 'America/Los_Angeles'
                          }) : ''} - {session.end_time ? new Date(`2000-01-01T${session.end_time}`).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit', 
                            hour12: true,
                            timeZone: 'America/Los_Angeles'
                          }) : ''} PST
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {session.program_name ? (
                          <div>
                            <div style={{ fontWeight: '500' }}>{session.program_name}</div>
                            {session.program_tier && (
                              <span className={`tier-badge ${session.program_tier}`} style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.75em',
                                marginTop: '4px'
                              }}>
                                {session.program_tier}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No program</span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {session.course_name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {session.instructor_name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {session.location || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ marginBottom: '4px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: session.current_enrollment >= session.max_capacity ? '#fee' : '#e8f5e9',
                            color: session.current_enrollment >= session.max_capacity ? '#c33' : '#2e7d32',
                            fontWeight: '500'
                          }}>
                            {session.current_enrollment}/{session.max_capacity}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                          <span style={{ fontWeight: '500' }}>
                            ({session.attendance_count ?? 0} / {session.scheduled_count ?? session.current_enrollment})
                          </span>
                          <span style={{ fontSize: '0.8em', marginLeft: '4px', color: '#999' }}>
                            attended
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span className={`status-badge ${session.status}`} style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85em',
                          textTransform: 'capitalize'
                        }}>
                          {session.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button 
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewSessionDetail(session)}
                            style={{ padding: '6px 12px', fontSize: '0.85em', backgroundColor: '#17a2b8', color: 'white' }}
                            title="View Session Details"
                          >
                            👁️ View
                          </button>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddStudentsToSession(session)}
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            title="Add Students"
                          >
                            👥 Students
                          </button>
                          <button 
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditSession(session)}
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            title="Edit Session"
                          >
                            ✏️ Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteSession(session.id)}
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            title="Delete Session"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Groups Tab */}
      {activeTab === 'groups' && (
        <div className="dashboard-content">
          <div className="groups-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3>Student Groups Management</h3>
              <p style={{ color: '#666', marginTop: '5px' }}>Create and manage student groups. Organize students into teams, projects, or classes.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleAddGroup}
            >
              ➕ Create Group
            </button>
          </div>

          {error && (
            <div className="error-message" style={{ 
              padding: '12px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px', 
              marginBottom: '20px',
              color: '#c33'
            }}>
              ⚠️ {error}
              <button 
                onClick={() => setError(null)}
                style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>
          )}

          <div className="groups-table-container" style={{ 
            backgroundColor: '#fff', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table className="groups-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f5f5f5' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Group Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Members</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Created By</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      <p>No groups found. Create your first group to get started.</p>
                    </td>
                  </tr>
                ) : (
                  groups.map((group) => (
                    <tr key={group.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>
                        {group.name}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {group.group_type || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {group.description ? (
                          <span style={{ fontSize: '0.9em', color: '#666' }}>
                            {group.description.length > 50 ? `${group.description.substring(0, 50)}...` : group.description}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: '500'
                        }}>
                          {group.member_count}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span className={`status-badge ${group.status}`} style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85em',
                          textTransform: 'capitalize'
                        }}>
                          {group.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                        {group.creator_name || 'System'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button 
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewGroupDetail(group)}
                            style={{ padding: '6px 12px', fontSize: '0.85em', backgroundColor: '#17a2b8', color: 'white', border: 'none' }}
                            title="View Group Details"
                          >
                            👁️ View
                          </button>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddMembersToGroup(group)}
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            title="Manage Members"
                          >
                            👥 Members
                          </button>
                          <button 
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditGroup(group)}
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            title="Edit Group"
                          >
                            ✏️ Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteGroup(group.id)}
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            title="Delete Group"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Consent Forms Tab */}
      {activeTab === 'consent-forms' && (
        <div className="dashboard-content">
          <div className="consent-forms-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3>Consent Forms Management</h3>
              <p style={{ color: '#666', marginTop: '5px' }}>View and manage all consent forms submitted by parents</p>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by parent name or email..."
              value={consentFormsSearch}
              onChange={(e) => setConsentFormsSearch(e.target.value)}
              style={{
                flex: '1',
                minWidth: '250px',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
            
            {/* Consent Options Filter */}
            <div style={{ position: 'relative' }} ref={consentFilterDropdownRef}>
              <button
                onClick={() => setShowConsentFilterDropdown(!showConsentFilterDropdown)}
                style={{
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '200px',
                  justifyContent: 'space-between'
                }}
              >
                <span>
                  {selectedConsentOptions.length === 0 
                    ? 'Filter by Consent Option' 
                    : `${selectedConsentOptions.length} option${selectedConsentOptions.length > 1 ? 's' : ''} selected`}
                </span>
                <span>{showConsentFilterDropdown ? '▲' : '▼'}</span>
              </button>
              
              {showConsentFilterDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  minWidth: '250px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '8px'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ padding: '8px', borderBottom: '1px solid #eee', marginBottom: '8px', fontWeight: 'bold' }}>
                    Select Consent Options:
                  </div>
                  {[
                    { key: 'program_acknowledgment', label: 'Program Acknowledgment' },
                    { key: 'media_consent', label: 'Media Consent' },
                    { key: 'privacy_consent', label: 'Privacy Consent' },
                    { key: 'emergency_medical_authorization', label: 'Medical Authorization' },
                    { key: 'liability_consent', label: 'Liability Consent' },
                    { key: 'code_of_conduct_consent', label: 'Code of Conduct' },
                    { key: 'newsletter_subscription', label: 'Newsletter Subscription' }
                  ].map((option) => (
                    <label
                      key={option.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedConsentOptions.includes(option.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedConsentOptions([...selectedConsentOptions, option.key]);
                          } else {
                            setSelectedConsentOptions(selectedConsentOptions.filter(opt => opt !== option.key));
                          }
                        }}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px' }}>{option.label}</span>
                    </label>
                  ))}
                  {selectedConsentOptions.length > 0 && (
                    <div style={{ padding: '8px', borderTop: '1px solid #eee', marginTop: '8px' }}>
                      <button
                        onClick={() => {
                          setSelectedConsentOptions([]);
                          setConsentFilterValue('');
                        }}
                        style={{
                          width: '100%',
                          padding: '6px',
                          fontSize: '12px',
                          backgroundColor: '#f44336',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Yes/No Filter */}
            {selectedConsentOptions.length > 0 && (
              <select
                value={consentFilterValue}
                onChange={(e) => setConsentFilterValue(e.target.value as 'yes' | 'no' | '')}
                style={{
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  minWidth: '120px'
                }}
              >
                <option value="">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            )}

            {/* Active Filters Indicator */}
            {(selectedConsentOptions.length > 0 || consentFilterValue) && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>Active filters:</span>
                {selectedConsentOptions.length > 0 && (
                  <span style={{ fontWeight: 'bold' }}>
                    {selectedConsentOptions.length} option{selectedConsentOptions.length > 1 ? 's' : ''}
                  </span>
                )}
                {consentFilterValue && (
                  <span style={{ fontWeight: 'bold' }}>
                    = {consentFilterValue.toUpperCase()}
                  </span>
                )}
              </div>
            )}
          </div>

          {consentFormsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading consent forms...</p>
            </div>
          ) : (
            <div className="consent-forms-table-container" style={{ 
              backgroundColor: '#fff', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflow: 'auto'
            }}>
              <table className="consent-forms-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Student</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Parent</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Created</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Signed</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Options</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Filter consent forms by search term and consent options
                    const filteredForms = consentForms.filter(form => {
                      // Search filter
                      if (consentFormsSearch.trim()) {
                        const search = consentFormsSearch.toLowerCase();
                        const matchesSearch = (
                          form.parent_name?.toLowerCase().includes(search) ||
                          form.parent_email?.toLowerCase().includes(search) ||
                          form.student_name?.toLowerCase().includes(search)
                        );
                        if (!matchesSearch) return false;
                      }

                      // Consent options filter
                      if (selectedConsentOptions.length > 0) {
                        // If no yes/no filter is selected, show all forms with selected options
                        if (!consentFilterValue) {
                          // Check if form has any of the selected options (regardless of value)
                          const hasAnySelectedOption = selectedConsentOptions.some(optionKey => {
                            switch (optionKey) {
                              case 'program_acknowledgment':
                                return form.program_acknowledgment !== undefined && form.program_acknowledgment !== null;
                              case 'media_consent':
                                return form.media_consent !== undefined && form.media_consent !== null;
                              case 'privacy_consent':
                                return form.privacy_consent !== undefined && form.privacy_consent !== null;
                              case 'emergency_medical_authorization':
                                return form.emergency_medical_authorization !== undefined && form.emergency_medical_authorization !== null;
                              case 'liability_consent':
                                return form.liability_consent !== undefined && form.liability_consent !== null;
                              case 'code_of_conduct_consent':
                                return form.code_of_conduct_consent !== undefined && form.code_of_conduct_consent !== null;
                              case 'newsletter_subscription':
                                return form.newsletter_subscription !== undefined && form.newsletter_subscription !== null;
                              default:
                                return false;
                            }
                          });
                          if (!hasAnySelectedOption) return false;
                        } else {
                          // Filter by yes/no value
                          const hasMatchingOption = selectedConsentOptions.some(optionKey => {
                            let formValue: boolean | string | null | undefined;
                            
                            switch (optionKey) {
                              case 'program_acknowledgment':
                                formValue = form.program_acknowledgment;
                                break;
                              case 'media_consent':
                                formValue = form.media_consent;
                                break;
                              case 'privacy_consent':
                                formValue = form.privacy_consent;
                                break;
                              case 'emergency_medical_authorization':
                                formValue = form.emergency_medical_authorization;
                                break;
                              case 'liability_consent':
                                formValue = form.liability_consent;
                                break;
                              case 'code_of_conduct_consent':
                                formValue = form.code_of_conduct_consent;
                                break;
                              case 'newsletter_subscription':
                                formValue = form.newsletter_subscription;
                                break;
                              default:
                                return false;
                            }

                            // Handle boolean values
                            if (typeof formValue === 'boolean') {
                              if (consentFilterValue === 'yes') {
                                return formValue === true;
                              } else if (consentFilterValue === 'no') {
                                return formValue === false;
                              }
                            }
                            
                            // Handle string values (like media_consent: 'yes' | 'no' | null)
                            if (typeof formValue === 'string') {
                              if (consentFilterValue === 'yes') {
                                return formValue === 'yes';
                              } else if (consentFilterValue === 'no') {
                                return formValue === 'no';
                              }
                            }
                            
                            // Handle null/undefined as "no"
                            if (consentFilterValue === 'no') {
                              return formValue === null || formValue === false || formValue === undefined;
                            }
                            
                            return false;
                          });
                          
                          if (!hasMatchingOption) return false;
                        }
                      }

                      return true;
                    });

                    if (filteredForms.length === 0) {
                      return (
                        <tr>
                          <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                            {consentForms.length === 0 
                              ? 'No consent forms found.' 
                              : `No consent forms match "${consentFormsSearch}"`}
                          </td>
                        </tr>
                      );
                    }

                    return filteredForms.map((form) => (
                      <tr key={form.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>
                          <strong>{form.student_name}</strong>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {form.parent_name}
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                          {form.parent_email || '—'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span className={`status-badge ${form.status.toLowerCase()}`} style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.85em',
                            textTransform: 'capitalize',
                            backgroundColor: form.status === 'SIGNED' ? '#e8f5e9' : form.status === 'PENDING' ? '#fff3cd' : '#f5f5f5',
                            color: form.status === 'SIGNED' ? '#2e7d32' : form.status === 'PENDING' ? '#856404' : '#666'
                          }}>
                            {form.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                          {new Date(form.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9em', color: '#666' }}>
                          {form.signed_at ? new Date(form.signed_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85em' }}>
                            <div><strong>Program Acknowledgment:</strong> {form.program_acknowledgment ? '✅ Yes' : '❌ No'}</div>
                            <div><strong>Media Consent:</strong> {form.media_consent === 'yes' ? '✅ Yes' : form.media_consent === 'no' ? '❌ No' : '—'}</div>
                            <div><strong>Privacy Consent:</strong> {form.privacy_consent ? '✅ Yes' : '❌ No'}</div>
                            <div><strong>Medical Authorization:</strong> {form.emergency_medical_authorization ? '✅ Yes' : '❌ No'}</div>
                            <div><strong>Liability Consent:</strong> {form.liability_consent ? '✅ Yes' : '❌ No'}</div>
                            <div><strong>Code of Conduct:</strong> {form.code_of_conduct_consent ? '✅ Yes' : '❌ No'}</div>
                            <div><strong>Newsletter:</strong> {form.newsletter_subscription ? '✅ Yes' : '❌ No'}</div>
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {form.s3_url ? (
                            <button
                              onClick={async () => {
                                try {
                                  const response = await axios.get(
                                    `${config.BACKEND_URL}/api/consent-forms/${form.id}/pdf-url`,
                                    {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                      }
                                    }
                                  );
                                  if (response.data.success && response.data.pdf_url) {
                                    window.open(response.data.pdf_url, '_blank', 'noopener,noreferrer');
                                  } else {
                                    alert('Failed to load PDF. Please try again.');
                                  }
                                } catch (error: any) {
                                  console.error('Error fetching PDF URL:', error);
                                  alert('Failed to load PDF. Please try again.');
                                }
                              }}
                              className="btn btn-sm btn-primary"
                              style={{ padding: '6px 12px', fontSize: '0.85em', cursor: 'pointer' }}
                            >
                              📄 View PDF
                            </button>
                          ) : (
                            <span style={{ color: '#999', fontSize: '0.85em' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Camps Tab */}
      {activeTab === 'camps' && (
        <CampManagement />
      )}

      {/* Competitions Tab */}
      {activeTab === 'competitions' && (
        <CompetitionManagement />
      )}

      {/* Organizations Tab */}
      {activeTab === 'orgs' && (
        <div className="dashboard-content">
          <OrganizationManagement />
        </div>
      )}

      {/* Attendance Dashboard Tab */}
      {activeTab === 'attendance' && (
        <div className="dashboard-content">
          <div className="attendance-header" style={{ marginBottom: '20px' }}>
            <h3>Attendance Dashboard</h3>
            <p style={{ color: '#666', marginTop: '5px' }}>View and filter attendance records by student, date, program, and status</p>
          </div>

          {/* Filters */}
          <div className="filters-container" style={{ 
            backgroundColor: '#f9f9f9', 
            padding: '20px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            <div className="filter-group">
              <label htmlFor="filter-student" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Student</label>
              <select
                id="filter-student"
                value={attendanceFilters.studentId}
                onChange={(e) => handleAttendanceFilterChange('studentId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Students</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.first_name} {student.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-program" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Program</label>
              <select
                id="filter-program"
                value={attendanceFilters.programId}
                onChange={(e) => handleAttendanceFilterChange('programId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Programs</option>
                {programs.map(program => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-start-date" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Start Date</label>
              <input
                type="date"
                id="filter-start-date"
                value={attendanceFilters.startDate}
                onChange={(e) => handleAttendanceFilterChange('startDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="filter-end-date" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>End Date</label>
              <input
                type="date"
                id="filter-end-date"
                value={attendanceFilters.endDate}
                onChange={(e) => handleAttendanceFilterChange('endDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="filter-status" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Status</label>
              <select
                id="filter-status"
                value={attendanceFilters.status}
                onChange={(e) => handleAttendanceFilterChange('status', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Statuses</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="late">Late</option>
                <option value="excused">Excused</option>
                <option value="trial">Trial</option>
              </select>
            </div>

            <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
              <button
                onClick={fetchAttendanceRecords}
                disabled={attendanceLoading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c63ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: attendanceLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: attendanceLoading ? 0.6 : 1
                }}
              >
                {attendanceLoading ? 'Loading...' : '🔍 Apply Filters'}
              </button>
              <button
                onClick={() => {
                  setAttendanceFilters({
                    studentId: '',
                    programId: '',
                    startDate: '',
                    endDate: '',
                    status: ''
                  });
                  setTimeout(() => fetchAttendanceRecords(), 100);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                🔄 Clear
              </button>
            </div>
          </div>

          {/* Attendance Records Table */}
          <div className="attendance-table-container">
            {attendanceLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="loading-spinner"></div>
                <p>Loading attendance records...</p>
              </div>
            ) : attendanceRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <p>No attendance records found.</p>
                <p style={{ marginTop: '10px' }}>Try adjusting your filters or check back later.</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '15px', color: '#666' }}>
                  Showing <strong>{attendanceRecords.length}</strong> attendance record{attendanceRecords.length !== 1 ? 's' : ''}
                </div>
                <table className="students-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Student</th>
                      <th>Grade</th>
                      <th>Tier</th>
                      <th>Program</th>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Duration</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRecords.map((record) => (
                      <tr key={record.id}>
                        <td>{record.attendance_date ? formatDatePSTShort(record.attendance_date) : '-'}</td>
                        <td>{record.student_name || '-'}</td>
                        <td>{record.student_grade || '-'}</td>
                        <td>
                          <span className={`tier-badge ${record.student_tier}`} style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75em',
                            textTransform: 'capitalize'
                          }}>
                            {record.student_tier || '-'}
                          </span>
                        </td>
                        <td>{record.program_name || '-'}</td>
                        <td>
                          {record.attendance_time 
                            ? new Date(`2000-01-01T${record.attendance_time}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                            : '-'}
                        </td>
                        <td>
                          <span className={`status-badge ${record.status}`} style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.85em',
                            textTransform: 'capitalize',
                            fontWeight: '500',
                            backgroundColor: record.status === 'present' ? '#d4edda' : 
                                           record.status === 'absent' ? '#f8d7da' :
                                           record.status === 'late' ? '#fff3cd' : '#e2e3e5',
                            color: record.status === 'present' ? '#155724' :
                                   record.status === 'absent' ? '#721c24' :
                                   record.status === 'late' ? '#856404' : '#383d41'
                          }}>
                            {record.status || '-'}
                          </span>
                        </td>
                        <td>{record.duration_minutes ? `${record.duration_minutes} min` : '-'}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {record.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Session Detail Modal */}
      {showSessionDetailModal && selectedSessionDetail && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal session-detail-modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, borderBottom: '2px solid #eee' }}>
              <h3>Session Details</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowSessionDetailModal(false);
                  setSelectedSessionDetail(null);
                  setSessionDetailStudents([]);
                  setEditingAttendance({});
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {sessionDetailLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading session details...</p>
                </div>
              ) : (
                <>
                  {/* Session Metadata */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#2c3e50' }}>Session Information</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Date:</strong>
                        <span>{selectedSessionDetail.date_available ? formatDatePSTLong(selectedSessionDetail.date_available) : 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Time:</strong>
                        <span>
                          {selectedSessionDetail.start_time ? new Date(`2000-01-01T${selectedSessionDetail.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A'}
                          {' - '}
                          {selectedSessionDetail.end_time ? new Date(`2000-01-01T${selectedSessionDetail.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Program:</strong>
                        <span>{selectedSessionDetail.program_name || 'N/A'}</span>
                        {selectedSessionDetail.program_tier && (
                          <span className={`tier-badge ${selectedSessionDetail.program_tier}`} style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75em' }}>
                            {selectedSessionDetail.program_tier}
                          </span>
                        )}
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Course:</strong>
                        <span>{selectedSessionDetail.course_name || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Instructor:</strong>
                        <span>{selectedSessionDetail.instructor_name || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Location:</strong>
                        <span>{selectedSessionDetail.location || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Enrollment:</strong>
                        <span>{selectedSessionDetail.current_enrollment || 0}/{selectedSessionDetail.max_capacity || 20}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Status:</strong>
                        <span className={`status-badge ${selectedSessionDetail.status}`} style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '0.85em', textTransform: 'capitalize' }}>
                          {selectedSessionDetail.status}
                        </span>
                      </div>
                    </div>
                    {selectedSessionDetail.description && (
                      <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Description:</strong>
                        <p style={{ margin: 0, color: '#333' }}>{selectedSessionDetail.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Enrolled Students Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0, color: '#2c3e50' }}>Enrolled Students ({sessionDetailStudents.length})</h4>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setShowSessionDetailModal(false);
                          handleAddStudentsToSession(selectedSessionDetail);
                        }}
                        style={{ padding: '8px 16px' }}
                      >
                        ➕ Add Students
                      </button>
                    </div>

                    {sessionDetailStudents.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#999', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <p>No students enrolled in this session.</p>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setShowSessionDetailModal(false);
                            handleAddStudentsToSession(selectedSessionDetail);
                          }}
                          style={{ marginTop: '10px' }}
                        >
                          Add Students
                        </button>
                      </div>
                    ) : (
                      <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ backgroundColor: '#f5f5f5' }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Student</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Tier</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Attendance</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Notes</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionDetailStudents.map((student: any) => {
                              const studentKey = student.student_id || student.id;
                              const currentStatus = editingAttendance[studentKey]?.status || student.attendance_status || 'not_marked';
                              const currentNotes = editingAttendance[studentKey]?.notes !== undefined ? editingAttendance[studentKey].notes : (student.notes || '');
                              
                              return (
                                <tr key={studentKey} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '12px' }}>
                                    <div style={{ fontWeight: '500' }}>{student.full_name}</div>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <span className={`tier-badge ${student.tier}`} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75em', textTransform: 'capitalize' }}>
                                      {student.tier}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    <select
                                      value={currentStatus}
                                      onChange={(e) => {
                                        setEditingAttendance(prev => ({
                                          ...prev,
                                          [studentKey]: {
                                            ...prev[studentKey],
                                            status: e.target.value
                                          }
                                        }));
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.9em',
                                        minWidth: '120px'
                                      }}
                                    >
                                      <option value="not_marked">Not Marked</option>
                                      <option value="present">Present</option>
                                      <option value="absent">Absent</option>
                                      <option value="late">Late</option>
                                      <option value="excused">Excused</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <input
                                      type="text"
                                      value={currentNotes}
                                      onChange={(e) => {
                                        setEditingAttendance(prev => ({
                                          ...prev,
                                          [studentKey]: {
                                            ...prev[studentKey],
                                            notes: e.target.value
                                          }
                                        }));
                                      }}
                                      placeholder="Add notes..."
                                      style={{
                                        width: '100%',
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.9em'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleMarkAttendance(studentKey, currentStatus, currentNotes)}
                                      style={{ padding: '6px 12px', fontSize: '0.85em' }}
                                    >
                                      💾 Save
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ padding: '15px', borderTop: '2px solid #eee', backgroundColor: '#f8f9fa' }}>
                          <button
                            className="btn btn-primary"
                            onClick={handleBulkMarkAttendance}
                            style={{ width: '100%', padding: '10px' }}
                          >
                            💾 Save All Attendance Changes
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSessionDetailModal(false);
                  setSelectedSessionDetail(null);
                  setSessionDetailStudents([]);
                  setEditingAttendance({});
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="dashboard-content">
          <h3>System Analytics</h3>
          <div className="analytics-grid">
            <div className="analytics-card">
              <h4>User Growth</h4>
              <p>Track user registration trends over time</p>
              <div className="chart-placeholder">
                📈 Chart would go here
              </div>
            </div>
            <div className="analytics-card">
              <h4>Activity Overview</h4>
              <p>Monitor system usage and engagement</p>
              <div className="chart-placeholder">
                📊 Activity metrics
              </div>
            </div>
            <div className="analytics-card">
              <h4>Role Distribution</h4>
              <p>Breakdown of users by role</p>
              <div className="chart-placeholder">
                🥧 Pie chart here
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="dashboard-content">
          <h3>System Settings</h3>
          <div className="settings-grid">
            <div className="setting-card">
              <h4>User Registration</h4>
              <p>Configure user registration settings</p>
              <button className="btn btn-secondary">Configure</button>
            </div>
            <div className="setting-card">
              <h4>Email Notifications</h4>
              <p>Manage email notification preferences</p>
              <button className="btn btn-secondary">Configure</button>
            </div>
            <div className="setting-card">
              <h4>Security Settings</h4>
              <p>Configure security and access controls</p>
              <button className="btn btn-secondary">Configure</button>
            </div>
            <div className="setting-card">
              <h4>System Maintenance</h4>
              <p>Database and system maintenance tools</p>
              <button className="btn btn-secondary">Configure</button>
            </div>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {(showUserForm || isEditingUser) && (
        <div className="modal-overlay">
          <div className="modal user-form-modal">
            <div className="modal-header">
              <h3>{isEditingUser ? 'Edit User' : 'Add New User'}</h3>
              <button 
                className="close-btn"
                onClick={resetUserForm}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <form onSubmit={(e) => { 
                e.preventDefault(); 
                if (isEditingUser) {
                  handleUpdateUser();
                } else {
                  handleCreateUser();
                }
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={userFormData.firstName}
                      onChange={(e) => handleUserFormChange('firstName', e.target.value)}
                      className={userFormErrors.firstName ? 'error' : ''}
                      placeholder="Enter first name"
                    />
                    {userFormErrors.firstName && <span className="error-text">{userFormErrors.firstName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={userFormData.lastName}
                      onChange={(e) => handleUserFormChange('lastName', e.target.value)}
                      className={userFormErrors.lastName ? 'error' : ''}
                      placeholder="Enter last name"
                    />
                    {userFormErrors.lastName && <span className="error-text">{userFormErrors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="newUserEmail"
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      value={userFormData.email}
                      readOnly={isEditingUser}
                      onChange={(e) => {
                        if (isEditingUser) return; // Prevent changes when editing
                        const newEmail = e.target.value;
                        console.log('🔍 Email field changed:', newEmail, 'current state:', userFormData.email, 'manually set:', emailManuallySet);
                        
                        // If user has manually set email and autofill is trying to overwrite it, ignore autofill
                        if (emailManuallySet && newEmail !== userFormData.email && 
                            (newEmail === 'admin1@example.com' || newEmail === 'admin2@example.com')) {
                          console.log('⚠️ Blocking autofill attempt to overwrite user email:', newEmail);
                          e.target.value = userFormData.email;
                          return;
                        }
                        
                        // Track that user manually entered email
                        if (newEmail.length > 0 && !emailManuallySet) {
                          setEmailManuallySet(true);
                        }
                        
                        handleUserFormChange('email', newEmail);
                      }}
                      onBlur={(e) => {
                        if (isEditingUser) return;
                        // Ensure the value is still correct on blur (after autofill might have changed it)
                        if (emailManuallySet && e.target.value !== userFormData.email && userFormData.email) {
                          console.log('🔍 Correcting email field after blur - restoring user input:', userFormData.email);
                          e.target.value = userFormData.email;
                          // Force React to re-render with correct value
                          handleUserFormChange('email', userFormData.email);
                        }
                      }}
                      className={userFormErrors.email ? 'error' : ''}
                      placeholder="Enter email address"
                      style={isEditingUser ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                    />
                    {isEditingUser && <small className="form-hint">Email cannot be changed</small>}
                    {userFormErrors.email && <span className="error-text">{userFormErrors.email}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={userFormData.phone}
                      onChange={(e) => {
                        let phoneValue = e.target.value.replace(/\D/g, ''); // Remove all non-digits
                        if (phoneValue.length > 10) phoneValue = phoneValue.slice(0, 10); // Limit to 10 digits
                        handleUserFormChange('phone', phoneValue);
                      }}
                      className={userFormErrors.phone ? 'error' : ''}
                      placeholder="2501234567"
                      maxLength={10}
                    />
                    <small>10-digit Canadian phone number (e.g., 2501234567)</small>
                    {userFormErrors.phone && <span className="error-text">{userFormErrors.phone}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="userType">User Role *</label>
                  <select
                    id="userType"
                    value={userFormData.userType}
                    onChange={(e) => handleUserFormChange('userType', e.target.value)}
                  >
                    <option value="parent">Parent</option>
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {!isEditingUser && (
                  <>
                    <div className="form-group">
                      <label>Password Management *</label>
                      <div className="password-options">
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="passwordOption"
                            value="generate"
                            checked={userFormData.passwordOption === 'generate'}
                            onChange={(e) => handleUserFormChange('passwordOption', e.target.value)}
                          />
                          <span>Generate secure password (recommended)</span>
                        </label>
                        
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="passwordOption"
                            value="temporary"
                            checked={userFormData.passwordOption === 'temporary'}
                            onChange={(e) => handleUserFormChange('passwordOption', e.target.value)}
                          />
                          <span>Set temporary password</span>
                        </label>
                        
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="passwordOption"
                            value="custom"
                            checked={userFormData.passwordOption === 'custom'}
                            onChange={(e) => handleUserFormChange('passwordOption', e.target.value)}
                          />
                          <span>Set custom password</span>
                        </label>
                      </div>
                    </div>

                    {userFormData.passwordOption === 'temporary' && (
                      <div className="form-group">
                        <label htmlFor="temporaryPassword">Temporary Password</label>
                        <div className="password-input-group">
                          <input
                            type="text"
                            id="temporaryPassword"
                            value={userFormData.temporaryPassword}
                            onChange={(e) => handleUserFormChange('temporaryPassword', e.target.value)}
                            placeholder="Enter temporary password or leave blank to auto-generate"
                          />
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleUserFormChange('temporaryPassword', generateTemporaryPassword())}
                          >
                            Generate
                          </button>
                        </div>
                      </div>
                    )}

                    {userFormData.passwordOption === 'custom' && (
                      <div className="form-group">
                        <label htmlFor="password">Custom Password</label>
                        <input
                          type="password"
                          id="password"
                          value={userFormData.password}
                          onChange={(e) => handleUserFormChange('password', e.target.value)}
                          className={userFormErrors.password ? 'error' : ''}
                          placeholder="Enter custom password (min 8 characters)"
                        />
                        {userFormErrors.password && <span className="error-text">{userFormErrors.password}</span>}
                      </div>
                    )}

                    <div className="form-group">
                      <label className="checkbox-option">
                        <input
                          type="checkbox"
                          checked={userFormData.forcePasswordChange}
                          onChange={(e) => handleUserFormChange('forcePasswordChange', e.target.checked)}
                        />
                        <span>Force password change on first login</span>
                      </label>
                    </div>
                  </>
                )}

                {isEditingUser && (
                  <div className="form-group">
                    <div style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '5px', marginTop: '10px' }}>
                      <h4 style={{ marginTop: 0, marginBottom: '10px' }}>Password Reset</h4>
                      {!showPasswordReset ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowPasswordReset(true)}
                        >
                          🔑 Reset Password
                        </button>
                      ) : (
                        <>
                          <div className="password-options" style={{ marginBottom: '15px' }}>
                            <label className="radio-option">
                              <input
                                type="radio"
                                name="passwordOption"
                                value="generate"
                                checked={userFormData.passwordOption === 'generate'}
                                onChange={(e) => handleUserFormChange('passwordOption', e.target.value)}
                              />
                              <span>Generate secure password</span>
                            </label>
                            
                            <label className="radio-option">
                              <input
                                type="radio"
                                name="passwordOption"
                                value="temporary"
                                checked={userFormData.passwordOption === 'temporary'}
                                onChange={(e) => handleUserFormChange('passwordOption', e.target.value)}
                              />
                              <span>Set temporary password (user must change on login)</span>
                            </label>
                            
                            <label className="radio-option">
                              <input
                                type="radio"
                                name="passwordOption"
                                value="custom"
                                checked={userFormData.passwordOption === 'custom'}
                                onChange={(e) => handleUserFormChange('passwordOption', e.target.value)}
                              />
                              <span>Set custom password</span>
                            </label>
                          </div>

                          {userFormData.passwordOption === 'custom' && (
                            <div className="form-group" style={{ marginBottom: '15px' }}>
                              <label htmlFor="editPassword">New Password</label>
                              <input
                                type="password"
                                id="editPassword"
                                value={userFormData.password}
                                onChange={(e) => handleUserFormChange('password', e.target.value)}
                                placeholder="Enter new password (min 8 characters)"
                              />
                            </div>
                          )}

                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label className="checkbox-option">
                              <input
                                type="checkbox"
                                checked={userFormData.forcePasswordChange}
                                onChange={(e) => handleUserFormChange('forcePasswordChange', e.target.checked)}
                              />
                              <span>Force password change on next login</span>
                            </label>
                          </div>

                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleResetPassword}
                              disabled={isCreatingUser}
                            >
                              {isCreatingUser ? 'Resetting...' : 'Reset Password'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowPasswordReset(false);
                                setUserFormData(prev => ({
                                  ...prev,
                                  password: '',
                                  passwordOption: 'generate',
                                  forcePasswordChange: false
                                }));
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetUserForm}
                    disabled={isCreatingUser}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isCreatingUser}
                  >
                    {isCreatingUser 
                      ? (isEditingUser ? 'Updating User...' : 'Creating User...') 
                      : (isEditingUser ? 'Update User' : 'Create User')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Program Form Modal */}
      {showProgramForm && (
        <div className="modal-overlay">
          <div className="modal program-form-modal">
            <div className="modal-header">
              <h3>{selectedProgram ? 'Edit Program' : 'Add New Program'}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowProgramForm(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <form onSubmit={handleProgramFormSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Program Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={programFormData.name}
                    onChange={handleProgramFormChange}
                    required
                    placeholder="Enter program name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="programType">Program Type</label>
                  <select
                    id="programType"
                    name="programType"
                    value={programFormData.programType}
                    onChange={handleProgramFormChange}
                    required
                  >
                    <option value="after_school">After School</option>
                    <option value="homeschool">Home School</option>
                    <option value="vacation_camp">Vacation Camp</option>
                    <option value="competition">Competition</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={programFormData.description}
                    onChange={handleProgramFormChange}
                    rows={3}
                    placeholder="Enter program description"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="scheduleNote">Schedule Note (Optional)</label>
                  <textarea
                    id="scheduleNote"
                    name="scheduleNote"
                    value={programFormData.scheduleNote ?? ''}
                    onChange={handleProgramFormChange}
                    rows={2}
                    placeholder="e.g., Chess club runs every Monday 4 PM to 5 PM during regular school days"
                  />
                  <small style={{color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem'}}>
                    This note will be displayed to parents when they select this program during enrollment
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tier">Tier</label>
                    <select
                      id="tier"
                      name="tier"
                      value={programFormData.tier}
                      onChange={handleProgramFormChange}
                      required
                    >
                      <option value="all">All</option>
                      <option value="foundations">Foundations</option>
                      <option value="innovators">Innovators</option>
                      <option value="pioneers">Pioneers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="durationMinutes">Duration (minutes) (Optional)</label>
                    <input
                      type="number"
                      id="durationMinutes"
                      name="durationMinutes"
                      value={programFormData.durationMinutes ?? ''}
                      onChange={handleProgramFormChange}
                      min="30"
                      max="300"
                      placeholder="Leave empty if not applicable"
                    />
                    <small style={{color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem'}}>
                      Note: Duration is typically defined at the course level
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sessionsRequired">Sessions Required (Optional)</label>
                    <input
                      type="number"
                      id="sessionsRequired"
                      name="sessionsRequired"
                      value={programFormData.sessionsRequired ?? ''}
                      onChange={handleProgramFormChange}
                      min="1"
                      max="50"
                      placeholder="Leave empty if not applicable"
                    />
                    <small style={{color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem'}}>
                      Note: Number of sessions is typically defined at the course level
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="color">Color</label>
                    <input
                      type="color"
                      id="color"
                      name="color"
                      value={programFormData.color}
                      onChange={handleProgramFormChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={programFormData.status}
                    onChange={handleProgramFormChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowProgramForm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleProgramFormSubmit}
              >
                {selectedProgram ? 'Update Program' : 'Create Program'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Program Detail View Modal */}
      {showProgramDetailModal && selectedProgramDetail && (
        <div className="modal-overlay">
          <div className="modal program-detail-modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Program Details: {selectedProgramDetail.name}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowProgramDetailModal(false);
                  setSelectedProgramDetail(null);
                  setSelectedDates([]);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              {/* Program Information Section */}
              <div className="program-detail-section">
                <h4>Program Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedProgramDetail.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">
                      <span className={`type-badge ${selectedProgramDetail.programType}`}>
                        {selectedProgramDetail.programType.replace('_', ' ')}
                      </span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Tier:</span>
                    <span className="detail-value">
                      <span className={`tier-badge ${selectedProgramDetail.tier}`}>
                        {selectedProgramDetail.tier}
                      </span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">
                      {selectedProgramDetail.durationMinutes != null 
                        ? `${selectedProgramDetail.durationMinutes} minutes` 
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Sessions Required:</span>
                    <span className="detail-value">
                      {selectedProgramDetail.sessionsRequired != null 
                        ? selectedProgramDetail.sessionsRequired 
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value">
                      <span className={`status-badge ${selectedProgramDetail.status}`}>
                        {selectedProgramDetail.status}
                      </span>
                    </span>
                  </div>
                  {selectedProgramDetail.description && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Description:</span>
                      <p className="detail-description">{selectedProgramDetail.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Calendar Section for Session Scheduling */}
              <div className="program-detail-section">
                <h4>Schedule Sessions</h4>
                
                {/* Session Time Configuration */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#495057', fontSize: '0.9rem' }}>
                        Start Time: *
                      </label>
                      <input
                        type="time"
                        value={sessionScheduleInfo.startTime}
                        onChange={(e) => setSessionScheduleInfo({...sessionScheduleInfo, startTime: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                        required
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#495057', fontSize: '0.9rem' }}>
                        End Time: *
                      </label>
                      <input
                        type="time"
                        value={sessionScheduleInfo.endTime}
                        onChange={(e) => setSessionScheduleInfo({...sessionScheduleInfo, endTime: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                        required
                      />
                    </div>
                  </div>
                  <p style={{ marginTop: '10px', fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
                    Select dates in the calendar below to create sessions with these times.
                  </p>
                </div>
                
                <div className="calendar-container">
                  {/* Month Navigation */}
                  <div className="calendar-header">
                    <button
                      className="calendar-nav-btn"
                      onClick={() => {
                        const newMonth = new Date(currentMonth);
                        newMonth.setMonth(newMonth.getMonth() - 1);
                        setCurrentMonth(newMonth);
                      }}
                    >
                      ← Previous Month
                    </button>
                    <h5 className="calendar-month-year">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h5>
                    <button
                      className="calendar-nav-btn"
                      onClick={async () => {
                        const newMonth = new Date(currentMonth);
                        newMonth.setMonth(newMonth.getMonth() + 1);
                        setCurrentMonth(newMonth);
                        // Refresh program sessions when month changes
                        if (selectedProgramDetail) {
                          try {
                            const response = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                              params: {
                                program_ids: selectedProgramDetail.id
                              }
                            });
                            setProgramSessions(Array.isArray(response.data) ? response.data : []);
                          } catch (error) {
                            console.error('❌ Error fetching program sessions:', error);
                          }
                        }
                      }}
                    >
                      Next Month →
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="calendar-wrapper">
                    {/* Day Headers */}
                    <div className="calendar-day-headers">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} className="calendar-day-header">{day}</div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="calendar-days-grid">
                      {(() => {
                        const year = currentMonth.getFullYear();
                        const month = currentMonth.getMonth();
                        const firstDay = new Date(year, month, 1);
                        const lastDay = new Date(year, month + 1, 0);
                        const startDate = new Date(firstDay);
                        startDate.setDate(startDate.getDate() - startDate.getDay());
                        
                        const days: Date[] = [];
                        const currentDate = new Date(startDate);
                        
                        // Generate exactly 6 weeks (42 days) for consistent calendar display
                        for (let i = 0; i < 42; i++) {
                          days.push(new Date(currentDate));
                          currentDate.setDate(currentDate.getDate() + 1);
                        }

                        return days.map((date, index) => {
                          const isCurrentMonth = date.getMonth() === month;
                          const isToday = date.toDateString() === new Date().toDateString();
                          const isSelected = selectedDates.some(d => d.toDateString() === date.toDateString());
                          
                          // Check if this date has a scheduled session for the selected program
                          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                          const hasSession = programSessions.some((session: any) => {
                            if (!session.date_available) return false;
                            const sessionDateStr = typeof session.date_available === 'string' 
                              ? session.date_available.split('T')[0]
                              : new Date(session.date_available).toISOString().split('T')[0];
                            return sessionDateStr === dateStr && 
                                   session.program_id === selectedProgramDetail?.id;
                          });

                          return (
                            <div
                              key={index}
                              className={`calendar-day-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasSession ? 'has-session' : ''}`}
                              onClick={() => {
                                // Allow selection of all dates, including past dates and other months
                                if (isSelected) {
                                  setSelectedDates(selectedDates.filter(d => d.toDateString() !== date.toDateString()));
                                } else {
                                  setSelectedDates([...selectedDates, date]);
                                }
                              }}
                              title={hasSession 
                                ? `${date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} - Session scheduled`
                                : date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            >
                              <span className="calendar-day-number">{date.getDate()}</span>
                              {hasSession && (
                                <span style={{
                                  position: 'absolute',
                                  top: '2px',
                                  right: '2px',
                                  fontSize: '10px',
                                  color: '#2e7d32',
                                  fontWeight: 'bold'
                                }}>✓</span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* Selected Dates Summary */}
                  {selectedDates.length > 0 && (
                    <div className="selected-dates-summary">
                      <h5>Selected Dates ({selectedDates.length}):</h5>
                      <div className="selected-dates-list">
                        {selectedDates
                          .sort((a, b) => a.getTime() - b.getTime())
                          .map((date, index) => (
                            <span key={index} className="selected-date-tag">
                              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              <button
                                className="remove-date-btn"
                                onClick={() => {
                                  setSelectedDates(selectedDates.filter(d => d.toDateString() !== date.toDateString()));
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                          if (selectedDates.length === 0) {
                            alert('Please select at least one date.');
                            return;
                          }
                          if (!sessionScheduleInfo.startTime || !sessionScheduleInfo.endTime) {
                            alert('Please set start and end times.');
                            return;
                          }
                          
                          const button = e.currentTarget;
                          
                          try {
                            // Disable button during creation
                            button.disabled = true;
                            button.textContent = 'Creating Sessions...';
                            
                            let successCount = 0;
                            let errorCount = 0;
                            
                            // Create a session for each selected date
                            for (const date of selectedDates) {
                              try {
                                // Format date as YYYY-MM-DD
                                const dateStr = date.toISOString().split('T')[0];
                                
                                // Prepare session data
                                const sessionData = {
                                  program_id: selectedProgramDetail.id,
                                  date_available: dateStr,
                                  start_time: sessionScheduleInfo.startTime,
                                  end_time: sessionScheduleInfo.endTime,
                                  max_capacity: 20,
                                  status: 'scheduled',
                                  description: `Scheduled session for ${selectedProgramDetail.name}`
                                };
                                
                                console.log('Creating session:', sessionData);
                                
                                const response = await axios.post(
                                  `${config.BACKEND_URL}/api/class-sessions/`,
                                  sessionData
                                );
                                
                                if (response.data && response.data.id) {
                                  successCount++;
                                  console.log(`✅ Created session for ${dateStr}`);
                                } else {
                                  errorCount++;
                                  console.error(`❌ Failed to create session for ${dateStr}:`, response.data);
                                }
                              } catch (error: any) {
                                errorCount++;
                                console.error(`❌ Error creating session for ${date.toDateString()}:`, error);
                                if (error.response?.data?.detail) {
                                  console.error('Error detail:', error.response.data.detail);
                                }
                              }
                            }
                            
                            // Re-enable button
                            button.disabled = false;
                            button.textContent = 'Create Sessions for Selected Dates';
                            
                            // Show results
                            if (successCount > 0) {
                              alert(`Successfully created ${successCount} session(s)${errorCount > 0 ? `. ${errorCount} failed.` : '.'}`);
                              // Refresh sessions list
                              await fetchSessions();
                              // Refresh program sessions to update calendar colors
                              if (selectedProgramDetail) {
                                try {
                                  const response = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                    params: {
                                      program_ids: selectedProgramDetail.id
                                    }
                                  });
                                  setProgramSessions(Array.isArray(response.data) ? response.data : []);
                                } catch (error) {
                                  console.error('❌ Error refreshing program sessions:', error);
                                }
                              }
                              // Clear selected dates
                              setSelectedDates([]);
                            } else {
                              alert(`Failed to create sessions. Please check the console for details.`);
                            }
                          } catch (error: any) {
                            console.error('❌ Error creating sessions:', error);
                            alert('An error occurred while creating sessions. Please check the console for details.');
                            // Re-enable button
                            button.disabled = false;
                            button.textContent = 'Create Sessions for Selected Dates';
                          }
                        }}
                      >
                        Create Sessions for Selected Dates
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowProgramDetailModal(false);
                  setSelectedProgramDetail(null);
                  setSelectedDates([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course Form Modal */}
      {showCourseForm && (
        <div className="modal-overlay">
          <div className="modal course-form-modal">
            <div className="modal-header">
              <h3>{selectedCourse ? 'Edit Course' : 'Add New Course'}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCourseForm(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <form onSubmit={handleCourseFormSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Course Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={courseFormData.name}
                    onChange={handleCourseFormChange}
                    required
                    placeholder="Enter course name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tier">Tier</label>
                    <select
                      id="tier"
                      name="tier"
                      value={courseFormData.tier}
                      onChange={handleCourseFormChange}
                      required
                    >
                      <option value="foundations">Foundations</option>
                      <option value="innovators">Innovators</option>
                      <option value="pioneers">Pioneers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="ageGroup">Age Group</label>
                    <select
                      id="ageGroup"
                      name="ageGroup"
                      value={courseFormData.ageGroup}
                      onChange={handleCourseFormChange}
                    >
                      <option value="">Select Age Group</option>
                      <option value="6-8">6-8 years</option>
                      <option value="9-12">9-12 years</option>
                      <option value="13-16">13-16 years</option>
                      <option value="17+">17+ years</option>
                      <option value="6-12">6-12 years</option>
                      <option value="9-16">9-16 years</option>
                      <option value="13-18">13-18 years</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="programId">Program (Optional)</label>
                  <select
                    id="programId"
                    name="programId"
                    value={courseFormData.programId}
                    onChange={handleCourseFormChange}
                  >
                    <option value="">Select Program (Optional)</option>
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.tier})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={courseFormData.description}
                    onChange={handleCourseFormChange}
                    rows={3}
                    placeholder="Enter course description"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="durationMinutes">Duration (minutes)</label>
                    <input
                      type="number"
                      id="durationMinutes"
                      name="durationMinutes"
                      value={courseFormData.durationMinutes}
                      onChange={handleCourseFormChange}
                      min="30"
                      max="300"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="sessionsRequired">Sessions Required</label>
                    <input
                      type="number"
                      id="sessionsRequired"
                      name="sessionsRequired"
                      value={courseFormData.sessionsRequired}
                      onChange={handleCourseFormChange}
                      min="1"
                      max="50"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="prerequisites">Prerequisites</label>
                  <textarea
                    id="prerequisites"
                    name="prerequisites"
                    value={courseFormData.prerequisites}
                    onChange={handleCourseFormChange}
                    rows={2}
                    placeholder="Enter prerequisites"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={courseFormData.status}
                    onChange={handleCourseFormChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCourseForm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCourseFormSubmit}
              >
                {selectedCourse ? 'Update Course' : 'Create Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Form Modal */}
      {showSessionForm && (
        <div className="modal-overlay">
          <div className="modal session-form-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{selectedSession ? 'Edit Session' : 'Create New Session'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowSessionForm(false);
                  setSelectedSession(null);
                  setError(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message" style={{ 
                  padding: '12px', 
                  backgroundColor: '#fee', 
                  border: '1px solid #fcc', 
                  borderRadius: '4px', 
                  marginBottom: '20px',
                  color: '#c33'
                }}>
                  ⚠️ {error}
                </div>
              )}
              <form onSubmit={handleSessionFormSubmit}>
                {/* Program Selection - First */}
                <div className="form-group">
                  <label htmlFor="programId">Program <span style={{ color: '#999' }}>(optional)</span></label>
                  <select
                    id="programId"
                    name="programId"
                    value={sessionFormData.programId}
                    onChange={handleSessionFormChange}
                  >
                    <option value="">Select a program (optional)</option>
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.tier})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Start and End Time - Second */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="startTime">Start Time <span style={{ color: '#c33' }}>*</span></label>
                    <input
                      type="time"
                      id="startTime"
                      name="startTime"
                      value={sessionFormData.startTime}
                      onChange={handleSessionFormChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="endTime">End Time <span style={{ color: '#c33' }}>*</span></label>
                    <input
                      type="time"
                      id="endTime"
                      name="endTime"
                      value={sessionFormData.endTime}
                      onChange={handleSessionFormChange}
                      required
                    />
                  </div>
                </div>

                {/* Calendar Month View for Date Selection */}
                <div className="form-group">
                  <label>Select Dates <span style={{ color: '#999' }}>(optional - select multiple)</span></label>
                  <div className="calendar-container" style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <div className="calendar-header">
                      <button
                        type="button"
                        className="calendar-nav-btn"
                        onClick={() => {
                          const newMonth = new Date(sessionCalendarMonth);
                          newMonth.setMonth(newMonth.getMonth() - 1);
                          setSessionCalendarMonth(newMonth);
                        }}
                        title="Previous Month"
                      >
                        ←
                      </button>
                      <div className="calendar-month-year">
                        {sessionCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                      <button
                        type="button"
                        className="calendar-nav-btn"
                        onClick={() => {
                          const newMonth = new Date(sessionCalendarMonth);
                          newMonth.setMonth(newMonth.getMonth() + 1);
                          setSessionCalendarMonth(newMonth);
                        }}
                        title="Next Month"
                      >
                        →
                      </button>
                    </div>
                    
                    <div className="calendar-day-headers">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="calendar-day-header">{day}</div>
                      ))}
                    </div>
                    
                    <div className="calendar-days-grid">
                      {(() => {
                        const year = sessionCalendarMonth.getFullYear();
                        const month = sessionCalendarMonth.getMonth();
                        
                        // Get first day of month and how many days to show before it
                        const firstDay = new Date(year, month, 1);
                        const startDate = new Date(firstDay);
                        startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
                        
                        // Generate 42 days (6 weeks)
                        const days: Date[] = [];
                        for (let i = 0; i < 42; i++) {
                          const date = new Date(startDate);
                          date.setDate(startDate.getDate() + i);
                          days.push(new Date(date));
                        }
                        
                        return days.map((date, index) => {
                          const isCurrentMonth = date.getMonth() === month;
                          const isToday = date.toDateString() === new Date().toDateString();
                          const isSelected = sessionSelectedDates.some(d => d.toDateString() === date.toDateString());
                          
                          return (
                            <div
                              key={index}
                              className={`calendar-day-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                if (!isCurrentMonth) return;
                                if (isSelected) {
                                  setSessionSelectedDates(sessionSelectedDates.filter(d => d.toDateString() !== date.toDateString()));
                                } else {
                                  setSessionSelectedDates([...sessionSelectedDates, date]);
                                }
                              }}
                              title={date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                              style={{
                                position: 'relative',
                                cursor: isCurrentMonth ? 'pointer' : 'default',
                                opacity: isCurrentMonth ? 1 : 0.3
                              }}
                            >
                              <span className="calendar-day-number">{date.getDate()}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  {sessionSelectedDates.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '0.9em' }}>
                      <strong>Selected {sessionSelectedDates.length} date(s):</strong> {sessionSelectedDates.map(d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })).join(', ')}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="courseId">Course <span style={{ color: '#999' }}>(optional)</span></label>
                  <select
                    id="courseId"
                    name="courseId"
                    value={sessionFormData.courseId}
                    onChange={handleSessionFormChange}
                  >
                    <option value="">Select a course (optional)</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name} ({course.tier})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="instructorId">Instructor <span style={{ color: '#999' }}>(optional)</span></label>
                  <select
                    id="instructorId"
                    name="instructorId"
                    value={sessionFormData.instructorId}
                    onChange={handleSessionFormChange}
                  >
                    <option value="">Select an instructor (optional)</option>
                    {instructors.map((instructor) => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.firstName} {instructor.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="location">Location <span style={{ color: '#999' }}>(optional)</span></label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={sessionFormData.location}
                      onChange={handleSessionFormChange}
                      placeholder="e.g., Main Hall, Room 101"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="maxCapacity">Max Capacity</label>
                    <input
                      type="number"
                      id="maxCapacity"
                      name="maxCapacity"
                      value={sessionFormData.maxCapacity}
                      onChange={handleSessionFormChange}
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description <span style={{ color: '#999' }}>(optional)</span></label>
                  <textarea
                    id="description"
                    name="description"
                    value={sessionFormData.description}
                    onChange={handleSessionFormChange}
                    rows={3}
                    placeholder="Add any additional details about this session..."
                  />
                </div>

                <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowSessionForm(false);
                      setSelectedSession(null);
                      setError(null);
                      setSessionSelectedDates([]);
                      setSessionCalendarMonth(new Date());
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isCreatingSessions}
                  >
                    {isCreatingSessions 
                      ? (selectedSession ? 'Updating...' : `Creating ${sessionSelectedDates.length || 1} Session(s)...`)
                      : (selectedSession ? 'Update Session' : sessionSelectedDates.length > 0 ? `Create ${sessionSelectedDates.length} Sessions` : 'Create Session')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Students to Session Modal */}
      {showAddStudentsModal && selectedSessionForStudents && (
        <div className="modal-overlay">
          <div className="modal add-students-modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Add Students to Session</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAddStudentsModal(false);
                  setSelectedSessionForStudents(null);
                  setSelectedStudentIds([]);
                  setSelectedGroupIds([]);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <strong>Session Details:</strong>
                <div style={{ marginTop: '8px' }}>
                  {selectedSessionForStudents.date_available && (
                    <div>Date: {formatDatePSTShort(selectedSessionForStudents.date_available)}</div>
                  )}
                  <div>Time: {selectedSessionForStudents.start_time?.substring(0, 5)} - {selectedSessionForStudents.end_time?.substring(0, 5)}</div>
                  {selectedSessionForStudents.program_name && (
                    <div>Program: {selectedSessionForStudents.program_name}</div>
                  )}
                  <div>Enrollment: {selectedSessionForStudents.current_enrollment}/{selectedSessionForStudents.max_capacity}</div>
                </div>
              </div>

              {/* Group Selection */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Add Groups (Optional)</label>
                <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                  Select groups to automatically add all students from those groups
                </p>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '10px'
                }}>
                  {groups.length === 0 ? (
                    <p style={{ color: '#999', textAlign: 'center', padding: '10px' }}>No groups available</p>
                  ) : (
                    groups.map((group) => {
                      const isSelected = selectedGroupIds.includes(group.id);
                      return (
                        <label 
                          key={group.id} 
                          style={{ 
                            display: 'block', 
                            padding: '10px', 
                            marginBottom: '8px',
                            backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                            border: `1px solid ${isSelected ? '#2196f3' : '#ddd'}`,
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={async (e) => {
                              if (e.target.checked) {
                                // Add group to selection
                                const newGroupIds = [...selectedGroupIds, group.id];
                                setSelectedGroupIds(newGroupIds);
                                
                                // Fetch members of this group and add them to selected students
                                try {
                                  const membersResponse = await axios.get(`${config.BACKEND_URL}/api/groups/${group.id}/members`);
                                  const memberIds = (membersResponse.data || []).map((m: GroupMember) => m.student_id);
                                  // Add group members to selected students (avoid duplicates)
                                  setSelectedStudentIds(prev => {
                                    const combined = Array.from(new Set([...prev, ...memberIds]));
                                    return combined;
                                  });
                                } catch (error) {
                                  console.error('Error fetching group members:', error);
                                }
                              } else {
                                // Remove group from selection
                                const newGroupIds = selectedGroupIds.filter(id => id !== group.id);
                                setSelectedGroupIds(newGroupIds);
                                
                                // Remove group members from selected students
                                try {
                                  const membersResponse = await axios.get(`${config.BACKEND_URL}/api/groups/${group.id}/members`);
                                  const memberIds = (membersResponse.data || []).map((m: GroupMember) => m.student_id);
                                  setSelectedStudentIds(prev => prev.filter(id => !memberIds.includes(id)));
                                } catch (error) {
                                  console.error('Error fetching group members:', error);
                                }
                              }
                            }}
                            style={{ marginRight: '10px' }}
                          />
                          <span style={{ fontWeight: '500' }}>
                            {group.name}
                          </span>
                          {group.group_type && (
                            <span style={{ color: '#666', marginLeft: '10px' }}>
                              ({group.group_type})
                            </span>
                          )}
                          <span style={{ color: '#666', marginLeft: '10px', fontSize: '0.9em' }}>
                            - {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Select Individual Students</label>
                <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                  Or select individual students to add
                </p>
                <div style={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '10px'
                }}>
                  {availableStudents.length === 0 ? (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No students available</p>
                  ) : (
                    availableStudents.map((student) => {
                      const isEnrolled = sessionStudents.some(s => s.id === student.id);
                      const isSelected = selectedStudentIds.includes(student.id);
                      return (
                        <label 
                          key={student.id} 
                          style={{ 
                            display: 'block', 
                            padding: '10px', 
                            marginBottom: '8px',
                            backgroundColor: isSelected ? '#e8f5e9' : isEnrolled ? '#fff3cd' : '#fff',
                            border: `1px solid ${isSelected ? '#4caf50' : isEnrolled ? '#ffc107' : '#ddd'}`,
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStudentIds([...selectedStudentIds, student.id]);
                              } else {
                                setSelectedStudentIds(selectedStudentIds.filter(id => id !== student.id));
                              }
                            }}
                            disabled={isEnrolled}
                            style={{ marginRight: '10px' }}
                          />
                          <span style={{ fontWeight: '500' }}>
                            {student.first_name} {student.last_name}
                          </span>
                          <span style={{ color: '#666', marginLeft: '10px' }}>
                            ({student.tier}, Age: {student.age})
                          </span>
                          {isEnrolled && (
                            <span style={{ 
                              marginLeft: '10px', 
                              color: '#856404', 
                              fontSize: '0.85em',
                              fontStyle: 'italic'
                            }}>
                              (Already enrolled)
                            </span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddStudentsModal(false);
                    setSelectedSessionForStudents(null);
                    setSelectedStudentIds([]);
                    setSelectedGroupIds([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveStudentsToSession}
                  disabled={selectedStudentIds.length === 0}
                >
                  Add {selectedStudentIds.length > 0 ? `${selectedStudentIds.length} ` : ''}Student{selectedStudentIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Form Modal */}
      {showGroupForm && (
        <div className="modal-overlay">
          <div className="modal group-form-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{selectedGroup ? 'Edit Group' : 'Create New Group'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowGroupForm(false);
                  setSelectedGroup(null);
                  setError(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message" style={{ 
                  padding: '12px', 
                  backgroundColor: '#fee', 
                  border: '1px solid #fcc', 
                  borderRadius: '4px', 
                  marginBottom: '20px',
                  color: '#c33'
                }}>
                  ⚠️ {error}
                </div>
              )}
              <form onSubmit={handleGroupFormSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Group Name <span style={{ color: '#c33' }}>*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={groupFormData.name}
                    onChange={handleGroupFormChange}
                    required
                    placeholder="e.g., Robotics Team A, Project Alpha"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="groupType">Group Type <span style={{ color: '#999' }}>(optional)</span></label>
                  <select
                    id="groupType"
                    name="groupType"
                    value={groupFormData.groupType}
                    onChange={handleGroupFormChange}
                  >
                    <option value="">Select type (optional)</option>
                    <option value="project">Project</option>
                    <option value="team">Team</option>
                    <option value="class">Class</option>
                    <option value="club">Club</option>
                    <option value="competition">Competition</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description <span style={{ color: '#999' }}>(optional)</span></label>
                  <textarea
                    id="description"
                    name="description"
                    value={groupFormData.description}
                    onChange={handleGroupFormChange}
                    rows={3}
                    placeholder="Add a description for this group..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={groupFormData.status}
                    onChange={handleGroupFormChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowGroupForm(false);
                      setSelectedGroup(null);
                      setError(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {selectedGroup ? 'Update Group' : 'Create Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserDetailModal && selectedUserDetail && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal user-detail-modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, borderBottom: '2px solid #eee' }}>
              <h3>User Details</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowUserDetailModal(false);
                  setSelectedUserDetail(null);
                  setUserChildren([]);
                  setShowAddChildForm(false);
                  setShowEditChildForm(false);
                  setSelectedChild(null);
                  resetChildForm();
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {userDetailLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading user details...</p>
                </div>
              ) : (
                <>
                  {/* User Information */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#2c3e50' }}>User Information</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Name:</strong>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>{selectedUserDetail.firstName} {selectedUserDetail.lastName}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Email:</strong>
                        <span>{selectedUserDetail.email}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Phone:</strong>
                        <span>{selectedUserDetail.phone || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Role:</strong>
                        <span className={`role-badge ${selectedUserDetail.userType}`} style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85em',
                          textTransform: 'capitalize'
                        }}>
                          {selectedUserDetail.userType}
                        </span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Status:</strong>
                        <span className={`status-badge ${selectedUserDetail.status}`} style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85em',
                          textTransform: 'capitalize'
                        }}>
                          {selectedUserDetail.status}
                        </span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Created:</strong>
                        <span>{selectedUserDetail.createdAt ? new Date(selectedUserDetail.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'N/A'}</span>
                      </div>
                      {selectedUserDetail.groups && selectedUserDetail.groups.length > 0 && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Groups:</strong>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {selectedUserDetail.groups.map((group: string, index: number) => (
                              <span key={index} style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                borderRadius: '12px',
                                fontSize: '0.85em'
                              }}>
                                {group}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Children Section (for parents) */}
                  {selectedUserDetail.userType === 'parent' && (
                    <div style={{ marginBottom: '30px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, color: '#2c3e50' }}>
                          Registered Children ({userChildren.length})
                        </h4>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleAddChild}
                          style={{ 
                            padding: '8px 16px',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          ➕ Add Child
                        </button>
                      </div>

                      {/* Add Child Form */}
                      {showAddChildForm && (
                        <div style={{ 
                          marginBottom: '20px', 
                          padding: '20px', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '8px',
                          border: '1px solid #dee2e6'
                        }}>
                          <h5 style={{ marginTop: 0, marginBottom: '15px' }}>Add New Child</h5>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                First Name <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={childFormData.first_name}
                                onChange={(e) => setChildFormData({ ...childFormData, first_name: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Last Name <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={childFormData.last_name}
                                onChange={(e) => setChildFormData({ ...childFormData, last_name: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Date of Birth <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                type="date"
                                value={childFormData.date_of_birth}
                                onChange={(e) => setChildFormData({ ...childFormData, date_of_birth: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Grade Level <span style={{ color: 'red' }}>*</span>
                              </label>
                              <select
                                value={childFormData.grade_level}
                                onChange={(e) => setChildFormData({ ...childFormData, grade_level: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              >
                                <option value="">Select Grade</option>
                                <option value="K">Kindergarten</option>
                                <option value="1">Grade 1</option>
                                <option value="2">Grade 2</option>
                                <option value="3">Grade 3</option>
                                <option value="4">Grade 4</option>
                                <option value="5">Grade 5</option>
                                <option value="6">Grade 6</option>
                                <option value="7">Grade 7</option>
                                <option value="8">Grade 8</option>
                                <option value="9">Grade 9</option>
                                <option value="10">Grade 10</option>
                                <option value="11">Grade 11</option>
                                <option value="12">Grade 12</option>
                                <option value="12+">12+</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Tier <span style={{ color: 'red' }}>*</span>
                              </label>
                              <select
                                value={childFormData.tier}
                                onChange={(e) => setChildFormData({ ...childFormData, tier: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              >
                                <option value="foundations">Foundations</option>
                                <option value="innovators">Innovators</option>
                                <option value="pioneers">Pioneers</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Status</label>
                              <select
                                value={childFormData.status}
                                onChange={(e) => setChildFormData({ ...childFormData, status: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              >
                                <option value="trial">Trial</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="graduated">Graduated</option>
                                <option value="suspended">Suspended</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Email</label>
                              <input
                                type="email"
                                value={childFormData.email}
                                onChange={(e) => setChildFormData({ ...childFormData, email: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Phone</label>
                              <input
                                type="tel"
                                value={childFormData.phone}
                                onChange={(e) => setChildFormData({ ...childFormData, phone: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Enrollment Date</label>
                              <input
                                type="date"
                                value={childFormData.enrollment_date}
                                onChange={(e) => setChildFormData({ ...childFormData, enrollment_date: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Address</label>
                              <textarea
                                value={childFormData.address}
                                onChange={(e) => setChildFormData({ ...childFormData, address: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '60px' }}
                                rows={2}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setShowAddChildForm(false);
                                resetChildForm();
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveChild}
                              disabled={isSavingChild}
                            >
                              {isSavingChild ? 'Saving...' : '✓ Add Child'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Edit Child Form */}
                      {showEditChildForm && selectedChild && (
                        <div style={{ 
                          marginBottom: '20px', 
                          padding: '20px', 
                          backgroundColor: '#fff3cd', 
                          borderRadius: '8px',
                          border: '1px solid #ffc107'
                        }}>
                          <h5 style={{ marginTop: 0, marginBottom: '15px' }}>Edit Child: {selectedChild.first_name} {selectedChild.last_name}</h5>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                First Name <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={childFormData.first_name}
                                onChange={(e) => setChildFormData({ ...childFormData, first_name: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Last Name <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={childFormData.last_name}
                                onChange={(e) => setChildFormData({ ...childFormData, last_name: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Date of Birth <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                type="date"
                                value={childFormData.date_of_birth}
                                onChange={(e) => setChildFormData({ ...childFormData, date_of_birth: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Grade Level <span style={{ color: 'red' }}>*</span>
                              </label>
                              <select
                                value={childFormData.grade_level}
                                onChange={(e) => setChildFormData({ ...childFormData, grade_level: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              >
                                <option value="">Select Grade</option>
                                <option value="K">Kindergarten</option>
                                <option value="1">Grade 1</option>
                                <option value="2">Grade 2</option>
                                <option value="3">Grade 3</option>
                                <option value="4">Grade 4</option>
                                <option value="5">Grade 5</option>
                                <option value="6">Grade 6</option>
                                <option value="7">Grade 7</option>
                                <option value="8">Grade 8</option>
                                <option value="9">Grade 9</option>
                                <option value="10">Grade 10</option>
                                <option value="11">Grade 11</option>
                                <option value="12">Grade 12</option>
                                <option value="12+">12+</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Tier <span style={{ color: 'red' }}>*</span>
                              </label>
                              <select
                                value={childFormData.tier}
                                onChange={(e) => setChildFormData({ ...childFormData, tier: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                required
                              >
                                <option value="foundations">Foundations</option>
                                <option value="innovators">Innovators</option>
                                <option value="pioneers">Pioneers</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Status</label>
                              <select
                                value={childFormData.status}
                                onChange={(e) => setChildFormData({ ...childFormData, status: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              >
                                <option value="trial">Trial</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="graduated">Graduated</option>
                                <option value="suspended">Suspended</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Email</label>
                              <input
                                type="email"
                                value={childFormData.email}
                                onChange={(e) => setChildFormData({ ...childFormData, email: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Phone</label>
                              <input
                                type="tel"
                                value={childFormData.phone}
                                onChange={(e) => setChildFormData({ ...childFormData, phone: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Enrollment Date</label>
                              <input
                                type="date"
                                value={childFormData.enrollment_date}
                                onChange={(e) => setChildFormData({ ...childFormData, enrollment_date: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Address</label>
                              <textarea
                                value={childFormData.address}
                                onChange={(e) => setChildFormData({ ...childFormData, address: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '60px' }}
                                rows={2}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setShowEditChildForm(false);
                                setSelectedChild(null);
                                resetChildForm();
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveChild}
                              disabled={isSavingChild}
                            >
                              {isSavingChild ? 'Saving...' : '✓ Update Child'}
                            </button>
                          </div>
                        </div>
                      )}

                      {userChildren.length === 0 && !showAddChildForm ? (
                        <div style={{ 
                          padding: '40px', 
                          textAlign: 'center', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '8px',
                          color: '#999'
                        }}>
                          <p style={{ margin: 0 }}>No children registered for this parent.</p>
                        </div>
                      ) : (
                        <div style={{ 
                          border: '1px solid #ddd', 
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                              <tr>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Name</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Email</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Age</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Grade</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Tier</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Enrollment Date</th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userChildren.map((child: any) => (
                                <tr key={child.id} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '12px', fontWeight: '500' }}>
                                    {child.first_name} {child.last_name}
                                  </td>
                                  <td style={{ padding: '12px', color: '#666' }}>
                                    {child.email || 'N/A'}
                                  </td>
                                  <td style={{ padding: '12px', color: '#666' }}>
                                    {child.age || 'N/A'}
                                  </td>
                                  <td style={{ padding: '12px', color: '#666' }}>
                                    {child.grade_level}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <span className={`tier-badge ${child.tier}`} style={{
                                      display: 'inline-block',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '0.85em',
                                      textTransform: 'capitalize'
                                    }}>
                                      {child.tier}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <span className={`status-badge ${child.status}`} style={{
                                      display: 'inline-block',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '0.85em',
                                      textTransform: 'capitalize'
                                    }}>
                                      {child.status}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px', color: '#666' }}>
                                    {child.enrollment_date ? new Date(child.enrollment_date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    }) : 'N/A'}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button
                                        type="button"
                                        onClick={() => handleEditChild(child)}
                                        style={{
                                          padding: '4px 8px',
                                          fontSize: '12px',
                                          background: '#007bff',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer'
                                        }}
                                        title="Edit Child"
                                      >
                                        ✏️ Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveChild(child)}
                                        style={{
                                          padding: '4px 8px',
                                          fontSize: '12px',
                                          background: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer'
                                        }}
                                        title="Remove Child"
                                      >
                                        🗑️ Remove
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowUserDetailModal(false);
                  setSelectedUserDetail(null);
                  setUserChildren([]);
                  setShowAddChildForm(false);
                  setShowEditChildForm(false);
                  setSelectedChild(null);
                  resetChildForm();
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowUserDetailModal(false);
                  handleEditUser(selectedUserDetail);
                }}
              >
                ✏️ Edit User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Detail Modal */}
      {showStudentDetailModal && selectedStudentDetail && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal student-detail-modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, borderBottom: '2px solid #eee' }}>
              <h3>Student Details</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowStudentDetailModal(false);
                  setSelectedStudentDetail(null);
                  setStudentEnrollments([]);
                  setStudentAttendance([]);
                  setAttendanceSelectedProgram('');
                  setAttendanceSelectedDates([]);
                  setAttendanceCalendarMonth(new Date());
                  setShowEnrollmentForm(false);
                  setEnrollmentProgramId('');
                  setEnrollmentDate(new Date().toISOString().split('T')[0]);
                  setAvailablePrograms([]);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {studentDetailLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading student details...</p>
                </div>
              ) : (
                <>
                  {/* Student Information */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ marginTop: 0, marginBottom: 0, color: '#2c3e50' }}>Student Information</h4>
                      {!isEditingStudent && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setIsEditingStudent(true);
                            setStudentEditData({
                              first_name: selectedStudentDetail.first_name || '',
                              last_name: selectedStudentDetail.last_name || '',
                              email: selectedStudentDetail.email || '',
                              grade_level: selectedStudentDetail.grade_level || '',
                              phone: (selectedStudentDetail as any).phone || '',
                              address: (selectedStudentDetail as any).address || '',
                              status: selectedStudentDetail.status || '',
                              profile_notes: (selectedStudentDetail as any).profile_notes || '',
                              learning_goals: (selectedStudentDetail as any).learning_goals || '',
                              slack_username: (selectedStudentDetail as any).slack_username || ''
                            });
                          }}
                          style={{ fontSize: '0.9em', padding: '6px 12px' }}
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </div>
                    
                    {!isEditingStudent ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Name:</strong>
                          <span style={{ fontSize: '16px', fontWeight: '600' }}>{selectedStudentDetail.first_name} {selectedStudentDetail.last_name}</span>
                        </div>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Email:</strong>
                          <span>{selectedStudentDetail.email || 'N/A'}</span>
                        </div>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Age:</strong>
                          <span>{selectedStudentDetail.age || 'N/A'}</span>
                        </div>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Grade:</strong>
                          <span>{selectedStudentDetail.grade_level}</span>
                        </div>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Tier:</strong>
                          <span className={`tier-badge ${selectedStudentDetail.tier}`} style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.85em',
                            textTransform: 'capitalize'
                          }}>
                            {selectedStudentDetail.tier}
                          </span>
                        </div>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Status:</strong>
                          <span className={`status-badge ${selectedStudentDetail.status}`} style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.85em',
                            textTransform: 'capitalize'
                          }}>
                            {selectedStudentDetail.status}
                          </span>
                        </div>
                        <div>
                          <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Parent:</strong>
                          <span>{selectedStudentDetail.parent_name || 'Unknown'}</span>
                        </div>
                        {(selectedStudentDetail as any).phone && (
                          <div>
                            <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Phone:</strong>
                            <span>{(selectedStudentDetail as any).phone}</span>
                          </div>
                        )}
                        {(selectedStudentDetail as any).address && (
                          <div>
                            <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Address:</strong>
                            <span>{(selectedStudentDetail as any).address}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>First Name: *</label>
                            <input
                              type="text"
                              value={studentEditData.first_name}
                              onChange={(e) => setStudentEditData({...studentEditData, first_name: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Last Name: *</label>
                            <input
                              type="text"
                              value={studentEditData.last_name}
                              onChange={(e) => setStudentEditData({...studentEditData, last_name: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Email:</label>
                            <input
                              type="email"
                              value={studentEditData.email}
                              onChange={(e) => setStudentEditData({...studentEditData, email: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Grade:</label>
                            <select
                              value={studentEditData.grade_level}
                              onChange={(e) => setStudentEditData({...studentEditData, grade_level: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                            >
                              <option value="">Select Grade</option>
                              <option value="K">Kindergarten</option>
                              <option value="1">Grade 1</option>
                              <option value="2">Grade 2</option>
                              <option value="3">Grade 3</option>
                              <option value="4">Grade 4</option>
                              <option value="5">Grade 5</option>
                              <option value="6">Grade 6</option>
                              <option value="7">Grade 7</option>
                              <option value="8">Grade 8</option>
                              <option value="9">Grade 9</option>
                              <option value="10">Grade 10</option>
                              <option value="11">Grade 11</option>
                              <option value="12">Grade 12</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Status:</label>
                            <select
                              value={studentEditData.status}
                              onChange={(e) => setStudentEditData({...studentEditData, status: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                            >
                              <option value="trial">Trial</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="graduated">Graduated</option>
                              <option value="suspended">Suspended</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Phone:</label>
                            <input
                              type="tel"
                              value={studentEditData.phone}
                              onChange={(e) => setStudentEditData({...studentEditData, phone: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                              placeholder="+1234567890"
                            />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Address:</label>
                            <input
                              type="text"
                              value={studentEditData.address}
                              onChange={(e) => setStudentEditData({...studentEditData, address: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                            />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Profile Notes:</label>
                            <textarea
                              value={studentEditData.profile_notes}
                              onChange={(e) => setStudentEditData({...studentEditData, profile_notes: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px', minHeight: '80px' }}
                              placeholder="Additional notes about the student..."
                            />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Learning Goals:</label>
                            <textarea
                              value={studentEditData.learning_goals}
                              onChange={(e) => setStudentEditData({...studentEditData, learning_goals: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px', minHeight: '80px' }}
                              placeholder="Student's learning goals..."
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#666' }}>Slack Username:</label>
                            <input
                              type="text"
                              value={studentEditData.slack_username}
                              onChange={(e) => setStudentEditData({...studentEditData, slack_username: e.target.value})}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                              placeholder="@username"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setIsEditingStudent(false);
                              setStudentEditData({
                                first_name: '',
                                last_name: '',
                                email: '',
                                grade_level: '',
                                phone: '',
                                address: '',
                                status: '',
                                profile_notes: '',
                                learning_goals: '',
                                slack_username: ''
                              });
                            }}
                            disabled={isUpdatingStudent}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              if (!selectedStudentDetail || !studentEditData.first_name || !studentEditData.last_name) {
                                alert('Please fill in required fields (First Name and Last Name)');
                                return;
                              }
                              
                              setIsUpdatingStudent(true);
                              try {
                                let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
                                
                                // Check if token is expired and try to refresh
                                if (token) {
                                  try {
                                    const payload = JSON.parse(atob(token.split('.')[1]));
                                    const exp = payload.exp * 1000;
                                    const now = Date.now();
                                    const timeUntilExpiry = exp - now;
                                    
                                    if (exp < now || timeUntilExpiry < 5 * 60 * 1000) {
                                      const { fetchAuthSession } = await import('aws-amplify/auth');
                                      const session = await fetchAuthSession({ forceRefresh: true });
                                      if (session.tokens?.accessToken) {
                                        token = session.tokens.accessToken.toString();
                                        localStorage.setItem('cognitoToken', token);
                                      } else {
                                        throw new Error('Unable to refresh token');
                                      }
                                    }
                                  } catch (refreshError: any) {
                                    console.error('❌ Token refresh failed:', refreshError);
                                    alert('Your session has expired. Please log out and log in again.');
                                    setIsUpdatingStudent(false);
                                    return;
                                  }
                                }
                                
                                if (!token || token.trim() === '') {
                                  alert('Authentication token not found. Please log out and log in again.');
                                  setIsUpdatingStudent(false);
                                  return;
                                }
                                
                                const updateData: any = {};
                                if (studentEditData.first_name) updateData.first_name = studentEditData.first_name;
                                if (studentEditData.last_name) updateData.last_name = studentEditData.last_name;
                                if (studentEditData.email) updateData.email = studentEditData.email;
                                if (studentEditData.grade_level) updateData.grade_level = studentEditData.grade_level;
                                if (studentEditData.phone) updateData.phone = studentEditData.phone;
                                if (studentEditData.address) updateData.address = studentEditData.address;
                                if (studentEditData.status) updateData.status = studentEditData.status;
                                if (studentEditData.profile_notes) updateData.profile_notes = studentEditData.profile_notes;
                                if (studentEditData.learning_goals) updateData.learning_goals = studentEditData.learning_goals;
                                if (studentEditData.slack_username) updateData.slack_username = studentEditData.slack_username;
                                
                                const response = await axios.put(
                                  `${config.BACKEND_URL}/api/students/${selectedStudentDetail.id}`,
                                  updateData,
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  }
                                );
                                
                                alert('Student information updated successfully!');
                                
                                // Refresh student data
                                const updatedStudent = response.data;
                                setSelectedStudentDetail({
                                  ...selectedStudentDetail,
                                  ...updatedStudent,
                                  age: updatedStudent.age || selectedStudentDetail.age
                                });
                                
                                // Refresh students list
                                await fetchStudents();
                                
                                setIsEditingStudent(false);
                              } catch (error: any) {
                                console.error('Error updating student:', error);
                                const errorMessage = error.response?.data?.detail || error.message || 'Failed to update student information';
                                alert(`Error: ${errorMessage}`);
                              } finally {
                                setIsUpdatingStudent(false);
                              }
                            }}
                            disabled={isUpdatingStudent}
                          >
                            {isUpdatingStudent ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Enrollments */}
                  <div style={{ marginBottom: '30px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0, color: '#2c3e50' }}>
                        Enrollments ({studentEnrollments.length})
                      </h4>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setShowEnrollmentForm(!showEnrollmentForm)}
                        style={{ padding: '6px 12px', fontSize: '0.85em' }}
                      >
                        {showEnrollmentForm ? '✕ Cancel' : '➕ Enroll in Program'}
                      </button>
                    </div>
                    
                    {/* Enrollment Form */}
                    {showEnrollmentForm && (
                      <div style={{ 
                        marginBottom: '20px', 
                        padding: '20px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                      }}>
                        <h5 style={{ marginTop: 0, marginBottom: '15px', color: '#2c3e50' }}>
                          Enroll Student in Program
                        </h5>
                        <p style={{ marginBottom: '15px', color: '#666', fontSize: '0.9em', fontStyle: 'italic' }}>
                          Students can be enrolled in multiple programs simultaneously (e.g., After School Innovators and First Lego League Challenge).
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#666' }}>
                              Select Program: *
                            </label>
                            <select
                              value={enrollmentProgramId}
                              onChange={(e) => setEnrollmentProgramId(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                              }}
                              disabled={isEnrolling}
                            >
                              <option value="">-- Select a program --</option>
                              {availablePrograms
                                .filter((program: any) => {
                                  // Filter out programs student is already enrolled in
                                  return !studentEnrollments.some((e: any) => 
                                    e.program_id === program.id && e.status === 'active'
                                  );
                                })
                                .map((program: any) => {
                                  const programType = program.programType || program.program_type || '';
                                  const tierDisplay = program.tier ? program.tier.charAt(0).toUpperCase() + program.tier.slice(1) : '';
                                  const typeDisplay = programType ? `(${programType.replace(/_/g, ' ')})` : '';
                                  return (
                                    <option key={program.id} value={program.id}>
                                      {program.name} {tierDisplay ? `- ${tierDisplay}` : ''} {typeDisplay}
                                    </option>
                                  );
                                })}
                            </select>
                            {availablePrograms.length === 0 && (
                              <p style={{ marginTop: '8px', color: '#999', fontSize: '0.9em', fontStyle: 'italic' }}>
                                {studentEnrollments.length > 0 
                                  ? 'No additional programs available for this student\'s tier, or student is already enrolled in all available programs.'
                                  : `No available programs found for this student's tier (${selectedStudentDetail.tier}).`}
                              </p>
                            )}
                            {availablePrograms.length > 0 && availablePrograms.filter((program: any) => {
                              return !studentEnrollments.some((e: any) => 
                                e.program_id === program.id && e.status === 'active'
                              );
                            }).length === 0 && (
                              <p style={{ marginTop: '8px', color: '#28a745', fontSize: '0.9em' }}>
                                ✓ Student is already enrolled in all available programs for their tier.
                              </p>
                            )}
                          </div>
                          
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#666' }}>
                              Enrollment Date:
                            </label>
                            <input
                              type="date"
                              value={enrollmentDate}
                              onChange={(e) => setEnrollmentDate(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                              }}
                              disabled={isEnrolling}
                            />
                          </div>
                          
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowEnrollmentForm(false);
                                setEnrollmentProgramId('');
                                setEnrollmentDate(new Date().toISOString().split('T')[0]);
                              }}
                              disabled={isEnrolling}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={handleEnrollStudent}
                              disabled={isEnrolling || !enrollmentProgramId}
                            >
                              {isEnrolling ? 'Enrolling...' : 'Enroll Student'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {studentEnrollments.length === 0 ? (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        color: '#999'
                      }}>
                        <p style={{ margin: 0 }}>No enrollments found for this student.</p>
                      </div>
                    ) : (
                      <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Program</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Student Tier</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Status</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Enrollment Date</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentEnrollments.map((enrollment: any) => {
                              const programDisplayName = enrollment.program_name 
                                ? (enrollment.program_tier && enrollment.program_tier !== 'all' 
                                    ? `${enrollment.program_name} (${enrollment.program_tier})` 
                                    : enrollment.program_name)
                                : enrollment.program_id || 'N/A';
                              
                              return (
                                <tr key={enrollment.id} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '12px', fontWeight: '500' }}>
                                    {programDisplayName}
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <span className={`tier-badge ${selectedStudentDetail.tier}`} style={{
                                      display: 'inline-block',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '0.85em',
                                      textTransform: 'capitalize',
                                      fontWeight: '500'
                                    }}>
                                      {selectedStudentDetail.tier}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <span className={`status-badge ${enrollment.status}`} style={{
                                      display: 'inline-block',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '0.85em',
                                      textTransform: 'capitalize'
                                    }}>
                                      {enrollment.status}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px', color: '#666' }}>
                                    {enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    }) : 'N/A'}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    {enrollment.status === 'active' && (
                                      <button
                                        onClick={() => handleUnenrollStudent(enrollment.id, programDisplayName)}
                                        disabled={unenrollingId === enrollment.id}
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: unenrollingId === enrollment.id ? '#ccc' : '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: unenrollingId === enrollment.id ? 'not-allowed' : 'pointer',
                                          fontSize: '0.85em',
                                          fontWeight: '500'
                                        }}
                                        title={`Remove student from ${programDisplayName}`}
                                      >
                                        {unenrollingId === enrollment.id ? 'Removing...' : 'Remove'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Update Attendance Section */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                      Update Attendance
                    </h4>
                    
                    {/* Program Selection */}
                    {studentEnrollments.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#666' }}>
                          Select Program:
                        </label>
                        <select
                          value={attendanceSelectedProgram}
                          onChange={async (e) => {
                            setAttendanceSelectedProgram(e.target.value);
                            setAttendanceSelectedDates([]);
                            
                            // Fetch scheduled sessions for the selected program
                            if (e.target.value && selectedStudentDetail) {
                              try {
                                // Fetch sessions for a wider date range (3 months before and after current month)
                                const year = attendanceCalendarMonth.getFullYear();
                                const month = attendanceCalendarMonth.getMonth();
                                const firstDay = new Date(year, month - 3, 1);
                                const lastDay = new Date(year, month + 4, 0);
                                const fromDate = firstDay.toISOString().split('T')[0];
                                const toDate = lastDay.toISOString().split('T')[0];
                                
                                const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                  params: {
                                    program_id: e.target.value,
                                    from_date: fromDate,
                                    to_date: toDate
                                  }
                                });
                                
                                setAttendanceScheduledSessions(sessionsResponse.data || []);
                              } catch (err: any) {
                                console.error('Error fetching scheduled sessions for attendance:', err);
                                setAttendanceScheduledSessions([]);
                              }
                            } else {
                              setAttendanceScheduledSessions([]);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">-- Select a program --</option>
                          {studentEnrollments.map((enrollment: any) => (
                            <option key={enrollment.id} value={enrollment.program_id}>
                              {enrollment.program_name 
                                ? (enrollment.program_tier && enrollment.program_tier !== 'all' 
                                    ? `${enrollment.program_name} (${enrollment.program_tier})` 
                                    : enrollment.program_name)
                                : enrollment.program_id}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Calendar */}
                    {attendanceSelectedProgram && (
                      <div className="calendar-container" style={{ marginBottom: '20px' }}>
                        <div className="calendar-header">
                          <button
                            className="calendar-nav-btn"
                            onClick={async () => {
                              const newMonth = new Date(attendanceCalendarMonth);
                              newMonth.setMonth(newMonth.getMonth() - 1);
                              setAttendanceCalendarMonth(newMonth);
                              
                              // Fetch sessions for the new month range
                              if (attendanceSelectedProgram && selectedStudentDetail) {
                                try {
                                  const year = newMonth.getFullYear();
                                  const month = newMonth.getMonth();
                                  const firstDay = new Date(year, month - 3, 1);
                                  const lastDay = new Date(year, month + 4, 0);
                                  const fromDate = firstDay.toISOString().split('T')[0];
                                  const toDate = lastDay.toISOString().split('T')[0];
                                  
                                  const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                    params: {
                                      program_id: attendanceSelectedProgram,
                                      from_date: fromDate,
                                      to_date: toDate
                                    }
                                  });
                                  
                                  setAttendanceScheduledSessions(sessionsResponse.data || []);
                                } catch (err: any) {
                                  console.error('Error fetching scheduled sessions:', err);
                                  setAttendanceScheduledSessions([]);
                                }
                              }
                            }}
                            title="Previous Month"
                          >
                            ←
                          </button>
                          <div className="calendar-month-year">
                            {attendanceCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </div>
                          <button
                            className="calendar-nav-btn"
                            onClick={async () => {
                              const newMonth = new Date(attendanceCalendarMonth);
                              newMonth.setMonth(newMonth.getMonth() + 1);
                              setAttendanceCalendarMonth(newMonth);
                              
                              // Fetch sessions for the new month range
                              if (attendanceSelectedProgram && selectedStudentDetail) {
                                try {
                                  const year = newMonth.getFullYear();
                                  const month = newMonth.getMonth();
                                  const firstDay = new Date(year, month - 3, 1);
                                  const lastDay = new Date(year, month + 4, 0);
                                  const fromDate = firstDay.toISOString().split('T')[0];
                                  const toDate = lastDay.toISOString().split('T')[0];
                                  
                                  const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                    params: {
                                      program_id: attendanceSelectedProgram,
                                      from_date: fromDate,
                                      to_date: toDate
                                    }
                                  });
                                  
                                  setAttendanceScheduledSessions(sessionsResponse.data || []);
                                } catch (err: any) {
                                  console.error('Error fetching scheduled sessions:', err);
                                  setAttendanceScheduledSessions([]);
                                }
                              }
                            }}
                            title="Next Month"
                          >
                            →
                          </button>
                        </div>
                        
                        <div className="calendar-day-headers">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="calendar-day-header">{day}</div>
                          ))}
                        </div>
                        
                        <div className="calendar-days-grid">
                          {(() => {
                            const year = attendanceCalendarMonth.getFullYear();
                            const month = attendanceCalendarMonth.getMonth();
                            
                            // Get first day of month and how many days to show before it
                            const firstDay = new Date(year, month, 1);
                            const startDate = new Date(firstDay);
                            startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
                            
                            // Generate 42 days (6 weeks)
                            const days: Date[] = [];
                            for (let i = 0; i < 42; i++) {
                              const date = new Date(startDate);
                              date.setDate(startDate.getDate() + i);
                              days.push(new Date(date));
                            }
                            
                            return days.map((date, index) => {
                              const isCurrentMonth = date.getMonth() === month;
                              const isToday = date.toDateString() === new Date().toDateString();
                              const isSelected = attendanceSelectedDates.some(d => d.toDateString() === date.toDateString());
                              
                              // Check if this date already has attendance marked
                              // Compare dates as YYYY-MM-DD strings to avoid timezone issues
                              // Show attendance for ALL programs, not just the selected one
                              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                              const hasAttendance = studentAttendance.some((record: any) => {
                                if (!record.attendance_date) return false;
                                // attendance_date is already YYYY-MM-DD format from database
                                const recordDateStr = typeof record.attendance_date === 'string' 
                                  ? record.attendance_date.split('T')[0] // Handle ISO strings if any
                                  : new Date(record.attendance_date).toISOString().split('T')[0];
                                // Show attendance for any program, not just the selected one
                                return recordDateStr === dateStr;
                              });
                              
                              // Check if attendance is for the selected program specifically
                              const hasAttendanceForSelectedProgram = attendanceSelectedProgram && studentAttendance.some((record: any) => {
                                if (!record.attendance_date) return false;
                                const recordDateStr = typeof record.attendance_date === 'string' 
                                  ? record.attendance_date.split('T')[0]
                                  : new Date(record.attendance_date).toISOString().split('T')[0];
                                return recordDateStr === dateStr && 
                                       record.program_id === attendanceSelectedProgram;
                              });
                              
                              // Check if this date has a scheduled session
                              const hasScheduledSession = attendanceScheduledSessions.some((session: any) => {
                                if (!session.date_available) return false;
                                const sessionDateStr = typeof session.date_available === 'string' 
                                  ? session.date_available.split('T')[0]
                                  : new Date(session.date_available).toISOString().split('T')[0];
                                return sessionDateStr === dateStr;
                              });
                              
                              return (
                                <div
                                  key={index}
                                  className={`calendar-day-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasAttendanceForSelectedProgram ? 'has-attendance' : ''} ${hasScheduledSession ? 'has-scheduled-session' : ''}`}
                                  onClick={() => {
                                    if (isSelected) {
                                      setAttendanceSelectedDates(attendanceSelectedDates.filter(d => d.toDateString() !== date.toDateString()));
                                    } else {
                                      setAttendanceSelectedDates([...attendanceSelectedDates, date]);
                                    }
                                  }}
                                  title={date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) + (hasScheduledSession ? ' - Scheduled Session' : '') + (hasAttendanceForSelectedProgram ? ' - Attendance Marked for Selected Program' : hasAttendance ? ' - Attendance Marked (Other Program)' : '')}
                                  style={{
                                    position: 'relative'
                                  }}
                                >
                                  <span className="calendar-day-number">{date.getDate()}</span>
                                  {hasScheduledSession && !hasAttendanceForSelectedProgram && (
                                    <span style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: '2px',
                                      fontSize: '10px',
                                      color: '#1976d2',
                                      fontWeight: 'bold'
                                    }}>📅</span>
                                  )}
                                  {hasAttendanceForSelectedProgram && (
                                    <span style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: hasScheduledSession ? '14px' : '2px',
                                      fontSize: '10px',
                                      color: '#2e7d32',
                                      fontWeight: 'bold'
                                    }}>✓</span>
                                  )}
                                  {hasAttendance && !hasAttendanceForSelectedProgram && (
                                    <span style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: hasScheduledSession ? '14px' : '2px',
                                      fontSize: '8px',
                                      color: '#999',
                                      fontWeight: 'bold'
                                    }}>○</span>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {/* Mark Attendance Button */}
                    {attendanceSelectedProgram && attendanceSelectedDates.length > 0 && (
                      <div style={{ marginTop: '15px' }}>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!attendanceSelectedProgram || attendanceSelectedDates.length === 0) {
                              alert('Please select a program and at least one date');
                              return;
                            }
                            
                            setIsMarkingAttendance(true);
                            try {
                              let successCount = 0;
                              let errorCount = 0;
                              
                              for (const date of attendanceSelectedDates) {
                                try {
                                  const dateStr = date.toISOString().split('T')[0];
                                  
                                  // Find a session for this date and program, or create attendance directly
                                  // First, try to find an existing session
                                  const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                    params: {
                                      program_id: attendanceSelectedProgram,
                                      from_date: dateStr,
                                      to_date: dateStr
                                    }
                                  });
                                  
                                  const sessions = sessionsResponse.data || [];
                                  let sessionId = null;
                                  
                                  if (sessions.length > 0) {
                                    sessionId = sessions[0].id;
                                  }
                                  
                                  // Mark attendance using the mark-attendance endpoint
                                  if (sessionId) {
                                    await axios.post(`${config.BACKEND_URL}/api/attendance/mark-attendance`, {
                                      session_id: sessionId,
                                      attendance_date: dateStr,
                                      attendance_data: [{
                                        student_id: selectedStudentDetail.id,
                                        status: 'present',
                                        notes: 'Marked via student detail view'
                                      }]
                                    });
                                  } else {
                                    // If no session exists, we need to create attendance directly
                                    // This requires program_id, so we'll use a direct API call
                                    // For now, let's try to create a session first or use an alternative endpoint
                                    console.warn(`No session found for ${dateStr}, skipping...`);
                                    errorCount++;
                                    continue;
                                  }
                                  
                                  successCount++;
                                } catch (error: any) {
                                  console.error(`Error marking attendance for ${date.toDateString()}:`, error);
                                  errorCount++;
                                }
                              }
                              
                              if (successCount > 0) {
                                alert(`Successfully marked attendance for ${successCount} date(s)${errorCount > 0 ? `. ${errorCount} date(s) failed.` : ''}`);
                                // Refresh attendance records
                                const attendanceResponse = await axios.get(`${config.BACKEND_URL}/api/attendance/records`, {
                                  params: { student_id: selectedStudentDetail.id }
                                });
                                setStudentAttendance(attendanceResponse.data.records || []);
                                setAttendanceSelectedDates([]);
                              } else {
                                alert(`Failed to mark attendance. Please ensure sessions exist for the selected dates.`);
                              }
                            } catch (error: any) {
                              console.error('Error marking attendance:', error);
                              alert('Error marking attendance. Please try again.');
                            } finally {
                              setIsMarkingAttendance(false);
                            }
                          }}
                          disabled={isMarkingAttendance}
                          style={{ width: '100%' }}
                        >
                          {isMarkingAttendance 
                            ? `Marking Attendance... (${attendanceSelectedDates.length} date(s))` 
                            : `Mark Present for ${attendanceSelectedDates.length} Selected Date(s)`}
                        </button>
                      </div>
                    )}
                    
                    {!attendanceSelectedProgram && studentEnrollments.length === 0 && (
                      <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                        Student must be enrolled in at least one program to mark attendance.
                      </p>
                    )}
                  </div>

                  {/* Attendance Records */}
                  <div>
                    <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                      Attendance Records ({studentAttendance.length})
                    </h4>
                    {studentAttendance.length === 0 ? (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        color: '#999'
                      }}>
                        <p style={{ margin: 0 }}>No attendance records found for this student.</p>
                      </div>
                    ) : (
                      <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        overflow: 'hidden',
                        maxHeight: '400px',
                        overflowY: 'auto'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Date</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Program</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Status</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Notes</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentAttendance.map((record: any) => (
                              <tr key={record.id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '12px' }}>
                                  {record.attendance_date ? formatDatePSTShort(record.attendance_date) : 'N/A'}
                                </td>
                                <td style={{ padding: '12px', color: '#666' }}>
                                  {record.program_name 
                                    ? (record.program_tier && record.program_tier !== 'all' 
                                        ? `${record.program_name} (${record.program_tier})` 
                                        : record.program_name)
                                    : record.program_id || 'N/A'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {editingAttendanceId === record.id ? (
                                    <select
                                      value={editingAttendanceData?.status || record.status}
                                      onChange={(e) => setEditingAttendanceData({...editingAttendanceData, status: e.target.value})}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd',
                                        fontSize: '0.85em'
                                      }}
                                    >
                                      <option value="present">Present</option>
                                      <option value="absent">Absent</option>
                                      <option value="late">Late</option>
                                      <option value="excused">Excused</option>
                                      <option value="trial">Trial</option>
                                      <option value="not_marked">Not Marked</option>
                                    </select>
                                  ) : (
                                    <span className={`status-badge ${record.status}`} style={{
                                      display: 'inline-block',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '0.85em',
                                      textTransform: 'capitalize',
                                      backgroundColor: record.status === 'present' ? '#e8f5e9' : record.status === 'absent' ? '#ffebee' : '#fff3e0',
                                      color: record.status === 'present' ? '#2e7d32' : record.status === 'absent' ? '#c62828' : '#e65100'
                                    }}>
                                      {record.status}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '12px', color: '#666', fontSize: '0.9em' }}>
                                  {editingAttendanceId === record.id ? (
                                    <input
                                      type="text"
                                      value={editingAttendanceData?.notes || record.notes || ''}
                                      onChange={(e) => setEditingAttendanceData({...editingAttendanceData, notes: e.target.value})}
                                      placeholder="Notes..."
                                      style={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd',
                                        fontSize: '0.9em'
                                      }}
                                    />
                                  ) : (
                                    record.notes || '—'
                                  )}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  {editingAttendanceId === record.id ? (
                                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                      <button
                                        onClick={async () => {
                                          if (!editingAttendanceData) return;
                                          
                                          setIsUpdatingAttendance(true);
                                          try {
                                            let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
                                            
                                            // Check if token is expired and try to refresh
                                            if (token) {
                                              try {
                                                const payload = JSON.parse(atob(token.split('.')[1]));
                                                const exp = payload.exp * 1000;
                                                const now = Date.now();
                                                const timeUntilExpiry = exp - now;
                                                
                                                if (exp < now || timeUntilExpiry < 5 * 60 * 1000) {
                                                  const { fetchAuthSession } = await import('aws-amplify/auth');
                                                  const session = await fetchAuthSession({ forceRefresh: true });
                                                  if (session.tokens?.accessToken) {
                                                    token = session.tokens.accessToken.toString();
                                                    localStorage.setItem('cognitoToken', token);
                                                  } else {
                                                    throw new Error('Unable to refresh token');
                                                  }
                                                }
                                              } catch (refreshError: any) {
                                                console.error('❌ Token refresh failed:', refreshError);
                                                alert('Your session has expired. Please log out and log in again.');
                                                setIsUpdatingAttendance(false);
                                                return;
                                              }
                                            }
                                            
                                            if (!token || token.trim() === '') {
                                              alert('Authentication token not found. Please log out and log in again.');
                                              setIsUpdatingAttendance(false);
                                              return;
                                            }
                                            
                                            await axios.put(
                                              `${config.BACKEND_URL}/api/attendance/records/${record.id}`,
                                              {
                                                status: editingAttendanceData.status,
                                                notes: editingAttendanceData.notes || ''
                                              },
                                              {
                                                headers: {
                                                  'Authorization': `Bearer ${token}`,
                                                  'Content-Type': 'application/json'
                                                }
                                              }
                                            );
                                            
                                            // Refresh attendance records
                                            const attendanceResponse = await axios.get(`${config.BACKEND_URL}/api/attendance/records`, {
                                              params: { student_id: selectedStudentDetail.id }
                                            });
                                            setStudentAttendance(attendanceResponse.data.records || []);
                                            
                                            setEditingAttendanceId(null);
                                            setEditingAttendanceData(null);
                                            alert('Attendance record updated successfully!');
                                          } catch (error: any) {
                                            console.error('Error updating attendance:', error);
                                            const errorMessage = error.response?.data?.detail || error.message || 'Failed to update attendance record';
                                            alert(`Error: ${errorMessage}`);
                                          } finally {
                                            setIsUpdatingAttendance(false);
                                          }
                                        }}
                                        disabled={isUpdatingAttendance}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: '#28a745',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: isUpdatingAttendance ? 'not-allowed' : 'pointer',
                                          fontSize: '0.8em'
                                        }}
                                      >
                                        ✓ Save
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingAttendanceId(null);
                                          setEditingAttendanceData(null);
                                        }}
                                        disabled={isUpdatingAttendance}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: '#6c757d',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: isUpdatingAttendance ? 'not-allowed' : 'pointer',
                                          fontSize: '0.8em'
                                        }}
                                      >
                                        ✕ Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                      <button
                                        onClick={() => {
                                          setEditingAttendanceId(record.id);
                                          setEditingAttendanceData({
                                            status: record.status,
                                            notes: record.notes || ''
                                          });
                                        }}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: '#007bff',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '0.8em'
                                        }}
                                        title="Edit attendance record"
                                      >
                                        ✏️ Edit
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (!window.confirm(`Are you sure you want to delete this attendance record for ${formatDatePSTShort(record.attendance_date)}?`)) {
                                            return;
                                          }
                                          
                                          setDeletingAttendanceId(record.id);
                                          try {
                                            let token = localStorage.getItem('cognitoToken') || localStorage.getItem('token');
                                            
                                            // Check if token is expired and try to refresh
                                            if (token) {
                                              try {
                                                const payload = JSON.parse(atob(token.split('.')[1]));
                                                const exp = payload.exp * 1000;
                                                const now = Date.now();
                                                const timeUntilExpiry = exp - now;
                                                
                                                if (exp < now || timeUntilExpiry < 5 * 60 * 1000) {
                                                  const { fetchAuthSession } = await import('aws-amplify/auth');
                                                  const session = await fetchAuthSession({ forceRefresh: true });
                                                  if (session.tokens?.accessToken) {
                                                    token = session.tokens.accessToken.toString();
                                                    localStorage.setItem('cognitoToken', token);
                                                  } else {
                                                    throw new Error('Unable to refresh token');
                                                  }
                                                }
                                              } catch (refreshError: any) {
                                                console.error('❌ Token refresh failed:', refreshError);
                                                alert('Your session has expired. Please log out and log in again.');
                                                setDeletingAttendanceId(null);
                                                return;
                                              }
                                            }
                                            
                                            if (!token || token.trim() === '') {
                                              alert('Authentication token not found. Please log out and log in again.');
                                              setDeletingAttendanceId(null);
                                              return;
                                            }
                                            
                                            await axios.delete(
                                              `${config.BACKEND_URL}/api/attendance/records/${record.id}`,
                                              {
                                                headers: {
                                                  'Authorization': `Bearer ${token}`
                                                }
                                              }
                                            );
                                            
                                            // Refresh attendance records
                                            const attendanceResponse = await axios.get(`${config.BACKEND_URL}/api/attendance/records`, {
                                              params: { student_id: selectedStudentDetail.id }
                                            });
                                            setStudentAttendance(attendanceResponse.data.records || []);
                                            
                                            // Refresh calendar if program is selected
                                            if (attendanceSelectedProgram) {
                                              const year = attendanceCalendarMonth.getFullYear();
                                              const month = attendanceCalendarMonth.getMonth();
                                              const firstDay = new Date(year, month - 3, 1);
                                              const lastDay = new Date(year, month + 4, 0);
                                              const fromDate = firstDay.toISOString().split('T')[0];
                                              const toDate = lastDay.toISOString().split('T')[0];
                                              
                                              try {
                                                const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                                  params: {
                                                    program_id: attendanceSelectedProgram,
                                                    from_date: fromDate,
                                                    to_date: toDate
                                                  }
                                                });
                                                setAttendanceScheduledSessions(sessionsResponse.data || []);
                                              } catch (err: any) {
                                                console.error('Error refreshing scheduled sessions:', err);
                                              }
                                            }
                                            
                                            alert('Attendance record deleted successfully!');
                                          } catch (error: any) {
                                            console.error('Error deleting attendance:', error);
                                            const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete attendance record';
                                            alert(`Error: ${errorMessage}`);
                                          } finally {
                                            setDeletingAttendanceId(null);
                                          }
                                        }}
                                        disabled={deletingAttendanceId === record.id}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: deletingAttendanceId === record.id ? '#ccc' : '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: deletingAttendanceId === record.id ? 'not-allowed' : 'pointer',
                                          fontSize: '0.8em'
                                        }}
                                        title="Delete attendance record"
                                      >
                                        {deletingAttendanceId === record.id ? 'Deleting...' : '🗑️ Delete'}
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Update Schedule Section */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                      Update Schedule
                    </h4>
                    
                    {/* Program Selection */}
                    {studentEnrollments.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#666' }}>
                          Select Program:
                        </label>
                        <select
                          value={scheduleSelectedProgram}
                          onChange={async (e) => {
                            setScheduleSelectedProgram(e.target.value);
                            setScheduleSelectedDates([]);
                            setAvailableSessionsForDates([]);
                            
                            // Fetch available sessions for the selected program
                            if (e.target.value && selectedStudentDetail) {
                              try {
                                const year = scheduleCalendarMonth.getFullYear();
                                const month = scheduleCalendarMonth.getMonth();
                                const firstDay = new Date(year, month, 1);
                                const lastDay = new Date(year, month + 1, 0);
                                const fromDate = firstDay.toISOString().split('T')[0];
                                const toDate = lastDay.toISOString().split('T')[0];
                                
                                const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                  params: {
                                    program_id: e.target.value,
                                    from_date: fromDate,
                                    to_date: toDate
                                  }
                                });
                                
                                setAvailableSessionsForDates(sessionsResponse.data || []);
                              } catch (err: any) {
                                console.error('Error fetching available sessions:', err);
                                setAvailableSessionsForDates([]);
                              }
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">-- Select a program --</option>
                          {studentEnrollments.map((enrollment: any) => (
                            <option key={enrollment.id} value={enrollment.program_id}>
                              {enrollment.program_name 
                                ? (enrollment.program_tier && enrollment.program_tier !== 'all' 
                                    ? `${enrollment.program_name} (${enrollment.program_tier})` 
                                    : enrollment.program_name)
                                : enrollment.program_id}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Calendar */}
                    {scheduleSelectedProgram && (
                      <div className="calendar-container" style={{ 
                        marginBottom: '20px', 
                        padding: '14px', 
                        maxWidth: '50%',
                        fontSize: '0.9em'
                      }}>
                        <div className="calendar-header" style={{ marginBottom: '10px', paddingBottom: '8px' }}>
                          <button
                            className="calendar-nav-btn"
                            onClick={async () => {
                              const newMonth = new Date(scheduleCalendarMonth);
                              newMonth.setMonth(newMonth.getMonth() - 1);
                              setScheduleCalendarMonth(newMonth);
                              
                              // Fetch sessions for the new month
                              if (scheduleSelectedProgram && selectedStudentDetail) {
                                try {
                                  const year = newMonth.getFullYear();
                                  const month = newMonth.getMonth();
                                  const firstDay = new Date(year, month, 1);
                                  const lastDay = new Date(year, month + 1, 0);
                                  const fromDate = firstDay.toISOString().split('T')[0];
                                  const toDate = lastDay.toISOString().split('T')[0];
                                  
                                  const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                    params: {
                                      program_id: scheduleSelectedProgram,
                                      from_date: fromDate,
                                      to_date: toDate
                                    }
                                  });
                                  
                                  setAvailableSessionsForDates(sessionsResponse.data || []);
                                } catch (err: any) {
                                  console.error('Error fetching available sessions:', err);
                                  setAvailableSessionsForDates([]);
                                }
                              }
                            }}
                            title="Previous Month"
                          >
                            ←
                          </button>
                          <div className="calendar-month-year" style={{ fontSize: '1.1rem' }}>
                            {scheduleCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </div>
                          <button
                            className="calendar-nav-btn"
                            onClick={async () => {
                              const newMonth = new Date(scheduleCalendarMonth);
                              newMonth.setMonth(newMonth.getMonth() + 1);
                              setScheduleCalendarMonth(newMonth);
                              
                              // Fetch sessions for the new month
                              if (scheduleSelectedProgram && selectedStudentDetail) {
                                try {
                                  const year = newMonth.getFullYear();
                                  const month = newMonth.getMonth();
                                  const firstDay = new Date(year, month, 1);
                                  const lastDay = new Date(year, month + 1, 0);
                                  const fromDate = firstDay.toISOString().split('T')[0];
                                  const toDate = lastDay.toISOString().split('T')[0];
                                  
                                  const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                    params: {
                                      program_id: scheduleSelectedProgram,
                                      from_date: fromDate,
                                      to_date: toDate
                                    }
                                  });
                                  
                                  setAvailableSessionsForDates(sessionsResponse.data || []);
                                } catch (err: any) {
                                  console.error('Error fetching available sessions:', err);
                                  setAvailableSessionsForDates([]);
                                }
                              }
                            }}
                            title="Next Month"
                          >
                            →
                          </button>
                        </div>
                        
                        <div className="calendar-day-headers">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="calendar-day-header">{day}</div>
                          ))}
                        </div>
                        
                        <div className="calendar-days-grid">
                          {(() => {
                            const year = scheduleCalendarMonth.getFullYear();
                            const month = scheduleCalendarMonth.getMonth();
                            
                            // Get first day of month and how many days to show before it
                            const firstDay = new Date(year, month, 1);
                            const startDate = new Date(firstDay);
                            startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
                            
                            // Generate 42 days (6 weeks)
                            const days: Date[] = [];
                            for (let i = 0; i < 42; i++) {
                              const date = new Date(startDate);
                              date.setDate(startDate.getDate() + i);
                              days.push(new Date(date));
                            }
                            
                            return days.map((date, index) => {
                              const isCurrentMonth = date.getMonth() === month;
                              const isToday = date.toDateString() === new Date().toDateString();
                              const isSelected = scheduleSelectedDates.some(d => d.toDateString() === date.toDateString());
                              
                              // Compare dates as YYYY-MM-DD strings to avoid timezone issues
                              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                              
                              // Check if this date has an available session for the selected program
                              const hasAvailableSession = availableSessionsForDates.some((session: any) => {
                                if (!session.date_available) return false;
                                const sessionDateStr = typeof session.date_available === 'string' 
                                  ? session.date_available.split('T')[0]
                                  : new Date(session.date_available).toISOString().split('T')[0];
                                return sessionDateStr === dateStr;
                              });
                              
                              // Check if this date already has a scheduled session
                              const hasSchedule = studentSchedule.some((record: any) => {
                                if (!record.schedule_date) return false;
                                const recordDateStr = typeof record.schedule_date === 'string' 
                                  ? record.schedule_date.split('T')[0]
                                  : new Date(record.schedule_date).toISOString().split('T')[0];
                                return recordDateStr === dateStr && 
                                       record.program_id === scheduleSelectedProgram;
                              });
                              
                              // Only enable days that have available sessions (and are in current month)
                              const isEnabled = isCurrentMonth && hasAvailableSession;
                              
                              return (
                                <div
                                  key={index}
                                  className={`calendar-day-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasSchedule ? 'has-session' : ''} ${!isEnabled ? 'disabled' : ''}`}
                                  onClick={() => {
                                    if (!isEnabled) return;
                                    if (isSelected) {
                                      setScheduleSelectedDates(scheduleSelectedDates.filter(d => d.toDateString() !== date.toDateString()));
                                    } else {
                                      setScheduleSelectedDates([...scheduleSelectedDates, date]);
                                    }
                                  }}
                                  title={isEnabled 
                                    ? date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                                    : 'No session available for this date'}
                                  style={{
                                    position: 'relative',
                                    cursor: isEnabled ? 'pointer' : 'not-allowed',
                                    opacity: isEnabled ? 1 : 0.4,
                                    backgroundColor: !isEnabled ? '#f5f5f5' : undefined,
                                    minHeight: '30px',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  <span className="calendar-day-number" style={{ fontSize: '0.85rem' }}>{date.getDate()}</span>
                                  {hasSchedule && (
                                    <span style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: '2px',
                                      fontSize: '8px',
                                      color: '#1976d2',
                                      fontWeight: 'bold'
                                    }}>📅</span>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {/* Add/Remove Schedule Buttons */}
                    {scheduleSelectedProgram && scheduleSelectedDates.length > 0 && (
                      <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!scheduleSelectedProgram || scheduleSelectedDates.length === 0 || !selectedStudentDetail) {
                              alert('Please select a program and at least one date');
                              return;
                            }
                            
                            setIsUpdatingSchedule(true);
                            try {
                              let successCount = 0;
                              let errorCount = 0;
                              
                              // Find enrollment for this program
                              const enrollment = studentEnrollments.find((e: any) => 
                                e.program_id === scheduleSelectedProgram && e.status === 'active'
                              );
                              
                              if (!enrollment) {
                                alert('No active enrollment found for this program. Please enroll the student first.');
                                setIsUpdatingSchedule(false);
                                return;
                              }
                              
                              // Fetch sessions for all selected dates (not just current month)
                              const allSelectedDates = scheduleSelectedDates.map(d => d.toISOString().split('T')[0]).sort();
                              const minDate = allSelectedDates[0];
                              const maxDate = allSelectedDates[allSelectedDates.length - 1];
                              
                              let sessionsForSelectedDates: any[] = [];
                              try {
                                const sessionsResponse = await axios.get(`${config.BACKEND_URL}/api/class-sessions/`, {
                                  params: {
                                    program_id: scheduleSelectedProgram,
                                    from_date: minDate,
                                    to_date: maxDate
                                  }
                                });
                                sessionsForSelectedDates = sessionsResponse.data || [];
                                console.log(`📅 Fetched ${sessionsForSelectedDates.length} sessions for date range ${minDate} to ${maxDate}`);
                              } catch (err: any) {
                                console.error('Error fetching sessions for selected dates:', err);
                                // Fall back to availableSessionsForDates
                                sessionsForSelectedDates = availableSessionsForDates;
                              }
                              
                              // Collect all session IDs for selected dates
                              const sessionIds: string[] = [];
                              
                              for (const date of scheduleSelectedDates) {
                                try {
                                  const dateStr = date.toISOString().split('T')[0];
                                  
                                  // Find session from fetched sessions (for all selected dates)
                                  const session = sessionsForSelectedDates.find((s: any) => {
                                    if (!s.date_available) return false;
                                    const sessionDateStr = typeof s.date_available === 'string' 
                                      ? s.date_available.split('T')[0]
                                      : new Date(s.date_available).toISOString().split('T')[0];
                                    return sessionDateStr === dateStr;
                                  });
                                  
                                  if (!session) {
                                    console.warn(`No session found for ${dateStr}, skipping...`);
                                    errorCount++;
                                    continue;
                                  }
                                  
                                  // Check if student is already enrolled in this session
                                  const alreadyEnrolled = studentSchedule.some((record: any) => 
                                    record.session_id === session.id
                                  );
                                  
                                  if (!alreadyEnrolled) {
                                    sessionIds.push(session.id);
                                  }
                                } catch (error: any) {
                                  console.error(`Error processing date ${date.toDateString()}:`, error);
                                  errorCount++;
                                }
                              }
                              
                              if (sessionIds.length === 0) {
                                alert('No new sessions to add. Student may already be enrolled in all selected dates.');
                                setIsUpdatingSchedule(false);
                                return;
                              }
                              
                              // Enroll student in all sessions at once
                              try {
                                await axios.post(`${config.BACKEND_URL}/api/course-registrations`, {
                                  studentId: selectedStudentDetail.id,
                                  programId: scheduleSelectedProgram,
                                  selectedSessions: sessionIds
                                });
                                
                                successCount = sessionIds.length;
                              } catch (error: any) {
                                console.error('Error enrolling in sessions:', error);
                                errorCount = sessionIds.length;
                              }
                              
                              if (successCount > 0) {
                                alert(`Successfully added ${successCount} session(s) to schedule${errorCount > 0 ? `. ${errorCount} failed.` : ''}`);
                                // Refresh schedule
                                const scheduleResponse = await axios.get(`${config.BACKEND_URL}/api/students/${selectedStudentDetail.id}/schedule`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('cognitoToken') || localStorage.getItem('token') || ''}`
                                  }
                                });
                                setStudentSchedule(scheduleResponse.data.schedule || []);
                                setScheduleSelectedDates([]);
                              } else {
                                alert(`Failed to add sessions. Please ensure sessions exist for the selected dates.`);
                              }
                            } catch (error: any) {
                              console.error('Error updating schedule:', error);
                              alert('Error updating schedule. Please try again.');
                            } finally {
                              setIsUpdatingSchedule(false);
                            }
                          }}
                          disabled={isUpdatingSchedule}
                          style={{ flex: 1 }}
                        >
                          {isUpdatingSchedule 
                            ? `Adding Sessions... (${scheduleSelectedDates.length} date(s))` 
                            : `Add Sessions for ${scheduleSelectedDates.length} Selected Date(s)`}
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={async () => {
                            if (!scheduleSelectedProgram || scheduleSelectedDates.length === 0 || !selectedStudentDetail) {
                              alert('Please select a program and at least one date');
                              return;
                            }
                            
                            if (!window.confirm(`Remove scheduled sessions for ${scheduleSelectedDates.length} selected date(s)?`)) {
                              return;
                            }
                            
                            setIsUpdatingSchedule(true);
                            try {
                              let successCount = 0;
                              let errorCount = 0;
                              
                              for (const date of scheduleSelectedDates) {
                                try {
                                  const dateStr = date.toISOString().split('T')[0];
                                  
                                  // Find scheduled sessions for this date and program
                                  const scheduledSessions = studentSchedule.filter((record: any) => {
                                    if (!record.schedule_date || record.program_id !== scheduleSelectedProgram) return false;
                                    const recordDateStr = typeof record.schedule_date === 'string' 
                                      ? record.schedule_date.split('T')[0]
                                      : new Date(record.schedule_date).toISOString().split('T')[0];
                                    return recordDateStr === dateStr;
                                  });
                                  
                                  // Remove each scheduled session
                                  for (const scheduledSession of scheduledSessions) {
                                    if (scheduledSession.session_id) {
                                      // Delete student_session record
                                      // Note: We'll need a DELETE endpoint for this, for now we'll skip
                                      console.warn('Session removal requires DELETE endpoint');
                                      // TODO: Implement DELETE endpoint for student_sessions
                                    }
                                  }
                                  
                                  if (scheduledSessions.length > 0) {
                                    successCount++;
                                  }
                                } catch (error: any) {
                                  console.error(`Error removing schedule for ${date.toDateString()}:`, error);
                                  errorCount++;
                                }
                              }
                              
                              if (successCount > 0) {
                                alert(`Successfully removed ${successCount} session(s) from schedule${errorCount > 0 ? `. ${errorCount} failed.` : ''}`);
                                // Refresh schedule
                                const scheduleResponse = await axios.get(`${config.BACKEND_URL}/api/students/${selectedStudentDetail.id}/schedule`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('cognitoToken') || localStorage.getItem('token') || ''}`
                                  }
                                });
                                setStudentSchedule(scheduleResponse.data.schedule || []);
                                setScheduleSelectedDates([]);
                              } else {
                                alert(`No scheduled sessions found for selected dates.`);
                              }
                            } catch (error: any) {
                              console.error('Error removing schedule:', error);
                              alert('Error removing schedule. Please try again.');
                            } finally {
                              setIsUpdatingSchedule(false);
                            }
                          }}
                          disabled={isUpdatingSchedule}
                          style={{ flex: 1 }}
                        >
                          {isUpdatingSchedule 
                            ? `Removing...` 
                            : `Remove Sessions`}
                        </button>
                      </div>
                    )}
                    
                    {!scheduleSelectedProgram && studentEnrollments.length === 0 && (
                      <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                        Student must be enrolled in at least one program to manage schedule.
                      </p>
                    )}
                  </div>

                  {/* Scheduled Sessions */}
                  <div>
                    <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                      Scheduled Sessions ({studentSchedule.length})
                    </h4>
                    {studentSchedule.length === 0 ? (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        color: '#999'
                      }}>
                        <p style={{ margin: 0 }}>No scheduled sessions found for this student.</p>
                      </div>
                    ) : (
                      <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        overflow: 'hidden',
                        maxHeight: '400px',
                        overflowY: 'auto'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Date</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Program</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Time</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Status</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Notes</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentSchedule.map((record: any) => (
                              <tr key={record.id || record.session_id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '12px' }}>
                                  {record.schedule_date ? formatDatePSTShort(record.schedule_date) : 'N/A'}
                                </td>
                                <td style={{ padding: '12px', color: '#666' }}>
                                  {record.program_name || record.program_id || 'N/A'}
                                </td>
                                <td style={{ padding: '12px', color: '#666' }}>
                                  {record.start_time && record.end_time 
                                    ? `${new Date(`2000-01-01T${record.start_time}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(`2000-01-01T${record.end_time}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                    : 'N/A'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <span className={`status-badge ${record.enrollment_status || 'enrolled'}`} style={{
                                    display: 'inline-block',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.85em',
                                    textTransform: 'capitalize',
                                    backgroundColor: record.enrollment_status === 'enrolled' ? '#e3f2fd' : record.enrollment_status === 'completed' ? '#e8f5e9' : '#fff3e0',
                                    color: record.enrollment_status === 'enrolled' ? '#1976d2' : record.enrollment_status === 'completed' ? '#2e7d32' : '#e65100'
                                  }}>
                                    {record.enrollment_status || 'enrolled'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px', color: '#666', fontSize: '0.9em' }}>
                                  {record.comments || record.session_description || '—'}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  {record.enrollment_status === 'enrolled' && (
                                    <button
                                      onClick={() => handleUnenrollFromSession(record.id || record.student_session_id, record.schedule_date, record.program_name)}
                                      disabled={unenrollingSessionId === (record.id || record.student_session_id)}
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: unenrollingSessionId === (record.id || record.student_session_id) ? '#ccc' : '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: unenrollingSessionId === (record.id || record.student_session_id) ? 'not-allowed' : 'pointer',
                                        fontSize: '0.85em',
                                        fontWeight: '500'
                                      }}
                                      title={`Remove student from session on ${record.schedule_date ? formatDatePSTShort(record.schedule_date) : 'this date'}`}
                                    >
                                      {unenrollingSessionId === (record.id || record.student_session_id) ? 'Removing...' : 'Unenroll'}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowStudentDetailModal(false);
                  setSelectedStudentDetail(null);
                  setStudentEnrollments([]);
                  setStudentAttendance([]);
                  setAttendanceSelectedProgram('');
                  setAttendanceSelectedDates([]);
                  setAttendanceCalendarMonth(new Date());
                  setScheduleSelectedDates([]);
                  setScheduleCalendarMonth(new Date());
                  setScheduleSelectedProgram('');
                  setStudentSchedule([]);
                  setShowEnrollmentForm(false);
                  setEnrollmentProgramId('');
                  setEnrollmentDate(new Date().toISOString().split('T')[0]);
                  setAvailablePrograms([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Detail Modal */}
      {showGroupDetailModal && selectedGroupDetail && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal group-detail-modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, borderBottom: '2px solid #eee' }}>
              <h3>Group Details</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowGroupDetailModal(false);
                  setSelectedGroupDetail(null);
                  setGroupDetailMembers([]);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {groupDetailLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading group details...</p>
                </div>
              ) : (
                <>
                  {/* Group Information */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#2c3e50' }}>Group Information</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Name:</strong>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>{selectedGroupDetail.name}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Type:</strong>
                        <span>{selectedGroupDetail.group_type || <span style={{ color: '#999', fontStyle: 'italic' }}>Not specified</span>}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Status:</strong>
                        <span className={`status-badge ${selectedGroupDetail.status}`} style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85em',
                          textTransform: 'capitalize'
                        }}>
                          {selectedGroupDetail.status}
                        </span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Created By:</strong>
                        <span>{selectedGroupDetail.creator_name || 'System'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Created:</strong>
                        <span>{selectedGroupDetail.created_at ? new Date(selectedGroupDetail.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Last Updated:</strong>
                        <span>{selectedGroupDetail.updated_at ? new Date(selectedGroupDetail.updated_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'N/A'}</span>
                      </div>
                    </div>
                    {selectedGroupDetail.description && (
                      <div style={{ marginTop: '15px' }}>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Description:</strong>
                        <p style={{ margin: 0, color: '#333', lineHeight: '1.6' }}>{selectedGroupDetail.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Group Members */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0, color: '#2c3e50' }}>
                        Members ({groupDetailMembers.length})
                      </h4>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setShowGroupDetailModal(false);
                          handleAddMembersToGroup(selectedGroupDetail);
                        }}
                        style={{ padding: '6px 12px', fontSize: '0.85em' }}
                      >
                        👥 Manage Members
                      </button>
                    </div>
                    
                    {groupDetailMembers.length === 0 ? (
                      <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        color: '#999'
                      }}>
                        <p style={{ margin: 0 }}>No members in this group yet.</p>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setShowGroupDetailModal(false);
                            handleAddMembersToGroup(selectedGroupDetail);
                          }}
                          style={{ marginTop: '15px' }}
                        >
                          Add Members
                        </button>
                      </div>
                    ) : (
                      <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Name</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Tier</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Grade</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>Age</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupDetailMembers.map((member) => (
                              <tr key={member.student_id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '12px', fontWeight: '500' }}>
                                  {member.full_name}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <span className={`tier-badge ${member.tier}`} style={{
                                    display: 'inline-block',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.85em',
                                    textTransform: 'capitalize'
                                  }}>
                                    {member.tier}
                                  </span>
                                </td>
                                <td style={{ padding: '12px', color: '#666' }}>
                                  {member.grade_level}
                                </td>
                                <td style={{ padding: '12px', color: '#666' }}>
                                  {member.age || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowGroupDetailModal(false);
                  setSelectedGroupDetail(null);
                  setGroupDetailMembers([]);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowGroupDetailModal(false);
                  handleEditGroup(selectedGroupDetail);
                }}
              >
                ✏️ Edit Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Members to Group Modal */}
      {showAddMembersModal && selectedGroupForMembers && (
        <div className="modal-overlay">
          <div className="modal add-members-modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Manage Group Members</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAddMembersModal(false);
                  setSelectedGroupForMembers(null);
                  setSelectedMemberIds([]);
                  setMemberSearchTerm('');
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <strong>Group:</strong> {selectedGroupForMembers.name}
                {selectedGroupForMembers.group_type && (
                  <span style={{ marginLeft: '10px', color: '#666' }}>({selectedGroupForMembers.group_type})</span>
                )}
                <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                  Current members: {groupMembers.length}
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px', display: 'block' }}>
                  Select Students
                </label>
                
                {/* Search Field */}
                <div style={{ 
                  marginBottom: '15px',
                  position: 'relative'
                }}>
                  <input
                    type="text"
                    placeholder="🔍 Search students by name, email, grade, or tier..."
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 40px 12px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.3s',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#4caf50';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ddd';
                    }}
                  />
                  {memberSearchTerm && (
                    <button
                      onClick={() => setMemberSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#999',
                        padding: '0',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                <div style={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '10px'
                }}>
                  {(() => {
                    // Filter students based on search term
                    const filteredStudents = availableStudents.filter(student => {
                      if (!memberSearchTerm.trim()) return true;
                      const searchLower = memberSearchTerm.toLowerCase();
                      const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
                      const grade = student.grade_level?.toLowerCase() || '';
                      const tier = student.tier?.toLowerCase() || '';
                      const email = student.email?.toLowerCase() || '';
                      
                      return fullName.includes(searchLower) || 
                             grade.includes(searchLower) || 
                             tier.includes(searchLower) ||
                             email.includes(searchLower);
                    });
                    
                    if (filteredStudents.length === 0) {
                      return (
                        <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                          {memberSearchTerm.trim() 
                            ? `No students found matching "${memberSearchTerm}"` 
                            : 'No students available'}
                        </p>
                      );
                    }
                    
                    return filteredStudents.map((student) => {
                      const isMember = groupMembers.some(m => m.student_id === student.id);
                      const isSelected = selectedMemberIds.includes(student.id);
                      return (
                        <label 
                          key={student.id} 
                          style={{ 
                            display: 'block', 
                            padding: '10px', 
                            marginBottom: '8px',
                            backgroundColor: isSelected ? '#e8f5e9' : '#fff',
                            border: `1px solid ${isSelected ? '#4caf50' : '#ddd'}`,
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMemberIds([...selectedMemberIds, student.id]);
                              } else {
                                setSelectedMemberIds(selectedMemberIds.filter(id => id !== student.id));
                              }
                            }}
                            style={{ marginRight: '10px' }}
                          />
                          <span style={{ fontWeight: '500' }}>
                            {student.first_name} {student.last_name}
                          </span>
                          <span style={{ color: '#666', marginLeft: '10px' }}>
                            ({student.tier}, Age: {student.age}, Grade: {student.grade_level})
                          </span>
                          {isMember && (
                            <span style={{ 
                              marginLeft: '10px', 
                              color: '#2e7d32', 
                              fontSize: '0.85em',
                              fontStyle: 'italic'
                            }}>
                              (Current member)
                            </span>
                          )}
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>

              <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddMembersModal(false);
                    setSelectedGroupForMembers(null);
                    setSelectedMemberIds([]);
                    setMemberSearchTerm('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveMembersToGroup}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Detail Modal */}
      {showSessionDetailModal && selectedSessionDetail && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal session-detail-modal" style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, borderBottom: '2px solid #eee' }}>
              <h3>Session Details</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowSessionDetailModal(false);
                  setSelectedSessionDetail(null);
                  setSessionDetailStudents([]);
                  setEditingAttendance({});
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {sessionDetailLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading session details...</p>
                </div>
              ) : (
                <>
                  {/* Session Metadata */}
                  <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#2c3e50' }}>Session Information</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Date:</strong>
                        <span>{selectedSessionDetail.date_available ? formatDatePSTLong(selectedSessionDetail.date_available) : 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Time:</strong>
                        <span>
                          {selectedSessionDetail.start_time ? new Date(`2000-01-01T${selectedSessionDetail.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A'}
                          {' - '}
                          {selectedSessionDetail.end_time ? new Date(`2000-01-01T${selectedSessionDetail.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Program:</strong>
                        <span>{selectedSessionDetail.program_name || 'N/A'}</span>
                        {selectedSessionDetail.program_tier && (
                          <span className={`tier-badge ${selectedSessionDetail.program_tier}`} style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75em' }}>
                            {selectedSessionDetail.program_tier}
                          </span>
                        )}
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Course:</strong>
                        <span>{selectedSessionDetail.course_name || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Instructor:</strong>
                        <span>{selectedSessionDetail.instructor_name || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Location:</strong>
                        <span>{selectedSessionDetail.location || 'N/A'}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Enrollment:</strong>
                        <span>{selectedSessionDetail.current_enrollment || 0}/{selectedSessionDetail.max_capacity || 20}</span>
                      </div>
                      <div>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Status:</strong>
                        <span className={`status-badge ${selectedSessionDetail.status}`} style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '0.85em', textTransform: 'capitalize' }}>
                          {selectedSessionDetail.status}
                        </span>
                      </div>
                    </div>
                    {selectedSessionDetail.description && (
                      <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                        <strong style={{ color: '#666', display: 'block', marginBottom: '5px' }}>Description:</strong>
                        <p style={{ margin: 0, color: '#333' }}>{selectedSessionDetail.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Enrolled Students Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0, color: '#2c3e50' }}>Enrolled Students ({sessionDetailStudents.length})</h4>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setShowSessionDetailModal(false);
                          handleAddStudentsToSession(selectedSessionDetail);
                        }}
                        style={{ padding: '8px 16px' }}
                      >
                        ➕ Add Students
                      </button>
                    </div>

                    {sessionDetailStudents.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#999', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <p>No students enrolled in this session.</p>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setShowSessionDetailModal(false);
                            handleAddStudentsToSession(selectedSessionDetail);
                          }}
                          style={{ marginTop: '10px' }}
                        >
                          Add Students
                        </button>
                      </div>
                    ) : (
                      <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ backgroundColor: '#f5f5f5' }}>
                            <tr>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Student</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Tier</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Attendance</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Notes</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionDetailStudents.map((student: any) => {
                              const studentKey = student.student_id || student.id;
                              const currentStatus = editingAttendance[studentKey]?.status || student.attendance_status || 'not_marked';
                              const currentNotes = editingAttendance[studentKey]?.notes !== undefined ? editingAttendance[studentKey].notes : (student.notes || '');
                              
                              return (
                                <tr key={studentKey} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '12px' }}>
                                    <div style={{ fontWeight: '500' }}>{student.full_name}</div>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <span className={`tier-badge ${student.tier}`} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75em', textTransform: 'capitalize' }}>
                                      {student.tier}
                                    </span>
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    <select
                                      value={currentStatus}
                                      onChange={(e) => {
                                        setEditingAttendance(prev => ({
                                          ...prev,
                                          [studentKey]: {
                                            ...prev[studentKey],
                                            status: e.target.value
                                          }
                                        }));
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.9em',
                                        minWidth: '120px'
                                      }}
                                    >
                                      <option value="not_marked">Not Marked</option>
                                      <option value="present">Present</option>
                                      <option value="absent">Absent</option>
                                      <option value="late">Late</option>
                                      <option value="excused">Excused</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: '12px' }}>
                                    <input
                                      type="text"
                                      value={currentNotes}
                                      onChange={(e) => {
                                        setEditingAttendance(prev => ({
                                          ...prev,
                                          [studentKey]: {
                                            ...prev[studentKey],
                                            notes: e.target.value
                                          }
                                        }));
                                      }}
                                      placeholder="Add notes..."
                                      style={{
                                        width: '100%',
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.9em'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'center' }}>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleMarkAttendance(studentKey, currentStatus, currentNotes)}
                                      style={{ padding: '6px 12px', fontSize: '0.85em' }}
                                    >
                                      💾 Save
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ padding: '15px', borderTop: '2px solid #eee', backgroundColor: '#f8f9fa' }}>
                          <button
                            className="btn btn-primary"
                            onClick={handleBulkMarkAttendance}
                            style={{ width: '100%', padding: '10px' }}
                          >
                            💾 Save All Attendance Changes
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSessionDetailModal(false);
                  setSelectedSessionDetail(null);
                  setSessionDetailStudents([]);
                  setEditingAttendance({});
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Form Modal */}
      {showStudentForm && (
        <div className="modal-overlay">
          <div className="modal student-form-modal" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>{selectedStudent ? 'Edit Student' : 'Add New Student'}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowStudentForm(false);
                  setSelectedStudent(null);
                  setStudentFormErrors({});
                  setParentSearchTerm('');
                  setShowParentDropdown(false);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <form onSubmit={async (e) => {
                e.preventDefault();
                setStudentFormErrors({});
                
                // Validation
                const errors: {[key: string]: string} = {};
                if (!studentFormData.firstName.trim()) errors.firstName = 'First name is required';
                if (!studentFormData.lastName.trim()) errors.lastName = 'Last name is required';
                if (!studentFormData.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
                if (!studentFormData.gradeLevel.trim()) errors.gradeLevel = 'Grade level is required';
                if (!studentFormData.parentId) errors.parentId = 'Parent is required';
                
                if (Object.keys(errors).length > 0) {
                  setStudentFormErrors(errors);
                  return;
                }
                
                try {
                  // Format phone number if provided
                  let phone = studentFormData.phone.trim();
                  if (phone && !phone.startsWith('+')) {
                    // Assume Canadian number, add +1
                    const digitsOnly = phone.replace(/\D/g, '');
                    if (digitsOnly.length === 10) {
                      phone = `+1${digitsOnly}`;
                    }
                  }
                  
                  const studentData = {
                    first_name: studentFormData.firstName.trim(),
                    last_name: studentFormData.lastName.trim(),
                    date_of_birth: studentFormData.dateOfBirth,
                    grade_level: studentFormData.gradeLevel.toUpperCase(),
                    email: studentFormData.email.trim() || null,
                    phone: phone || null,
                    address: studentFormData.address.trim() || null,
                    parent_id: studentFormData.parentId,
                    enrollment_date: studentFormData.enrollmentDate || new Date().toISOString().split('T')[0],
                    profile_notes: studentFormData.profileNotes.trim() || null,
                    learning_goals: studentFormData.learningGoals.trim() || null,
                    slack_username: studentFormData.slackUsername.trim() || null,
                    create_login: false // Admin-created students don't get login accounts
                  };
                  
                  console.log('Creating student:', studentData);
                  
                  const response = await axios.post(
                    `${config.BACKEND_URL}/api/students`,
                    studentData
                  );
                  
                  console.log('Student created successfully:', response.data);
                  
                  // Refresh students list
                  await fetchStudents();
                  
                  // Close modal
                  setShowStudentForm(false);
                  setSelectedStudent(null);
                  setStudentFormErrors({});
                  setParentSearchTerm('');
                  setShowParentDropdown(false);
                  
                  alert('Student created successfully! Consent forms will be pending for the parent to complete.');
                } catch (error: any) {
                  console.error('Error creating student:', error);
                  const errorMessage = error.response?.data?.detail || error.message || 'Failed to create student';
                  setError(errorMessage);
                  alert(`Error: ${errorMessage}`);
                }
              }}>
                <div className="form-group">
                  <label htmlFor="parentId">Parent *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="parentSearch"
                      value={parentSearchTerm}
                      onChange={(e) => {
                        setParentSearchTerm(e.target.value);
                        setShowParentDropdown(true);
                      }}
                      onFocus={() => setShowParentDropdown(true)}
                      placeholder="Search parent by name or email..."
                      required
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    {showParentDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        {users
                          .filter(user => user.userType === 'parent')
                          .filter(user => {
                            if (!parentSearchTerm.trim()) return true;
                            const searchLower = parentSearchTerm.toLowerCase();
                            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                            const email = user.email?.toLowerCase() || '';
                            return fullName.includes(searchLower) || email.includes(searchLower);
                          })
                          .map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                setStudentFormData({ ...studentFormData, parentId: user.id });
                                setParentSearchTerm(`${user.firstName} ${user.lastName} (${user.email})`);
                                setShowParentDropdown(false);
                              }}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <div style={{ fontWeight: '500' }}>{user.firstName} {user.lastName}</div>
                              <div style={{ fontSize: '0.85em', color: '#666' }}>{user.email}</div>
                            </div>
                          ))}
                        {users.filter(user => user.userType === 'parent').length === 0 && (
                          <div style={{ padding: '10px', color: '#999', fontStyle: 'italic' }}>
                            No parents found. Please create a parent user first.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {studentFormErrors.parentId && <span style={{ color: 'red', fontSize: '0.85em' }}>{studentFormErrors.parentId}</span>}
                  <input type="hidden" value={studentFormData.parentId} required />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={studentFormData.firstName}
                      onChange={(e) => setStudentFormData({ ...studentFormData, firstName: e.target.value })}
                      required
                      placeholder="Enter first name"
                    />
                    {studentFormErrors.firstName && <span style={{ color: 'red', fontSize: '0.85em' }}>{studentFormErrors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={studentFormData.lastName}
                      onChange={(e) => setStudentFormData({ ...studentFormData, lastName: e.target.value })}
                      required
                      placeholder="Enter last name"
                    />
                    {studentFormErrors.lastName && <span style={{ color: 'red', fontSize: '0.85em' }}>{studentFormErrors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dateOfBirth">Date of Birth *</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      value={studentFormData.dateOfBirth}
                      onChange={(e) => setStudentFormData({ ...studentFormData, dateOfBirth: e.target.value })}
                      required
                      max={new Date().toISOString().split('T')[0]}
                    />
                    {studentFormErrors.dateOfBirth && <span style={{ color: 'red', fontSize: '0.85em' }}>{studentFormErrors.dateOfBirth}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="gradeLevel">Grade Level *</label>
                    <select
                      id="gradeLevel"
                      value={studentFormData.gradeLevel}
                      onChange={(e) => setStudentFormData({ ...studentFormData, gradeLevel: e.target.value })}
                      required
                    >
                      <option value="">Select Grade</option>
                      <option value="K">Kindergarten</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(grade => (
                        <option key={grade} value={grade.toString()}>{grade}</option>
                      ))}
                      <option value="12+">12+</option>
                    </select>
                    {studentFormErrors.gradeLevel && <span style={{ color: 'red', fontSize: '0.85em' }}>{studentFormErrors.gradeLevel}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email (Optional)</label>
                    <input
                      type="email"
                      id="email"
                      value={studentFormData.email}
                      onChange={(e) => setStudentFormData({ ...studentFormData, email: e.target.value })}
                      placeholder="student@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone (Optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      value={studentFormData.phone}
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        if (digitsOnly.length <= 10) {
                          setStudentFormData({ ...studentFormData, phone: digitsOnly });
                        }
                      }}
                      placeholder="2501234567"
                      maxLength={10}
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>10-digit Canadian phone number</small>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address (Optional)</label>
                  <textarea
                    id="address"
                    value={studentFormData.address}
                    onChange={(e) => setStudentFormData({ ...studentFormData, address: e.target.value })}
                    rows={2}
                    placeholder="Enter address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="enrollmentDate">Enrollment Date</label>
                  <input
                    type="date"
                    id="enrollmentDate"
                    value={studentFormData.enrollmentDate}
                    onChange={(e) => setStudentFormData({ ...studentFormData, enrollmentDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="profileNotes">Profile Notes (Optional)</label>
                  <textarea
                    id="profileNotes"
                    value={studentFormData.profileNotes}
                    onChange={(e) => setStudentFormData({ ...studentFormData, profileNotes: e.target.value })}
                    rows={3}
                    placeholder="Any notes about the student..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="learningGoals">Learning Goals (Optional)</label>
                  <textarea
                    id="learningGoals"
                    value={studentFormData.learningGoals}
                    onChange={(e) => setStudentFormData({ ...studentFormData, learningGoals: e.target.value })}
                    rows={3}
                    placeholder="Student's learning goals..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="slackUsername">Slack Username (Optional)</label>
                  <input
                    type="text"
                    id="slackUsername"
                    value={studentFormData.slackUsername}
                    onChange={(e) => setStudentFormData({ ...studentFormData, slackUsername: e.target.value })}
                    placeholder="@username"
                  />
                </div>

                <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowStudentForm(false);
                      setSelectedStudent(null);
                      setStudentFormErrors({});
                      setParentSearchTerm('');
                      setShowParentDropdown(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {selectedStudent ? 'Update Student' : 'Create Student'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="dashboard-content">
          <AdminBillingDashboard />
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
